import React, { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Orb3D } from "../orb/Orb3D";
import type { OrbRenderQuality } from "../orb/Orb3D";
import type { OrbState as ChatOrbState } from "../orb";
import type { OrbState as Orb3DState } from "../orb/OrbShader";
import type { TtsTestStatus, useSimoChat, VoiceOption } from "../views/useSimoChat";
import { api, type ApprovalInboxItemView, type AutonomousWorkQueueApprovalMode, type AutonomousWorkQueueItemView, type AutonomousWorkQueuePriority, type AutonomousWorkQueueStatus, type ConnectorWriteLiveEvidenceView } from "../api";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import { voiceSelectionStatus } from "../voice/voiceSelectionStatus";
import { OwnerAuthStatusPanel } from "./OwnerAuthStatusPanel";
import { OperatorConsolePanel } from "./ControlCenterPanels";
import { InfraPanel } from "./CommandPanels";
import { OperationsActionFlowPanel } from "./ControlCenterPanels";
import { ownerConnectorStatusCopy } from "./connectorStatusCopy";
import type { SettingsTab } from "../views/SettingsView";
import type { DashboardData } from "./useDashboardData";
import { OwnerMissionControl, ownerMissionOrbState } from "./OwnerMissionControl";

type ChatController = ReturnType<typeof useSimoChat>;

const CONNECTOR_WRITE_EXECUTED_EVENT = "simo:connector-write-executed";

export type CosmicVisualState = "idle" | "listening" | "thinking" | "working" | "approval_required" | "error" | "success";
export type CosmicLivingTone = "ready" | "voice" | "working" | "approval" | "degraded" | "blocked";

export interface CosmicVisualMock {
  state: CosmicVisualState | null;
  amplitude: number | null;
}
export type CosmicPerformanceMode = "high" | "balanced" | "low";

export interface CosmicPerformanceProfile {
  mode: CosmicPerformanceMode;
  renderQuality: OrbRenderQuality;
  reducedMotion: boolean;
  starfield: boolean;
  reason: "query" | "reduced_motion" | "mobile" | "tablet" | "default";
}

export interface CosmicVoiceSignal {
  listening: boolean;
  thinking: boolean;
  speaking: boolean;
  amplitude: number;
}

const COSMIC_STATE_LABEL: Record<CosmicVisualState, string> = {
  idle: "idle",
  listening: "listening",
  thinking: "thinking",
  working: "working",
  approval_required: "approval_required",
  error: "error",
  success: "success",
};

const COSMIC_TO_ORB_STATE: Record<CosmicVisualState, Orb3DState> = {
  idle: "idle",
  listening: "wake",
  thinking: "thinking",
  working: "acting",
  approval_required: "confirming",
  error: "error",
  success: "speaking",
};

const COSMIC_PERFORMANCE_MODES: CosmicPerformanceMode[] = ["high", "balanced", "low"];

export type CosmicDockZone = "context" | "runstream" | "approval" | "evidence" | "memory" | "status" | "command";

export const COSMIC_AGENT_NODES = [
  { id: "code", label: "Code", role: "Patch / PR", angle: -70, speed: 31, dock: "runstream" },
  { id: "ops", label: "Ops", role: "Host Health", angle: -28, speed: 37, dock: "status" },
  { id: "security", label: "Security", role: "Policy Gate", angle: 18, speed: 42, dock: "approval" },
  { id: "support", label: "Support", role: "Tickets", angle: 64, speed: 35, dock: "command" },
  { id: "business", label: "Business", role: "KPI", angle: 112, speed: 46, dock: "context" },
  { id: "research", label: "Research", role: "Scan", angle: 166, speed: 39, dock: "evidence" },
  { id: "ux", label: "UX", role: "Flow", angle: 218, speed: 44, dock: "command" },
  { id: "brain", label: "Brain", role: "Memory", angle: 286, speed: 33, dock: "memory" },
] as const;

export type CosmicAgentId = (typeof COSMIC_AGENT_NODES)[number]["id"];

export interface CosmicSceneSnapshot {
  orbState: Orb3DState;
  visualState: CosmicVisualState;
  label: string;
  livingTone: CosmicLivingTone;
  livingLabel: string;
  livingPrompt: string;
  ownerCue: string;
  amplitude: number;
  activeAgents: CosmicAgentId[];
  activeDockZones: CosmicDockZone[];
  activeLanes: CosmicLaneSignal[];
  hasApprovalGate: boolean;
  hasErrors: boolean;
  /**
   * SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: feingranularer Owner-Zustand.
   * Wird ausschliesslich aus echten Runtime-Quellen abgeleitet (agent_runs,
   * tool_runs, work queue, approvals, voice/chat-stream) — nie geraten.
   */
  ownerState: CosmicOwnerState;
  ownerStateLabel: string;
  ownerOrbState: Orb3DState;
}

// ── SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: Owner-Zustaende ────────────
//
// Harte Regel: Kein Zustand "arbeitet/fuehrt aus" ohne echten Runtime-State.
// Quellen: agent_runs (status/phase), tool_runs (nur fuer laufende Runs),
// autonomous work queue, approval inbox, Voice-Signal (Mic/STT/TTS live) und
// der Chat-Stream-Orb (der nur waehrend eines echten SSE-Laufs gesetzt wird).
export type CosmicOwnerState =
  | "idle"
  | "listening"
  | "transcribing"
  | "thinking"
  | "using_brain"
  | "using_connector"
  | "calling_tool"
  | "waiting_for_approval"
  | "executing"
  | "blocked"
  | "done"
  | "error";

export const COSMIC_OWNER_STATE_LABEL: Record<CosmicOwnerState, string> = {
  idle: "Bereit — kein aktiver Lauf",
  listening: "Hoert zu",
  transcribing: "Transkribiert Sprache",
  thinking: "Denkt nach",
  using_brain: "Nutzt Brain/Memory",
  using_connector: "Nutzt Connector",
  calling_tool: "Fuehrt Tool aus",
  waiting_for_approval: "Wartet auf Owner-Freigabe",
  executing: "Fuehrt aus",
  blocked: "Blockiert",
  done: "Letzter Lauf abgeschlossen",
  error: "Fehler",
};

export const COSMIC_OWNER_STATE_TO_ORB: Record<CosmicOwnerState, Orb3DState> = {
  idle: "idle",
  listening: "wake",
  transcribing: "thinking",
  thinking: "thinking",
  using_brain: "thinking",
  using_connector: "acting",
  calling_tool: "acting",
  waiting_for_approval: "confirming",
  executing: "acting",
  blocked: "blocked",
  done: "done",
  error: "error",
};

/** Echte Runtime-Signale fuer die Owner-Zustandsableitung (keine Heuristik). */
export interface CosmicOwnerRuntimeSignal {
  /** agent_runs mit status === "running". */
  runningRuns: number;
  /** agent_runs mit status === "awaiting_approval" (oder Phase awaiting_approval). */
  awaitingApprovalRuns: number;
  /** Laufende Runs in Ausfuehrungsphase (execute/dry_run/verify). */
  executePhaseRuns: number;
  /** Tool-IDs der tool_runs, die zu aktuell laufenden Runs gehoeren. */
  activeToolIds: string[];
  /** Work-Queue-Items mit status === "in_progress". */
  workQueueRunning: number;
  /** Work-Queue-Items mit status === "blocked". */
  workQueueBlocked: number;
  /** Juengster Run (updatedAt) ist status === "done". */
  lastRunDone: boolean;
}

const COSMIC_CONNECTOR_TOOL_PATTERN = /gmail|outlook|gcal|calendar|teams|github|itsimo|connector|web_search|fetch_url/i;
const COSMIC_BRAIN_TOOL_PATTERN = /memory|brain|recall|learning/i;

export function deriveCosmicOwnerRuntimeSignal(data: {
  runs: DashboardData["runs"];
  toolRuns: DashboardData["toolRuns"];
  workQueueItems?: DashboardData["workQueueItems"];
}): CosmicOwnerRuntimeSignal {
  const runningRunIds = new Set(data.runs.filter((run) => run.status === "running").map((run) => run.id));
  const awaiting = data.runs.filter((run) => run.status === "awaiting_approval" || run.phase === "awaiting_approval").length;
  const executePhases = data.runs.filter(
    (run) => run.status === "running" && (run.phase === "execute" || run.phase === "dry_run" || run.phase === "verify"),
  ).length;
  const activeToolIds = data.toolRuns
    .filter((toolRun) => runningRunIds.has(toolRun.runId))
    .slice(0, 8)
    .map((toolRun) => toolRun.toolId);
  const queue = data.workQueueItems ?? [];
  const mostRecentRun = [...data.runs].sort((l, r) => (l.updatedAt < r.updatedAt ? 1 : l.updatedAt > r.updatedAt ? -1 : 0))[0];
  return {
    runningRuns: runningRunIds.size,
    awaitingApprovalRuns: awaiting,
    executePhaseRuns: executePhases,
    activeToolIds,
    workQueueRunning: queue.filter((item) => item.status === "in_progress").length,
    workQueueBlocked: queue.filter((item) => item.status === "blocked").length,
    lastRunDone: mostRecentRun?.status === "done",
  };
}

/**
 * Leitet den feingranularen Owner-Zustand ausschliesslich aus echten Signalen
 * ab. Ohne laufenden Run, laufendes Work-Queue-Item, pending Approval oder
 * Live-Voice-Signal gibt es NIE "executing"/"calling_tool" — nur "idle"/"done".
 */
export function deriveCosmicOwnerState(input: {
  orb: ChatOrbState;
  pendingApprovals: number;
  dataErrors: number;
  voice?: CosmicVoiceSignal;
  runtime?: CosmicOwnerRuntimeSignal;
}): CosmicOwnerState {
  const runtime = input.runtime;
  if (input.dataErrors > 0 || input.orb === "error") return "error";
  if ((runtime?.workQueueBlocked ?? 0) > 0 || input.orb === "blocked") return "blocked";
  if (input.pendingApprovals > 0 || (runtime?.awaitingApprovalRuns ?? 0) > 0 || input.orb === "confirming") {
    return "waiting_for_approval";
  }
  if (input.voice?.listening || input.orb === "wake") return "listening";
  // CosmicVoiceSignal.thinking ist das echte STT-Processing-Signal.
  if (input.voice?.thinking) return "transcribing";
  const hasRealExecution = (runtime?.runningRuns ?? 0) > 0 || (runtime?.workQueueRunning ?? 0) > 0;
  if (hasRealExecution) {
    const activeToolIds = runtime?.activeToolIds ?? [];
    const inExecutePhase = (runtime?.executePhaseRuns ?? 0) > 0 || input.orb === "acting";
    if (inExecutePhase && activeToolIds.some((toolId) => COSMIC_BRAIN_TOOL_PATTERN.test(toolId))) return "using_brain";
    if (inExecutePhase && activeToolIds.some((toolId) => COSMIC_CONNECTOR_TOOL_PATTERN.test(toolId))) return "using_connector";
    if (inExecutePhase && activeToolIds.length > 0) return "calling_tool";
    if (inExecutePhase) return "executing";
    return "thinking";
  }
  // Der Chat-Orb "acting" wird nur waehrend eines echten SSE-Tool-Laufs gesetzt;
  // ohne Poll-Bestaetigung bleibt das trotzdem ein Live-Signal des Streams.
  if (input.orb === "acting") return "executing";
  if (input.orb === "thinking") return "thinking";
  if (input.orb === "speaking") return "done";
  if (runtime?.lastRunDone) return "done";
  return "idle";
}

/** QA-Visual-Mock (?cosmicState=...) auf einen konsistenten Owner-Zustand mappen. */
function deriveCosmicOwnerStateFromMock(state: CosmicVisualState): CosmicOwnerState {
  switch (state) {
    case "listening": return "listening";
    case "thinking": return "thinking";
    case "working": return "executing";
    case "approval_required": return "waiting_for_approval";
    case "error": return "error";
    case "success": return "done";
    default: return "idle";
  }
}

// ── SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: Subagent-Orbit ehrlich ─────
//
// Jeder Orbit-Agent zeigt genau einen Zustand aus echten Datenquellen:
// bereit / geplant / laeuft / wartet (Freigabe/Review) / blockiert /
// abgeschlossen / fehler. Aktiv-Animation gibt es NUR bei "laeuft" — also nur,
// wenn ein realer Run, ein reales Work-Queue-Item oder ein realer Tool-Lauf
// dahinter steht. Bereiche ohne echte Datenbindung bleiben "bereit" mit dem
// ehrlichen Detail "noch nicht verbunden".
export type CosmicAgentRuntimeStatus =
  | "ready"
  | "planned"
  | "running"
  | "waiting"
  | "blocked"
  | "done"
  | "error";

export const COSMIC_AGENT_STATUS_LABEL: Record<CosmicAgentRuntimeStatus, string> = {
  ready: "bereit",
  planned: "geplant",
  running: "laeuft",
  waiting: "wartet auf Freigabe",
  blocked: "blockiert",
  done: "abgeschlossen",
  error: "Fehler",
};

export interface CosmicAgentRuntimeEntry {
  id: CosmicAgentId;
  label: string;
  role: string;
  status: CosmicAgentRuntimeStatus;
  detail: string;
}

function workQueueStatusToAgentStatus(status: AutonomousWorkQueueStatus): CosmicAgentRuntimeStatus {
  if (status === "in_progress") return "running";
  if (status === "blocked") return "blocked";
  if (status === "done") return "done";
  return "planned"; // queued/paused = eingeplant, keine Ausfuehrung
}

export function deriveCosmicAgentRuntime(input: {
  runs: DashboardData["runs"];
  toolRuns: DashboardData["toolRuns"];
  workQueueItems?: DashboardData["workQueueItems"];
  routines?: DashboardData["routines"];
  routineRuns?: DashboardData["routineRuns"];
  pendingApprovals: number;
  learningProposals: number;
  dataErrors: number;
  infraOnline: boolean;
  notifications: number;
}): CosmicAgentRuntimeEntry[] {
  const runningRuns = input.runs.filter((run) => run.status === "running");
  const awaitingRuns = input.runs.filter((run) => run.status === "awaiting_approval");
  const runningRunIds = new Set(runningRuns.map((run) => run.id));
  const mostRecentRun = [...input.runs].sort((l, r) => (l.updatedAt < r.updatedAt ? 1 : l.updatedAt > r.updatedAt ? -1 : 0))[0];
  const runningToolRuns = input.toolRuns.filter((toolRun) => runningRunIds.has(toolRun.runId));
  const queue = (input.workQueueItems ?? []).filter((item) => item.status !== "archived");
  const routines = input.routines ?? [];
  const enabledRoutines = routines.filter((routine) => routine.enabled);
  const latestRoutineRun = (input.routineRuns ?? [])[0];

  const queueForAgent = (agentId: string, agentLabel: string): DashboardData["workQueueItems"] => {
    const needle = agentId.toLowerCase();
    const label = agentLabel.toLowerCase();
    return queue.filter((item) => {
      const assigned = (item.assignedAgent ?? "").toLowerCase();
      return assigned.length > 0 && (assigned.includes(needle) || assigned.includes(label));
    });
  };

  return COSMIC_AGENT_NODES.map((agent) => {
    let status: CosmicAgentRuntimeStatus = "ready";
    let detail = "noch nicht verbunden";

    // 1) Work-Queue-Zuweisung ist die staerkste reale Bindung pro Agent.
    const assigned = queueForAgent(agent.id, agent.label) ?? [];
    const runningItem = assigned.find((item) => item.status === "in_progress");
    const blockedItem = assigned.find((item) => item.status === "blocked");
    const plannedItem = assigned.find((item) => item.status === "queued" || item.status === "paused");
    const doneItem = assigned.find((item) => item.status === "done");
    if (runningItem) return { id: agent.id, label: agent.label, role: agent.role, status: "running", detail: `Work-Queue: ${runningItem.title}` };
    if (blockedItem) return { id: agent.id, label: agent.label, role: agent.role, status: "blocked", detail: blockedItem.blockedReason ?? `Work-Queue: ${blockedItem.title}` };

    // 2) Knoten-spezifische reale Quellen.
    if (agent.id === "code") {
      if (runningRuns.length > 0) { status = "running"; detail = `${runningRuns.length} Agent-Run aktiv`; }
      else if (awaitingRuns.length > 0) { status = "waiting"; detail = `${awaitingRuns.length} Run wartet auf Freigabe`; }
      else if (mostRecentRun?.status === "error") { status = "error"; detail = "Letzter Run mit Fehler beendet"; }
      else if (mostRecentRun?.status === "done") { status = "done"; detail = "Letzter Run abgeschlossen"; }
      else { detail = "kein aktiver Run"; }
    } else if (agent.id === "security") {
      if (input.pendingApprovals > 0) { status = "waiting"; detail = `${input.pendingApprovals} Freigabe offen`; }
      else if (input.dataErrors > 0) { status = "blocked"; detail = `${input.dataErrors} Live-Read blockiert`; }
      else { detail = "kein Gate offen"; }
    } else if (agent.id === "research") {
      if (runningToolRuns.length > 0) { status = "running"; detail = `${runningToolRuns.length} Tool-Lauf im aktiven Run`; }
      else { detail = "kein aktiver Tool-Lauf"; }
    } else if (agent.id === "brain") {
      if (input.learningProposals > 0) { status = "waiting"; detail = `${input.learningProposals} Vorschlag wartet auf Review`; }
      else { detail = "kein offenes Review"; }
    } else if (agent.id === "ops") {
      if (latestRoutineRun?.result === "error") { status = "error"; detail = "Letzte Routine fehlgeschlagen"; }
      else if (enabledRoutines.length > 0) { status = "planned"; detail = `${enabledRoutines.length} Routine geplant`; }
      else { detail = input.infraOnline ? "Host bestaetigt, keine Routine" : "kein Infra-Status bestaetigt"; }
    } else if (agent.id === "support") {
      if (input.notifications > 0) { detail = `${input.notifications} Meldung offen`; }
      else { detail = "keine offenen Meldungen"; }
    } else if (plannedItem) {
      status = "planned"; detail = `Work-Queue: ${plannedItem.title}`;
    } else if (doneItem) {
      status = "done"; detail = `Work-Queue: ${doneItem.title}`;
    }
    return { id: agent.id, label: agent.label, role: agent.role, status, detail };
  });
}

// ── SIMO-ORBIT-RUNTIME-STATE-BINDING-1: Beams, QA-Badge, Approval-Dedupe ────

/**
 * Aktivitaets-Beams vom Orb zu den Orbit-Agents. Ausschliesslich aus echten
 * Runtime-Quellen (dieselben wie die Orbit-Zustaende): ein Beam existiert nur,
 * wenn ein Agent wirklich `running` ist ODER der Owner-Zustand echte Brain-/
 * Connector-Aktivitaet meldet (`using_brain`/`using_connector` entstehen nur
 * aus laufenden Runs mit aktiven tool_runs). Im Idle gibt es KEINE Beams.
 *
 * Mapping: `using_brain` -> Brain-Node (Memory); `using_connector` ->
 * Research-Node (Scan), weil Connector-Zugriffe als Tool-Laeufe laufen und der
 * Research-Node die Tool-Lane des Orbits ist.
 */
export function deriveCosmicOrbitBeams(input: {
  runtime?: CosmicAgentRuntimeEntry[];
  ownerState: CosmicOwnerState;
}): CosmicAgentId[] {
  const beams = new Set<CosmicAgentId>();
  for (const entry of input.runtime ?? []) {
    if (entry.status === "running") beams.add(entry.id);
  }
  if (input.ownerState === "using_brain") beams.add("brain");
  if (input.ownerState === "using_connector") beams.add("research");
  return [...beams];
}

/**
 * Sichtbares Badge, sobald der QA-Visual-Mock (`?cosmicState=...`) aktiv ist.
 * Verhindert, dass ein Mock-Zustand still fuer einen echten Runtime-Zustand
 * gehalten wird (P3-Follow-up aus dem #715-Review).
 */
export function deriveCosmicMockBadgeLabel(mock: CosmicVisualMock): string | null {
  return mock.state ? `QA-Mock aktiv: ${COSMIC_STATE_LABEL[mock.state]}` : null;
}

/**
 * EINE konsistente, Inbox-basierte Zaehlquelle fuer offene Owner-Freigaben.
 *
 * Hintergrund: `/api/approval-inbox` liest direkt aus derselben `approvals`-
 * Tabelle wie `/api/approvals` und die Chat-SSE-Approval-Karten. Die fruehere
 * Summe (Chat-Karten + approvals + Inbox) zaehlte dieselbe Approval deshalb
 * bis zu dreifach. Hier gilt: Basis ist die Inbox (kanonisch fuer entschiedene
 * IDs); pending-IDs aus der approvals-Liste und den Chat-SSE-Karten werden per
 * ID-Union ergaenzt — nie doppelt, und nie fuer IDs, die die Inbox bereits als
 * entschieden kennt. Die approvals-Union ist Pflicht, weil die Inbox nur ein
 * Fenster (?limit=30, alle Status) laedt und aeltere pending Approvals sonst
 * aus der Zaehlung verschwinden wuerden (Merge-Review #717, P2).
 */
export function deriveCosmicPendingApprovalCount(input: {
  approvalInbox: { items: Array<{ id: string; status: string }> } | null;
  approvals: Array<{ id: string; status: string }>;
  chatPendingApprovalIds?: string[];
}): number {
  // Entscheidungen sind terminal (pending -> approved/rejected/expired/
  // cancelled). Eine als entschieden gemeldete ID ist deshalb aus JEDER Quelle
  // die neuere Wahrheit und gewinnt gegen stale pending-Eintraege.
  const decidedIds = new Set<string>();
  for (const item of input.approvalInbox?.items ?? []) {
    if (item.status !== "pending") decidedIds.add(item.id);
  }
  for (const approval of input.approvals) {
    if (approval.status !== "pending") decidedIds.add(approval.id);
  }
  // Merge-Review #717 (P2): pending-IDs aus der approvals-Liste IMMER mit in
  // die Union nehmen (nicht nur als Fallback). Die Inbox laedt nur ein
  // Fenster (?limit=30, alle Status, created_at DESC) — eine aeltere pending
  // Approval kann aus dem Fenster fallen, waehrend `/api/approvals` sie
  // weiterhin liefert.
  const pendingIds = new Set<string>();
  for (const item of input.approvalInbox?.items ?? []) {
    if (item.status === "pending" && !decidedIds.has(item.id)) pendingIds.add(item.id);
  }
  for (const approval of input.approvals) {
    if (approval.status === "pending" && !decidedIds.has(approval.id)) pendingIds.add(approval.id);
  }
  for (const id of input.chatPendingApprovalIds ?? []) {
    if (!decidedIds.has(id)) pendingIds.add(id);
  }
  return pendingIds.size;
}

/** Chat-SSE-Approval-Karten-IDs, die noch pending sind (fuer die ID-Union). */
export function collectChatPendingApprovalIds(history: Array<{
  approvalState?: string;
  pendingApprovals?: Array<{ id: string; status: string }>;
}>): string[] {
  const ids = new Set<string>();
  for (const message of history) {
    if (message.approvalState === "done") continue;
    for (const card of message.pendingApprovals ?? []) {
      if (card.status === "pending") ids.add(card.id);
    }
  }
  return [...ids];
}

export type FullControlSurfaceTone = "go" | "partial" | "blocked";

export interface FullControlSurfaceRow {
  id: string;
  label: string;
  status: string;
  detail: string;
  tone: FullControlSurfaceTone;
}

export type FullControlEditabilityTone = "editable" | "gated" | "proposal" | "missing";

export interface FullControlEditabilityRow {
  id: string;
  label: string;
  mode: string;
  detail: string;
  tone: FullControlEditabilityTone;
}

export type TruthRoadmapControlTone = "active" | "proposal" | "gated" | "missing";

export interface TruthRoadmapControlRow {
  id: string;
  label: string;
  source: string;
  status: string;
  detail: string;
  tone: TruthRoadmapControlTone;
}

export type HarnessControlTone = "ready" | "watch" | "blocked";

export interface HarnessControlRow {
  id: string;
  label: string;
  status: string;
  detail: string;
  evidence: string;
  tone: HarnessControlTone;
}

export type HarnessProposalAction = "run_start" | "run_end" | "handoff" | "brain_context" | "supervisor_loop";

export interface HarnessLearningProposalDraft {
  kind: "policy";
  targetKey: string;
  proposedValue: string;
  source: "harness-control-center";
  reason: string;
  evidence: {
    schema: "it-simo.harness-control-proposal.v1";
    action: HarnessProposalAction;
    note: string;
    readyRows: number;
    totalRows: number;
    blockedRows: number;
    gate: "Owner Review";
    runtimeMutation: false;
    noRunnerAction: true;
    noShellAction: true;
    noDeployAction: true;
    noConnectorWrite: true;
  };
}

export type BrainControlTone = "ready" | "review" | "gated" | "missing";

export interface BrainControlRow {
  id: string;
  label: string;
  status: string;
  detail: string;
  evidence: string;
  tone: BrainControlTone;
}

export type BrainControlProposalAction = "review_rule" | "retention_policy" | "inject_policy" | "scope_policy" | "export_restore_policy";

export interface BrainControlLearningProposalDraft {
  kind: "policy";
  targetKey: string;
  proposedValue: string;
  source: "brain-control-center";
  reason: string;
  evidence: {
    schema: "it-simo.brain-control-proposal.v1";
    action: BrainControlProposalAction;
    note: string;
    readyRows: number;
    reviewRows: number;
    totalRows: number;
    gate: "Owner Review";
    runtimeMutation: false;
    noMemoryWrite: true;
    noRawValueRead: true;
    noRestoreApply: true;
    noConnectorWrite: true;
  };
}

export type TruthEditorTone = "active" | "proposal" | "gated" | "missing";

export interface TruthEditorRow {
  id: string;
  label: string;
  status: string;
  detail: string;
  evidence: string;
  tone: TruthEditorTone;
}

export function deriveFullControlSurfaceRows(data: DashboardData): FullControlSurfaceRow[] {
  // Ehrlich (S4, agentStateTaxonomy): `executionState==="active"` ist per Server
  // ein ECHT laufender Run (`agent-roster.ts` statusForRuns liefert "active" nur
  // bei `run.status==="running"`); der enabled-/Aktivierungs-Zustand waere
  // "idle". Darum ist "laeuft" (nicht "aktiviert") das taxonomiekonforme Label
  // fuer diesen Zaehler. Zusatz: der Registry-Endpoint (`/api/agent-registry`)
  // speist die Live-Runs aktuell leer (`buildCommandAgentRoster([], roles)`), so
  // dass dieser Zaehler in Produktion immer 0 ist und das Label real nie rendert
  // — die Bindung ist damit vorwaertskorrekt (feuert nur bei echtem Run).
  const activeAgents = data.agentRegistry?.agents.filter((agent) => agent.executionState === "active").length ?? data.agents.length;
  const policiesVisible =
    data.approvalInbox != null &&
    data.runnerBoundary != null &&
    data.agentRegistry != null;
  const connectorReady = data.connectors.length > 0 && data.connectors.every((connector) => connector.reachable);
  const forgeStatus = data.infra?.forgejo.status ?? null;
  const hasEvidence = data.gatedEvidence.length > 0 || data.timeline.some((event) => event.type === "evidence");

  return [
    {
      id: "agents",
      label: "Agents",
      status: data.agentRegistry ? `${data.agentRegistry.agents.length} sichtbar` : "laedt",
      detail: activeAgents > 0 ? `${activeAgents} laeuft; Steuerung policy-gated.` : "Registry sichtbar; Aktivierung bleibt gated.",
      tone: data.agentRegistry ? "go" : "partial",
    },
    {
      id: "subagents-skills",
      label: "Subagents / Skills",
      status: data.agentRegistry ? "gated editor" : "offen",
      detail: "Rollen, Teams, Faehigkeiten und Zugriffsvorschlaege sind sichtbar; Rollen sind ueber gated UI direkt editierbar.",
      tone: "partial",
    },
    {
      id: "routines",
      label: "Routinen",
      status: data.overview ? String(data.overview.routines.count) : "laedt",
      detail: data.overview ? `${data.overview.routines.runs} Lauf-Events im Snapshot.` : "Routine-Daten werden geladen.",
      tone: data.overview ? "partial" : "blocked",
    },
    {
      id: "policies",
      label: "Policies",
      status: policiesVisible ? "gated" : "laedt",
      detail: policiesVisible ? "Approval Inbox, Runner Boundary und Agent Gates sind owner-lesbar." : "Policy-Readback noch nicht vollstaendig geladen.",
      tone: policiesVisible ? "go" : "partial",
    },
    {
      id: "brain",
      label: "Brain",
      status: `${data.memoryTotal} Memory`,
      detail: `${data.learningProposals.length} Learning Proposals; durable Writes bleiben proposal-first.`,
      tone: data.memoryTotal > 0 || data.learningProposals.length > 0 ? "go" : "partial",
    },
    {
      id: "forge",
      label: "Forge / CI",
      status: forgeStatus ?? "laedt",
      detail: "Forgejo bleibt primary; PR/Safe-Tag-Aktionen bleiben gated.",
      tone: forgeStatus === "ok" ? "go" : forgeStatus ? "partial" : "blocked",
    },
    {
      id: "connectors",
      label: "Connectoren",
      status: data.connectors.length > 0 ? `${data.connectors.filter((connector) => connector.reachable).length}/${data.connectors.length}` : "laedt",
      detail: connectorReady ? "Alle gelesenen Connectoren erreichbar; Writes fragen weiter." : "Reauth/Setup bleibt owner-actionable.",
      tone: connectorReady ? "go" : "partial",
    },
    {
      id: "work-queue",
      label: "Work Queue",
      status: hasEvidence ? "evidence" : "laedt",
      detail: "Tablet-Intake ist bewiesen; autonome Ausfuehrung bleibt capability-gated.",
      tone: hasEvidence ? "go" : "partial",
    },
  ];
}

export function deriveFullControlEditabilityRows(data: DashboardData): FullControlEditabilityRow[] {
  const routinesKnown = data.overview != null;
  const policiesVisible = data.approvalInbox != null && data.runnerBoundary != null;
  const agentTeamsVisible = (data.agentRegistry?.teams.length ?? 0) > 0 || (data.agentRegistry?.agents.length ?? 0) > 0;
  const brainActionable = data.memoryTotal > 0 || data.learningProposals.length > 0;
  const forgeVisible = data.infra?.forgejo.status != null;
  const ownerGateReady =
    data.runnerBoundary?.safeExecutionEnabled === true &&
    data.runnerBoundary.ownerAuthRequired === true &&
    data.runnerBoundary.policyBindingRequired === true &&
    data.runnerBoundary.workerIdentityRequired === true;

  return [
    {
      id: "routines-editor",
      label: "Routinen",
      mode: routinesKnown ? "direkt editierbar" : "laedt",
      detail: routinesKnown
        ? "Settings unterstuetzen Anlegen, Bearbeiten, Pausieren, Loeschen und manuelle Testlaeufe."
        : "Routine-Status ist noch nicht geladen.",
      tone: routinesKnown ? "editable" : "gated",
    },
    {
      id: "agent-os-settings",
      label: "Agent OS",
      mode: "gated settings",
      detail: "Autonomy Mode, Clarification, Reads, Changes und Modell-Defaults sind owner-gated einstellbar.",
      tone: "gated",
    },
    {
      id: "brain-rules",
      label: "Brain Regeln",
      mode: brainActionable ? "proposal-first" : "wartet",
      detail: brainActionable
        ? "Memory-Metadaten koennen freigegeben, deaktiviert oder vergessen werden; durable Wissen bleibt reviewbar."
        : "Brain-Review erscheint, sobald Memory oder Learning Proposals geladen sind.",
      tone: brainActionable ? "proposal" : "gated",
    },
    {
      id: "subagents",
      label: "Subagents",
      mode: agentTeamsVisible ? "gated roles editor" : "offen",
      detail: "Teams, Rollen, Routing, Aenderungsvorschlaege und gated Rollenverwaltung sind sichtbar; Prompt-/Scope-Apply bleibt proposal-first.",
      tone: agentTeamsVisible ? "editable" : "missing",
    },
    {
      id: "skills-policy",
      label: "Skills / Policies",
      mode: policiesVisible ? "diff preview" : "offen",
      detail: "Approval Inbox, Runner Boundary und Policy-Diff-Vorschau sind sichtbar; Skill-Editor bleibt Folgeblock.",
      tone: policiesVisible ? "proposal" : "missing",
    },
    {
      id: "versioning",
      label: "Forge / Versioning",
      mode: forgeVisible ? "closure requests" : "offen",
      detail: "Forge/CI, PR-, Merge- und Safe-Tag-Readiness plus owner-gated Closure-Anfragen sind sichtbar; Aktionen bleiben gated.",
      tone: forgeVisible ? "proposal" : "missing",
    },
    {
      id: "execution",
      label: "Ausfuehrung",
      mode: ownerGateReady ? "readiness gated" : "blockiert",
      detail: ownerGateReady
        ? "Safe Execute Readiness zeigt Owner Auth, Policy Binding, Worker Identity, Lease/Evidence und Worktree Gate; generisches Execute bleibt aus."
        : "Execution bleibt blockiert, bis alle Owner-/Policy-/Worker-Gates belegt sind.",
      tone: ownerGateReady ? "gated" : "missing",
    },
  ];
}

export function deriveTruthRoadmapControlRows(data: DashboardData): TruthRoadmapControlRow[] {
  const hasRoadmapEvidence =
    data.timeline.some((event) => /roadmap|truth|work-queue|tablet/i.test(`${event.summary} ${event.actionId ?? ""} ${event.evidenceRef ?? ""}`)) ||
    data.gatedEvidence.some((evidence) => /roadmap|truth|work-queue|tablet/i.test(`${evidence.actionId} ${evidence.resultStatus} ${evidence.summary ?? ""}`));
  const pendingTruthProposals = data.learningProposals.filter((proposal) => proposal.kind === "project_truth").length;
  const agentRegistryVisible = data.agentRegistry != null;
  const policyVisible = data.approvalInbox != null && data.runnerBoundary != null;
  const forgeVisible = data.infra?.forgejo.status != null;

  return [
    {
      id: "simo-roadmap",
      label: "Kanonische Gesamtroadmap",
      source: "docs/roadmaps/SIMO-SIMOT-SHARED-BRAIN-FINAL-ROADMAP-2026-07-10.md",
      status: "aktiv",
      detail: hasRoadmapEvidence
        ? "Eine kanonische Roadmap verbindet SIMO, SimoT, Shared Brain, Agent OS und Operations Center mit Evidence-/Work-Queue-Signalen."
        : "SIMO, SimoT, Shared Brain, Agent OS und Operations Center teilen eine kanonische Roadmap; Live-Ableitung wartet auf Evidence-Signal.",
      tone: "active",
    },
    {
      id: "roadmap-family",
      label: "Roadmap-Vertrag",
      source: "docs/roadmaps/README.md",
      status: "sichtbar",
      detail: "SIMO, SimoT, Shared Brain, Agent OS und Operations Center teilen einen kanonischen Roadmap-Vertrag.",
      tone: "active",
    },
    {
      id: "project-truth",
      label: "Project Truth",
      source: "PROJECT-TRUTH / docs/architecture",
      status: pendingTruthProposals > 0 ? `${pendingTruthProposals} Proposal` : "proposal-first",
      detail: "Durable Produktwahrheit darf nur ueber Diff-Vorschau, Review und Capability Gate geaendert werden.",
      tone: pendingTruthProposals > 0 ? "proposal" : "gated",
    },
    {
      id: "agent-truth",
      label: "Agent Truth",
      source: "docs/agents + registry",
      status: agentRegistryVisible ? "registry sichtbar" : "wartet",
      detail: agentRegistryVisible
        ? "Agent Registry ist geladen; Agent-Truth-Aenderungen bleiben Proposal/Review."
        : "Agent Registry ist noch nicht geladen; keine Truth-Aenderung aus dieser UI.",
      tone: agentRegistryVisible ? "proposal" : "missing",
    },
    {
      id: "work-queue-derivation",
      label: "Work Queue Ableitung",
      source: "docs/roadmaps/SIMO-SIMOT-SHARED-BRAIN-FINAL-ROADMAP-2026-07-10.md -> Work Queue",
      status: hasRoadmapEvidence ? "Evidence" : "gated",
      detail: "Roadmap-Ziele koennen als Work-Queue-Vorschlag sichtbar werden; automatische Ausfuehrung bleibt capability-gated.",
      tone: hasRoadmapEvidence ? "proposal" : "gated",
    },
    {
      id: "diff-audit",
      label: "Diff / Audit",
      source: "Policy + Evidence",
      status: policyVisible ? "pflicht" : "wartet",
      detail: "Roadmap-, Project-Truth- und Agent-Truth-Writes brauchen Diff-Vorschau, Audit und Owner-Policy.",
      tone: policyVisible ? "gated" : "missing",
    },
    {
      id: "forge-readback",
      label: "Forge Readback",
      source: "origin primary",
      status: forgeVisible ? data.infra?.forgejo.status ?? "sichtbar" : "wartet",
      detail: "Truth-Slices muessen nach Commit/Push gegen Forgejo primary und GitHub mirror zurueckgelesen werden.",
      tone: forgeVisible ? "gated" : "missing",
    },
  ];
}

export function deriveHarnessControlRows(data: DashboardData): HarnessControlRow[] {
  const harness = data.agentHarnessControl;
  if (!harness) {
    return [
      {
        id: "loading",
        label: "Harness",
        status: data.agentHarnessControlError ? "blockiert" : "laedt",
        detail: data.agentHarnessControlError
          ? data.agentHarnessControlError.message
          : "Owner-gated Harness-Readback wird geladen.",
        evidence: "/api/agent-harness/*",
        tone: data.agentHarnessControlError ? "blocked" : "watch",
      },
    ];
  }

  const allReadOnly = [
    harness.brainContextPacket,
    harness.capabilityEnvelope,
    harness.subagentRegistry,
    harness.handoff,
    harness.supervisorLoop,
    harness.handoffDrift,
    harness.forgeCiDrift,
    harness.connectorReadiness,
    harness.routineHealth,
    harness.uiUxReality,
  ].every((item) => {
    const boundary = item as { writesPerformed?: boolean; executionAllowed?: boolean; mutationAllowed?: boolean };
    return boundary.writesPerformed === false && boundary.executionAllowed !== true && boundary.mutationAllowed !== true;
  });

  return [
    {
      id: "brain-context",
      label: "Brain Context Packet",
      status: harness.brainContextPacket.packet.packetRef,
      detail: `${harness.brainContextPacket.packet.redactionStatus}; ${harness.brainContextPacket.packet.connectorReadiness?.length ?? 0} Connector-Signale.`,
      evidence: harness.brainContextPacket.blockId,
      tone: "ready",
    },
    {
      id: "capability-envelope",
      label: "Capability Envelope",
      status: `${harness.capabilityEnvelope.envelope.allowed.length} allow / ${harness.capabilityEnvelope.envelope.askRequired.length} ask`,
      detail: `${harness.capabilityEnvelope.envelope.blocked.length} blocked; Quelle ${harness.capabilityEnvelope.envelope.policySource}.`,
      evidence: harness.capabilityEnvelope.envelope.envelopeRef,
      tone: harness.capabilityEnvelope.envelope.blocked.length > 0 ? "watch" : "ready",
    },
    {
      id: "subagents",
      label: "Subagent Registry",
      status: `${harness.subagentRegistry.summary.total} sichtbar`,
      detail: `${harness.subagentRegistry.summary.active} aktiv, ${harness.subagentRegistry.summary.blocked} blockiert, Mutation aus.`,
      evidence: harness.subagentRegistry.blockId,
      tone: harness.subagentRegistry.mutationAllowed ? "blocked" : "ready",
    },
    {
      id: "handoff",
      label: "Codex / Claude Handoff",
      status: harness.handoff.packet.worktreeState,
      detail: `${harness.handoff.packet.evidence.length} Evidence, ${harness.handoff.packet.openRisks.length} offene Risiken, ${harness.handoff.packet.redactionStatus}.`,
      evidence: harness.handoff.packet.handoffRef,
      tone: harness.handoff.packet.openRisks.length > 0 ? "watch" : "ready",
    },
    {
      id: "supervisor",
      label: "Supervisor Loop",
      status: `${harness.supervisorLoop.findings?.length ?? 0} Findings`,
      detail: "Executor/Reviewer-Trennung ist als proposal-only Loop sichtbar.",
      evidence: harness.supervisorLoop.blockId,
      tone: "ready",
    },
    {
      id: "drift-readiness",
      label: "Drift / Readiness",
      status: `${harness.forgeCiDrift.ci?.status ?? "ci"} / ${harness.connectorReadiness.summary?.ready ?? 0} ready`,
      detail: `${harness.routineHealth.summary?.needsOwnerAction ?? 0} Routine-Aktionen; UI/UX ${harness.uiUxReality.summary?.covered ?? 0}/${harness.uiUxReality.summary?.totalSurfaces ?? 0}.`,
      evidence: `${harness.forgeCiDrift.blockId} + ${harness.connectorReadiness.blockId}`,
      tone: harness.forgeCiDrift.ci?.exactHeadMatch === false || (harness.routineHealth.summary?.repeatedFailures ?? 0) > 0 ? "watch" : "ready",
    },
    {
      id: "boundary",
      label: "Harness Boundary",
      status: allReadOnly ? "read-only" : "pruefen",
      detail: allReadOnly
        ? "Keine Writes, keine Mutation, keine Ausfuehrung aus diesem Control Center."
        : "Mindestens ein Harness-Signal muss vor echter Ausfuehrung geprueft werden.",
      evidence: "writesPerformed=false",
      tone: allReadOnly ? "ready" : "blocked",
    },
  ];
}

export function buildHarnessControlLearningProposal(
  action: HarnessProposalAction,
  note: string,
  rows: HarnessControlRow[],
): HarnessLearningProposalDraft {
  const normalizedNote = note.trim();
  const readyRows = rows.filter((row) => row.tone === "ready").length;
  const blockedRows = rows.filter((row) => row.tone === "blocked").length;
  const actionLabel: Record<HarnessProposalAction, string> = {
    run_start: "Run Start Schritte",
    run_end: "Run End Pflichten",
    handoff: "Handoff Template",
    brain_context: "Brain Context Packet",
    supervisor_loop: "Supervisor Loop",
  };
  return {
    kind: "policy",
    targetKey: `agent_harness.control.${action}`,
    proposedValue: [
      "Harness Control Proposal",
      `Aktion: ${actionLabel[action]}`,
      `Readback: ${readyRows}/${rows.length} bereit, ${blockedRows} blockiert`,
      "",
      "Owner-Notiz:",
      normalizedNote,
      "",
      "Gate: Owner Review",
      "Auto-Ausfuehrung: nein",
    ].join("\n"),
    source: "harness-control-center",
    reason: `Owner-visible harness control proposal for ${action}.`,
    evidence: {
      schema: "it-simo.harness-control-proposal.v1",
      action,
      note: normalizedNote,
      readyRows,
      totalRows: rows.length,
      blockedRows,
      gate: "Owner Review",
      runtimeMutation: false,
      noRunnerAction: true,
      noShellAction: true,
      noDeployAction: true,
      noConnectorWrite: true,
    },
  };
}

export function deriveBrainControlRows(data: DashboardData): BrainControlRow[] {
  const memoryBySpace = data.memory.reduce<Record<string, number>>((acc, item) => {
    acc[item.space] = (acc[item.space] ?? 0) + 1;
    return acc;
  }, {});
  const pendingLearning = data.learningProposals.filter((proposal) => proposal.status === "pending").length;
  const policyLearning = data.learningProposals.filter((proposal) => proposal.kind === "policy" || proposal.kind === "skill").length;
  const projectTruthLearning = data.learningProposals.filter((proposal) => proposal.kind === "project_truth").length;
  const decisionLogCount = data.brainDecisionLog?.length ?? 0;
  const decisionLogBlocked = data.brainDecisionLogError != null;
  const scopedAgents = data.agentBrainScopes?.length ?? 0;
  const runSignals = data.runs.length + data.timeline.filter((event) => event.runId != null).length;
  const evidenceSignals = data.gatedEvidence.length + data.timeline.filter((event) => event.type === "evidence").length;
  const errorSignals = data.timeline.filter((event) => event.status === "error" || /error|blocked|failed/i.test(event.summary)).length;

  return [
    {
      id: "project-wiki",
      label: "Project Wiki",
      status: `${memoryBySpace.project ?? 0} project`,
      detail: "Projektkontext ist als value-free Memory-Meta sichtbar; Rohwerte bleiben ausserhalb der Sidebar.",
      evidence: "/api/memory/meta?space=project",
      tone: (memoryBySpace.project ?? 0) > 0 ? "ready" : "gated",
    },
    {
      id: "context-graph",
      label: "Context Graph",
      status: `${scopedAgents} Scopes`,
      detail: "Agent-Brain-Scopes, Timeline, Evidence und Runs bilden die aktuelle sichtbare Graph-Kante.",
      evidence: "/api/brain/agent-scope + command timeline",
      tone: scopedAgents > 0 || evidenceSignals > 0 ? "ready" : "gated",
    },
    {
      id: "run-memory",
      label: "Run Memory",
      status: `${runSignals} Signale`,
      detail: "Run-, Timeline- und Harness-Signale sind lesbar; dauerhafte Run-Learnings bleiben Proposal/Review.",
      evidence: "command runs + timeline",
      tone: runSignals > 0 ? "ready" : "gated",
    },
    {
      id: "decision-log",
      label: "Decision Log",
      status: decisionLogBlocked ? "blockiert" : `${decisionLogCount} Decisions`,
      detail: decisionLogBlocked
        ? data.brainDecisionLogError?.message ?? "Decision Log braucht Owner/Admin Session."
        : "Decision-Readback ist allowlisted und value-free.",
      evidence: "/api/brain/decision-log",
      tone: decisionLogBlocked ? "missing" : decisionLogCount > 0 ? "ready" : "gated",
    },
    {
      id: "learning-proposals",
      label: "Learning Proposals",
      status: `${pendingLearning} pending`,
      detail: `${policyLearning} Skill/Policy, ${projectTruthLearning} Project-Truth; Apply bleibt review-gated.`,
      evidence: "/api/learning/proposals",
      tone: pendingLearning > 0 ? "review" : "ready",
    },
    {
      id: "rules-policies",
      label: "Rules / Policies",
      status: data.approvalInbox && data.runnerBoundary ? "sichtbar" : "wartet",
      detail: "Approval Inbox, Runner Boundary, Capability Envelope und Learning Policy bilden den Regel-Readback.",
      evidence: "approval inbox + runner boundary + harness envelope",
      tone: data.approvalInbox && data.runnerBoundary ? "ready" : "gated",
    },
    {
      id: "evidence-index",
      label: "Evidence Index",
      status: `${evidenceSignals} Evidence`,
      detail: `${errorSignals} Error-/Blocked-Signale; Error Book bleibt aus Evidence/Decision ableitbar.`,
      evidence: "gated evidence + command timeline",
      tone: evidenceSignals > 0 ? "ready" : "gated",
    },
    {
      id: "privacy-retention",
      label: "Privacy / Retention",
      status: `${memoryBySpace.private ?? 0} private`,
      detail: "Memory-Meta zeigt Space, Sensitivity, Retention und Inject Policy ohne Rohwerte.",
      evidence: "/api/memory/meta",
      tone: data.memory.length > 0 ? "ready" : "gated",
    },
    {
      id: "export-restore",
      label: "Export / Restore",
      status: "redacted",
      detail: "Brain Export ist section-basiert; Restore bleibt Preflight/Test-Space-gated.",
      evidence: "/api/memory/export + restore validate",
      tone: "gated",
    },
  ];
}

export function buildBrainControlLearningProposal(
  action: BrainControlProposalAction,
  note: string,
  rows: BrainControlRow[],
): BrainControlLearningProposalDraft {
  const normalizedNote = note.trim();
  const readyRows = rows.filter((row) => row.tone === "ready").length;
  const reviewRows = rows.filter((row) => row.tone === "review").length;
  const actionLabel: Record<BrainControlProposalAction, string> = {
    review_rule: "Learning Review Regel",
    retention_policy: "Retention / Privacy Policy",
    inject_policy: "Auto-Inject Policy",
    scope_policy: "Agent-/Project-Scope Policy",
    export_restore_policy: "Export-/Restore-Policy",
  };
  return {
    kind: "policy",
    targetKey: `brain_control.policy.${action}`,
    proposedValue: [
      "Brain Control Proposal",
      `Aktion: ${actionLabel[action]}`,
      `Readback: ${readyRows}/${rows.length} bereit, ${reviewRows} Review`,
      "",
      "Owner-Notiz:",
      normalizedNote,
      "",
      "Gate: Owner Review",
      "Durable Memory Write: nein",
    ].join("\n"),
    source: "brain-control-center",
    reason: `Owner-visible brain control proposal for ${action}.`,
    evidence: {
      schema: "it-simo.brain-control-proposal.v1",
      action,
      note: normalizedNote,
      readyRows,
      reviewRows,
      totalRows: rows.length,
      gate: "Owner Review",
      runtimeMutation: false,
      noMemoryWrite: true,
      noRawValueRead: true,
      noRestoreApply: true,
      noConnectorWrite: true,
    },
  };
}

export function deriveTruthEditorRows(data: DashboardData): TruthEditorRow[] {
  const truthProposals = data.learningProposals.filter((proposal) => proposal.kind === "project_truth");
  const policyProposals = data.learningProposals.filter((proposal) => proposal.kind === "policy");
  const agentRegistryVisible = data.agentRegistry != null;
  const agentCount = data.agentRegistry?.agents.length ?? data.agents.length;
  const sourceText = [
    ...data.timeline.map((event) => `${event.summary} ${event.actionId ?? ""} ${event.evidenceRef ?? ""}`),
    ...data.gatedEvidence.map((item) => `${item.actionId} ${item.summary ?? ""}`),
    ...data.memory.map((item) => `${item.key} ${item.source}`),
  ].join(" ");
  const readmeSeen = /readme/i.test(sourceText);
  const activeRoadmapLinked = true;
  const auditVisible = data.auditOk === true || data.timeline.some((event) => event.auditId != null);
  const brainVisible = data.memoryTotal > 0 || (data.brainDecisionLog?.length ?? 0) > 0 || data.learningProposals.length > 0;
  const forgeVisible = data.infra?.forgejo.status != null;
  const contradictionSignalMap = {
    README: readmeSeen,
    Roadmap: activeRoadmapLinked,
    Brain: brainVisible,
    Registry: agentRegistryVisible,
    Forge: forgeVisible,
  };
  const contradictionSignals = Object.entries(contradictionSignalMap)
    .filter(([, present]) => present)
    .map(([label]) => label);
  const missingContradictionSignals = Object.entries(contradictionSignalMap)
    .filter(([, present]) => !present)
    .map(([label]) => label);

  return [
    {
      id: "active-warning",
      label: "Aktive Produktwahrheit",
      status: "Warnung",
      detail: "Project Truth und Agent Truth sind aktive Wahrheit; direkte Aenderung braucht Diff, Review und Capability Gate.",
      evidence: "docs/roadmaps/SIMO-SIMOT-SHARED-BRAIN-FINAL-ROADMAP-2026-07-10.md + PROJECT-TRUTH",
      tone: "active",
    },
    {
      id: "project-truth-diff",
      label: "Project Truth Diff",
      status: truthProposals.length > 0 ? `${truthProposals.length} Proposal` : "proposal-first",
      detail: truthProposals.length > 0
        ? "Project-Truth-Aenderungen liegen in der Learning Review Queue und duerfen erst nach Review wirken."
        : "Neue Project-Truth-Aenderungen muessen als Proposal mit Vor/Nach/Evidence entstehen.",
      evidence: "/api/learning/proposals kind=project_truth",
      tone: truthProposals.length > 0 ? "proposal" : "gated",
    },
    {
      id: "agent-truth-diff",
      label: "Agent Truth Diff",
      status: agentRegistryVisible ? `${agentCount} Agents` : "wartet",
      detail: agentRegistryVisible
        ? "Agent Registry, Subagent Diffs und Policy-Proposals bilden die Agent-Truth-Vorschau."
        : "Agent Truth braucht Agent Registry Readback, bevor Aenderungen bewertet werden.",
      evidence: "/api/agent-registry + policy proposals",
      tone: agentRegistryVisible ? "proposal" : "missing",
    },
    {
      id: "save-gate",
      label: "Speichern / Apply",
      status: data.approvalInbox && data.runnerBoundary ? "capability-gated" : "blockiert",
      detail: "Speichern bleibt ueber Owner/Admin-Review, Permission-/Capability-Gate und Audit gebunden.",
      evidence: "approval inbox + runner boundary + learning decide",
      tone: data.approvalInbox && data.runnerBoundary ? "gated" : "missing",
    },
    {
      id: "history",
      label: "Historie",
      status: auditVisible ? "Audit sichtbar" : "wartet",
      detail: auditVisible
        ? "Wer/was geaendert hat, bleibt ueber Audit, Timeline und Learning Decisions nachvollziehbar."
        : "Historie braucht Audit-/Timeline-Readback.",
      evidence: "audit chain + command timeline",
      tone: auditVisible ? "active" : "missing",
    },
    {
      id: "roadmap-link",
      label: "Aktive Roadmap",
      status: "verlinkt",
      detail: "Truth-Aenderungen muessen gegen den kanonischen Roadmap-Vertrag fuer SIMO, SimoT, Shared Brain, Agent OS und Operations Center geprueft werden.",
      evidence: "docs/roadmaps/SIMO-SIMOT-SHARED-BRAIN-FINAL-ROADMAP-2026-07-10.md",
      tone: "active",
    },
    {
      id: "contradiction-check",
      label: "Widerspruchspruefung",
      status: `${contradictionSignals.length}/5 Signale`,
      detail: `Vergleich gegen README/Roadmap/Brain/Registry/Forge: vorhanden ${contradictionSignals.join(", ") || "keine"}; offen ${missingContradictionSignals.join(", ") || "keine"}.`,
      evidence: "README + Roadmap + Brain + Agent Registry + Forge",
      tone: contradictionSignals.length >= 4 ? "gated" : "missing",
    },
    {
      id: "policy-learning",
      label: "Policy / Agent Proposals",
      status: `${policyProposals.length} Policy`,
      detail: "Agent-Truth-, Rollen- und Policy-Aenderungen laufen proposal-first durch Learning Review.",
      evidence: "/api/learning/proposals kind=policy",
      tone: policyProposals.length > 0 ? "proposal" : "gated",
    },
  ];
}

export interface CosmicLaneSignal {
  id: "voice" | "agent" | "owner" | "connector" | "infra" | "brain";
  label: string;
  status: string;
  tone: CosmicLivingTone;
  active: boolean;
}

export interface AgentWorkspaceSnapshot {
  prompt: string;
  response: string;
  draftReply: AgentWorkspaceDraftReply | null;
  runstream: string[];
  transcript: AgentWorkspaceTranscriptItem[];
  planBrief: AgentWorkspacePlanBrief;
  focus: AgentWorkspaceFocus;
  lifecycle: AgentWorkspaceStage[];
  decision: AgentWorkspaceDecision;
  progressCue: AgentWorkspaceProgressCue;
  outcome: AgentWorkspaceOutcome;
  gate: string;
  evidence: string;
  active: boolean;
}

export type AgentWorkspaceStageStatus = "waiting" | "active" | "done" | "approval" | "blocked";

export type AgentWorkspaceDecisionTone = "ready" | "working" | "approval" | "blocked" | "review";

export interface AgentWorkspaceDecision {
  label: string;
  detail: string;
  primaryAction: string;
  tone: AgentWorkspaceDecisionTone;
}

export interface AgentWorkspaceProgressCue {
  label: string;
  detail: string;
  tone: AgentWorkspaceDecisionTone;
}

export type AgentWorkspaceOutcomeLabel = "GO" | "CONDITIONAL GO" | "NO-GO" | "OFFEN";

export interface AgentWorkspaceOutcome {
  label: AgentWorkspaceOutcomeLabel;
  detail: string;
  evidence: string;
  tone: AgentWorkspaceDecisionTone;
}

export interface AgentWorkspaceDraftReply {
  headline: string;
  understanding: string;
  nextAction: string;
  guardrail: string;
  evidence: string;
  checklist: AgentWorkspaceDraftChecklistItem[];
  tone: AgentWorkspaceDecisionTone;
}

export interface AgentWorkspaceDraftChecklistItem {
  label: string;
  detail: string;
  status: AgentWorkspaceStageStatus;
}

export interface AgentWorkspaceStage {
  id: "prompt" | "plan" | "tools" | "diff" | "tests" | "gate" | "evidence";
  label: string;
  status: AgentWorkspaceStageStatus;
  detail: string;
}

export interface AgentWorkspaceTranscriptItem {
  id: string;
  source: "owner" | "simo" | "run" | "tool" | "timeline" | "gate";
  title: string;
  detail: string;
  tone: AgentWorkspaceDecisionTone;
}

export interface AgentWorkspacePlanBrief {
  objective: string;
  currentStep: string;
  nextStep: string;
  guardrail: string;
  evidence: string;
  tone: AgentWorkspaceDecisionTone;
}

export interface AgentWorkspaceFocus {
  label: string;
  primary: string;
  secondary: string;
  nextAction: string;
  supporting: string;
  tone: AgentWorkspaceDecisionTone;
}

export interface MissionComposerFocus {
  label: string;
  detail: string;
  primaryAction: string;
  safeAction: string;
  ownerPath: string | null;
  evidence: string;
  tone: AgentWorkspaceDecisionTone;
}

export interface LiveDataUnlockItem {
  key: string;
  endpoint: string;
  protectedData: string;
  authModel: string;
  sentHeader: string;
  forbiddenHeader: string;
  ownerGateFlow: string;
  backendExpectation: string;
  statusJudgement: string;
  cause: string;
  action: string;
  primaryCta: string;
  safeCta: string;
  detailsCta: string;
  message: string;
  rawMessage: string;
}

export interface LiveDataUnlockProof {
  session: string;
  ownerAuth: string;
  ownerKey: string;
  blockedReads: string;
  nextProbe: string;
}

export interface LiveDataUnlockDecision {
  label: string;
  detail: string;
  primaryAction: string;
  safeAction: string;
  evidence: string;
  tone: "session" | "owner" | "connector" | "proxy" | "unknown";
}

export interface LivePreflightBlockerSummary {
  count: number;
  ownerAuthBlockers: string[];
  sessionBlockers: string[];
  connectorBlockers: string[];
  liveDataBlockers: string[];
  voiceBlockers: string[];
  label: string;
  detail: string;
  action: string;
  ownerQuestion: string;
  evidence: string;
}

export interface OwnerLiveSystemSignal {
  // OPNAV1 Truth-Fixture: "Gates" (Approval-Inbox) heisst jetzt explizit
  // "Freigaben (Läufe)"; "Owner-Gates" ist die kanonische Human-Gate-Zahl.
  label: "Code" | "Freigaben (Läufe)" | "Owner-Gates" | "Connectoren" | "Infra" | "Brain" | "Evidence";
  value: string;
}

export interface OwnerWatchingBrief {
  label: string;
  detail: string;
  tone: "ready" | "watching" | "approval" | "degraded";
}

export interface OwnerConnectorMissionDecision {
  label: string;
  detail: string;
  primaryAction: string;
  safeAction: string;
  ownerPath: string;
  affected: string;
  risk: string;
  evidence: string;
  tone: "ready" | "reauth" | "setup" | "degraded";
}

export interface OwnerVoiceMissionDecision {
  label: string;
  detail: string;
  selected: string;
  playback: string;
  primaryAction: string;
  safeAction: string;
  evidence: string;
  tone: "ready" | "active" | "fallback" | "setup" | "off";
}

export interface OwnerDecisionPrompt {
  question: string;
  action: string;
  reason: string;
  tone: "ok" | "warn" | "approval" | "info";
}

export type OwnerNextLane = "status" | "approval" | "runstream" | "memory" | "command";
export type OwnerNextTone = "ok" | "warn" | "approval" | "info";

export interface OwnerNextSignal {
  tone: OwnerNextTone;
  title: string;
  detail: string;
  lane: OwnerNextLane;
}

export function deriveOwnerNextSignal(input: {
  dataErrors: number;
  pendingApprovals: number;
  activeRuns: number;
  learningProposals: number;
  notifications: number;
  completedRuns: number;
  evidenceCount: number;
}): OwnerNextSignal {
  if (input.dataErrors > 0) {
    return {
      tone: "warn",
      title: "Live-Daten entsperren",
      detail: `${input.dataErrors} Live-Reads brauchen Owner-Auth oder erneutes Laden.`,
      lane: "status",
    };
  }
  if (input.pendingApprovals > 0) {
    return {
      tone: "approval",
      title: "Owner-Freigaben entscheiden",
      detail: `${input.pendingApprovals} Freigaben warten im Gate.`,
      lane: "approval",
    };
  }
  if (input.activeRuns > 0) {
    return {
      tone: "info",
      title: "Aktive Runs beobachten",
      detail: `${input.activeRuns} Run schreibt gerade Evidence oder wartet auf Gate.`,
      lane: "runstream",
    };
  }
  if (input.learningProposals > 0) {
    return {
      tone: "info",
      title: "Learning pruefen",
      detail: `${input.learningProposals} Learning-Proposal wartet auf Review.`,
      lane: "memory",
    };
  }
  return {
    tone: "ok",
    title: "Naechste Aufgabe starten",
    detail: "Keine Blocker, keine offenen Freigaben.",
    lane: "command",
  };
}

export function deriveOwnerDecisionPrompt(input: {
  dataErrors: number;
  pendingApprovals: number;
  activeRuns: number;
  connectorReachable: number;
  connectorTotal: number;
  evidenceCount: number;
  livePreflightBlockers?: LivePreflightBlockerSummary;
}): OwnerDecisionPrompt {
  if (input.dataErrors > 0) {
    const blockers = input.livePreflightBlockers;
    return {
      question: blockers?.ownerQuestion ?? "Welche Live-Daten soll SIMO jetzt entsperren oder sicher ueberspringen?",
      action: blockers?.action ?? "Blocker klaeren",
      reason: blockers?.detail ?? `${input.dataErrors} Live-Datenblocker verhindert belastbare Entscheidungen.`,
      tone: "warn",
    };
  }
  if (input.pendingApprovals > 0) {
    return {
      question: "Welche Owner-Freigabe entscheidest du jetzt?",
      action: "Freigabe pruefen",
      reason: `${input.pendingApprovals} Owner-Entscheidung wartet.`,
      tone: "approval",
    };
  }
  if (input.activeRuns > 0) {
    return {
      question: "Soll SIMO den laufenden Run weiter beobachten oder die Mission ergaenzen?",
      action: "Runstream beobachten",
      reason: `${input.activeRuns} Run aktiv; Ergebnis ist noch nicht final.`,
      tone: "info",
    };
  }
  if (input.connectorTotal > 0 && input.connectorReachable < input.connectorTotal) {
    return {
      question: "Soll SIMO mit sicheren Daten fortfahren oder Connector-Reauth vorbereiten?",
      action: "Connectoren pruefen",
      reason: `${input.connectorReachable}/${input.connectorTotal} Connectoren erreichbar.`,
      tone: "warn",
    };
  }
  return {
    question: "Welche Mission soll SIMO als naechstes planen und ausfuehren?",
    action: "Mission starten",
    reason: input.evidenceCount > 0 ? `${input.evidenceCount} Evidence-Nachweise liegen bereit.` : "Keine Blocker oder Owner-Gates offen.",
    tone: "ok",
  };
}

export function deriveOwnerLivingCue(input: {
  activeRuns: number;
  pendingApprovals: number;
  dataErrors: number;
  learningProposals: number;
  evidenceCount: number;
  infraOnline: boolean;
  connectorReachable: number;
  connectorTotal: number;
  voice?: CosmicVoiceSignal;
}): string {
  if (input.dataErrors > 0) {
    return `${input.dataErrors} Live-Datenblocker klaeren, bevor SIMO belastbar weiterarbeitet.`;
  }
  if (input.connectorTotal > 0 && input.connectorReachable < input.connectorTotal) {
    return `${input.connectorReachable}/${input.connectorTotal} Connectoren erreichbar; Reauth oder Setup zuerst pruefen.`;
  }
  if (!input.infraOnline) {
    return "Infra ist nicht bestaetigt; Host-/Forge-/Runtime-Status pruefen.";
  }
  if (input.pendingApprovals > 0) {
    return `${input.pendingApprovals} Owner-Entscheidung offen; Freigabe oder Ablehnung ist der naechste Schritt.`;
  }
  if (input.activeRuns > 0) {
    return `${input.activeRuns} Run aktiv; Runstream, Tests und Evidence beobachten.`;
  }
  if (input.voice?.listening) {
    return "Voice hoert zu; formuliere die naechste Mission.";
  }
  if (input.voice?.thinking) {
    return "Voice verarbeitet; Antwort und Plan folgen im Arbeitsfluss.";
  }
  if (input.voice?.speaking) {
    return "SIMO spricht; danach kann die naechste Mission starten.";
  }
  if (input.learningProposals > 0) {
    return `${input.learningProposals} Brain-Vorschlag wartet auf Review.`;
  }
  if (input.evidenceCount > 0) {
    return `${input.evidenceCount} Evidence-Nachweise liegen bereit; naechste Mission kann starten.`;
  }
  return "Alles ruhig; SIMO wartet auf deine naechste Mission.";
}

function liveEndpointForKey(key: string): { endpoint: string; protectedData: string } {
  switch (key) {
    case "approvalInbox":
      return { endpoint: "/api/approval-inbox?limit=30", protectedData: "Approval Inbox" };
    case "learning":
      return { endpoint: "/api/learning/proposals", protectedData: "Brain Learning Review" };
    case "ownerAuth":
      return { endpoint: "/api/owner/status", protectedData: "Owner Auth Status" };
    case "connectors":
      return { endpoint: "/api/connectors", protectedData: "Connector Live-Status" };
    case "memory":
      return { endpoint: "/api/memory/meta", protectedData: "Shared Brain Meta" };
    case "command-stream":
      return { endpoint: "/api/command-center/stream", protectedData: "Owner Live Event Stream" };
    case "ownerEventsStream":
      return { endpoint: "/api/events/stream", protectedData: "Owner Cross-Device Event Stream" };
    case "overview":
      return { endpoint: "/api/command-center/overview", protectedData: "Command Overview" };
    case "runs":
      return { endpoint: "/api/command-center/runs?limit=12", protectedData: "Command Runs" };
    case "timeline":
      return { endpoint: "/api/command-center/timeline?limit=40", protectedData: "Command Timeline" };
    case "agents":
      return { endpoint: "/api/command-center/agents", protectedData: "Agent Roster" };
    case "infra":
      return { endpoint: "/api/command-center/infra", protectedData: "Infrastructure Status" };
    case "runtimeReliability":
      return { endpoint: "/api/runtime/reliability-baseline", protectedData: "Runtime Reliability Baseline" };
    case "tools":
      return { endpoint: "/api/command-center/tools?limit=20", protectedData: "Tool Run Evidence" };
    case "notifications":
      return { endpoint: "/api/notifications?limit=20", protectedData: "Notification Inbox" };
    case "gatedEvidence":
      return { endpoint: "/api/command-center/armed/evidence?limit=24", protectedData: "Gated Evidence" };
    case "ownerDevices":
      return { endpoint: "/api/owner/devices", protectedData: "Owner Devices" };
    case "agentRegistry":
      return { endpoint: "/api/agent-registry", protectedData: "Agent Registry" };
    case "workerSandbox":
      return { endpoint: "/api/worker-sandbox/status", protectedData: "Worker Sandbox Boundary" };
    case "runnerBoundary":
      return { endpoint: "/api/runner/jobs/boundary", protectedData: "Runner Job Boundary" };
    case "audit":
      return { endpoint: "/api/audit/verify", protectedData: "Audit Integrity" };
    case "approvals":
      return { endpoint: "/api/approvals", protectedData: "Legacy Approval Status" };
    case "health":
      return { endpoint: "/api/health", protectedData: "System Health" };
    default:
      return { endpoint: key, protectedData: key };
  }
}

function liveDataCause(message: string): string {
  if (/missing_session|Owner-Session fehlt|Owner Auth nicht akzeptiert|neu anmelden/i.test(message)) {
    return "Dein Login-State ist vorhanden oder erwartet, aber der Live-Read hat keine gueltige SIMO Owner-Session akzeptiert.";
  }
  if (/API-Zugriff nicht freigegeben|unauthorized|Bearer/i.test(message)) {
    return "Der Request wurde vor der Owner-Session-Pruefung durch das API-Bearer-/Proxy-Gate abgelehnt.";
  }
  if (/Owner-Freigabe erforderlich|insufficient_role|learning_review_forbidden|403/i.test(message)) {
    return "Die Session ist da, aber diese Live-Daten brauchen Owner-/Admin-Rechte.";
  }
  if (/reauth|required|invalid_grant|expired|revoked|Neu verbinden/i.test(message)) {
    return "Ein Connector braucht eine neue Freigabe, bevor Live-Daten wieder belastbar sind.";
  }
  return "SIMO konnte diesen Live-Read nicht verifizieren. Details bleiben sichtbar, ohne Secrets anzuzeigen.";
}

function liveDataAction(message: string): string {
  if (/missing_session|Owner-Session fehlt|Owner Auth nicht akzeptiert|neu anmelden|^401$/i.test(message)) {
    return "Owner-Freigabe erneuern und danach Live-Daten neu laden.";
  }
  if (/API-Zugriff nicht freigegeben|unauthorized|Bearer/i.test(message)) {
    return "Server-/Proxy-Auth fuer diesen Endpoint pruefen; Owner-Session allein reicht hier nicht.";
  }
  if (/Owner-Freigabe erforderlich|insufficient_role|learning_review_forbidden|403/i.test(message)) {
    return "Mit Owner/Admin-Session fortfahren oder nur sichere Daten laden.";
  }
  if (/reauth|required|invalid_grant|expired|revoked|Neu verbinden/i.test(message)) {
    return "Connector neu verbinden und danach Status erneut lesen.";
  }
  return "Neu laden; falls es bleibt, Request-ID und Endpoint als Evidence nutzen.";
}

function liveDataPrimaryCta(message: string): string {
  if (/missing_session|Owner-Session fehlt|Owner Auth nicht akzeptiert|neu anmelden|^401$/i.test(message)) {
    return "Owner-Freigabe erneuern";
  }
  if (/Owner-Freigabe erforderlich|insufficient_role|learning_review_forbidden|403/i.test(message)) {
    return "Owner/Admin-Session verwenden";
  }
  if (/reauth|required|invalid_grant|expired|revoked|Neu verbinden/i.test(message)) {
    return "Connector neu verbinden";
  }
  if (/API-Zugriff nicht freigegeben|unauthorized|Bearer/i.test(message)) {
    return "Server-/Proxy-Auth pruefen";
  }
  return "Live-Daten neu laden";
}

function liveDataSafeCta(key: string): string {
  if (key === "approvalInbox" || key === "learning") {
    return "Nur sichere Daten laden";
  }
  if (key === "connectors") {
    return "Ohne Connector-Daten fortfahren";
  }
  return "Nur verifizierte Daten nutzen";
}

function liveDataBackendExpectation(key: string): string {
  if (key === "ownerAuth") {
    return "liest Owner-Auth-Konfiguration; legacy-owner-token ist Status-Metadatum, kein Browser-Secret";
  }
  if (key === "approvalInbox" || key === "learning") {
    return "erwartet gueltige HttpOnly-Cookie-Session mit Owner/Admin-Rolle";
  }
  if (key === "connectors") {
    return "erwartet SIMO-Session plus connector-spezifische Reauth, wenn OAuth abgelaufen ist";
  }
  return "erwartet SIMO-Session; endpoint-spezifische Policy bleibt Evidence";
}

function liveDataOwnerGateFlow(key: string): string {
  if (key === "ownerAuth") {
    return "Backend liest Owner-Auth-Status; legacy-owner-token darf nur als keyId/Fingerprint-Metadatum erscheinen.";
  }
  if (key === "approvalInbox" || key === "learning") {
    return "Neuer Owner-Gate-Flow: gueltige SIMO-Session mit Owner/Admin-Rolle; kein Legacy-Owner-Token im Renderer.";
  }
  if (key === "connectors") {
    return "Connector-Flow: SIMO-Session plus provider-spezifische Reauth; OAuth-Secret bleibt serverseitig.";
  }
  return "Session-basierter Live-Read; endpoint-spezifische Policy entscheidet serverseitig.";
}

function liveDataStatusJudgement(message: string): string {
  if (/missing_session|Owner-Session fehlt|Owner Auth nicht akzeptiert|neu anmelden|401/i.test(message)) {
    return "401 ist korrekt bei fehlender oder ungueltiger SIMO-Session; Regression erst, wenn die HttpOnly-Cookie-Session gueltig ist.";
  }
  if (/Owner-Freigabe erforderlich|insufficient_role|learning_review_forbidden|403/i.test(message)) {
    return "403 ist korrekt bei vorhandener Session ohne passende Owner-/Admin-Rolle.";
  }
  if (/reauth|required|invalid_grant|expired|revoked|Neu verbinden/i.test(message)) {
    return "Connector-Status ist kein Owner-Auth-Fehler; Reauth ist erforderlich.";
  }
  return "Status nicht abschliessend bewertet; Endpoint, Header-Modell und Request-ID als Evidence nutzen.";
}

export function deriveLiveDataUnlockItems(errors: Record<string, string>): LiveDataUnlockItem[] {
  return Object.entries(errors).map(([key, rawMessage]) => {
    const endpoint = liveEndpointForKey(key);
    const message = ownerFacingGlobalError(rawMessage);
    return {
      key,
      endpoint: endpoint.endpoint,
      protectedData: endpoint.protectedData,
      authModel: "HttpOnly-Cookie-Session; kein Owner-Secret im Renderer",
      sentHeader: "Cookie: simo_session (HttpOnly, renderer-unlesbar)",
      forbiddenHeader: "x-simo-owner-token und x-simo-session-token werden nicht vom Renderer gesendet",
      ownerGateFlow: liveDataOwnerGateFlow(key),
      backendExpectation: liveDataBackendExpectation(key),
      statusJudgement: liveDataStatusJudgement(rawMessage),
      cause: liveDataCause(rawMessage),
      action: liveDataAction(rawMessage),
      primaryCta: liveDataPrimaryCta(rawMessage),
      safeCta: liveDataSafeCta(key),
      detailsCta: "Details anzeigen",
      message,
      rawMessage,
    };
  });
}

function liveBlockerLine(item: LiveDataUnlockItem): string {
  return `${item.protectedData}: ${item.action}`;
}

export function deriveLivePreflightBlockers(errors: Record<string, string>): LivePreflightBlockerSummary {
  const items = deriveLiveDataUnlockItems(errors);
  const summary: LivePreflightBlockerSummary = {
    count: items.length,
    ownerAuthBlockers: [],
    sessionBlockers: [],
    connectorBlockers: [],
    liveDataBlockers: [],
    voiceBlockers: [],
    label: "Preflight bereit",
    detail: "Owner-Session, Connectoren, Voice und Live-Daten haben keinen bekannten Blocker.",
    action: "Mission starten",
    ownerQuestion: "Welche Mission soll SIMO als naechstes planen und ausfuehren?",
    evidence: "Keine blockierten Live-Reads im aktuellen Snapshot.",
  };

  for (const item of items) {
    const text = `${item.key} ${item.rawMessage} ${item.message}`;
    if (/missing_session|invalid_session|Owner-Session fehlt|Owner Auth nicht akzeptiert|neu anmelden|^401$/i.test(text)) {
      summary.sessionBlockers.push(liveBlockerLine(item));
    } else if (/Owner-Freigabe erforderlich|insufficient_role|learning_review_forbidden|Owner\/Admin|403/i.test(text)) {
      summary.ownerAuthBlockers.push(liveBlockerLine(item));
    } else if (item.key === "connectors" || /reauth|required|invalid_grant|expired|revoked|Neu verbinden|OAuth/i.test(text)) {
      summary.connectorBlockers.push(liveBlockerLine(item));
    } else if (/voice|stt|tts|mic|mikro|audio/i.test(text)) {
      summary.voiceBlockers.push(liveBlockerLine(item));
    } else {
      summary.liveDataBlockers.push(liveBlockerLine(item));
    }
  }

  const evidenceParts = [
    `sessionBlockers=${summary.sessionBlockers.length}`,
    `ownerAuthBlockers=${summary.ownerAuthBlockers.length}`,
    `connectorBlockers=${summary.connectorBlockers.length}`,
    `liveDataBlockers=${summary.liveDataBlockers.length}`,
    `voiceBlockers=${summary.voiceBlockers.length}`,
  ];
  summary.evidence = evidenceParts.join("; ");

  const first =
    summary.sessionBlockers[0] ??
    summary.ownerAuthBlockers[0] ??
    summary.connectorBlockers[0] ??
    summary.voiceBlockers[0] ??
    summary.liveDataBlockers[0];

  if (summary.sessionBlockers.length > 0) {
    summary.label = "Owner-Session erneuern";
    summary.detail = first ?? "Owner-Session fehlt oder ist abgelaufen.";
    summary.action = "Owner-Session neu anmelden";
    summary.ownerQuestion = "Owner-Session ist nicht gueltig. Soll SIMO dich jetzt zum Login fuehren?";
  } else if (summary.ownerAuthBlockers.length > 0) {
    summary.label = "Owner/Admin-Session verwenden";
    summary.detail = first ?? "Live-Daten brauchen Owner-/Admin-Rechte.";
    summary.action = "Owner/Admin-Session pruefen";
    summary.ownerQuestion = "Diese Live-Daten brauchen Owner/Admin-Rechte. Welche sichere Alternative soll SIMO nutzen?";
  } else if (summary.connectorBlockers.length > 0) {
    summary.label = "Connector-Reauth erforderlich";
    summary.detail = first ?? "Ein Connector muss neu verbunden werden.";
    summary.action = "Connector neu verbinden";
    summary.ownerQuestion = "Welchen Connector soll SIMO neu verbinden oder sicher ueberspringen?";
  } else if (summary.voiceBlockers.length > 0) {
    summary.label = "Voice pruefen";
    summary.detail = first ?? "Voice oder Mikrofon ist nicht bereit.";
    summary.action = "Voice/Mikrofon pruefen";
    summary.ownerQuestion = "Soll SIMO Voice pruefen oder die Mission als Text starten?";
  } else if (summary.liveDataBlockers.length > 0) {
    summary.label = "Live-Daten gezielt entsperren";
    summary.detail = first ?? "Ein Live-Read ist blockiert.";
    summary.action = "Live-Daten neu laden";
    summary.ownerQuestion = "Welche Live-Daten soll SIMO entsperren oder sicher ueberspringen?";
  }

  return summary;
}

export function deriveLiveDataUnlockProof(input: {
  hasSessionToken: boolean;
  ownerAuthStatus: DashboardData["ownerAuthStatus"];
  ownerAuthError: DashboardData["ownerAuthError"];
  blockedReads: number;
}): LiveDataUnlockProof {
  const configured = input.ownerAuthStatus?.ownerAuth.configured === true;
  const keyId = input.ownerAuthStatus?.ownerAuth.keyId ?? null;
  return {
    session: input.hasSessionToken
      ? "Browser sendet die HttpOnly-Cookie-Session fuer geschuetzte Live-Reads."
      : "Keine verifizierte SIMO-Cookie-Session; Login oder Session-Erneuerung erforderlich.",
    ownerAuth: input.ownerAuthError
      ? `Owner-Auth-Status nicht lesbar; Request-ID ${input.ownerAuthError.requestId ?? "nicht gemeldet"}.`
      : configured
        ? "Backend meldet Owner Auth konfiguriert."
        : "Backend meldet Owner Auth nicht konfiguriert.",
    ownerKey: keyId
      ? `Owner-Key-ID: ${keyId}; keyId ist Metadatum, kein Browser-Secret.`
      : "Keine Owner-Key-ID im Status-Readback; kein Secret im Renderer.",
    blockedReads: `${input.blockedReads} Live-Read(s) blockiert oder eingeschraenkt.`,
    nextProbe: input.hasSessionToken
      ? "Nach Owner/Admin-Session: Live-Daten neu laden und Endpoint-Status erneut pruefen."
      : "Erst Owner-Session herstellen, dann Live-Daten neu laden.",
  };
}

export function deriveLiveDataUnlockDecision(input: {
  proof: LiveDataUnlockProof;
  items: LiveDataUnlockItem[];
}): LiveDataUnlockDecision {
  const sessionBlocked = input.items.find((item) => /missing_session|Owner-Session fehlt|Owner Auth nicht akzeptiert|neu anmelden|^401$/i.test(item.rawMessage));
  const ownerRoleBlocked = input.items.find((item) => /Owner-Freigabe erforderlich|insufficient_role|learning_review_forbidden|403/i.test(item.rawMessage));
  const connectorBlocked = input.items.find((item) => /reauth|required|invalid_grant|expired|revoked|Neu verbinden/i.test(item.rawMessage));
  const proxyBlocked = input.items.find((item) => /API-Zugriff nicht freigegeben|unauthorized|Bearer/i.test(item.rawMessage));
  const endpoints = input.items.map((item) => item.endpoint).join(", ") || "keine blockierten Endpoints";

  if (sessionBlocked) {
    return {
      label: "Owner-Session erneuern",
      detail: `${sessionBlocked.protectedData} blockiert: ${sessionBlocked.cause}`,
      primaryAction: sessionBlocked.primaryCta,
      safeAction: sessionBlocked.safeCta,
      evidence: `${sessionBlocked.endpoint} · ${sessionBlocked.sentHeader} · ${input.proof.ownerKey}`,
      tone: "session",
    };
  }

  if (ownerRoleBlocked) {
    return {
      label: "Owner/Admin-Session verwenden",
      detail: `${ownerRoleBlocked.protectedData} braucht Owner-/Admin-Rechte; vorhandene Session reicht fuer diesen Live-Read nicht.`,
      primaryAction: ownerRoleBlocked.primaryCta,
      safeAction: ownerRoleBlocked.safeCta,
      evidence: `${ownerRoleBlocked.endpoint} · ${ownerRoleBlocked.statusJudgement}`,
      tone: "owner",
    };
  }

  if (connectorBlocked) {
    return {
      label: "Connector-Reauth entscheiden",
      detail: `${connectorBlocked.protectedData} ist eingeschraenkt; OAuth-/Provider-Freigabe muss erneuert werden.`,
      primaryAction: connectorBlocked.primaryCta,
      safeAction: connectorBlocked.safeCta,
      evidence: `${connectorBlocked.endpoint} · ${connectorBlocked.backendExpectation}`,
      tone: "connector",
    };
  }

  if (proxyBlocked) {
    return {
      label: "Server-/Proxy-Auth pruefen",
      detail: `${proxyBlocked.protectedData} wurde vor der Owner-Session-Pruefung blockiert.`,
      primaryAction: proxyBlocked.primaryCta,
      safeAction: proxyBlocked.safeCta,
      evidence: `${proxyBlocked.endpoint} · ${proxyBlocked.statusJudgement}`,
      tone: "proxy",
    };
  }

  return {
    label: "Live-Daten neu laden",
    detail: input.items.length > 0
      ? "SIMO kann den Blocker nicht eindeutig klassifizieren; Endpoint-Details bleiben als Evidence sichtbar."
      : "Keine blockierten Live-Reads im aktuellen UI-State.",
    primaryAction: input.items[0]?.primaryCta ?? "Live-Daten neu laden",
    safeAction: input.items[0]?.safeCta ?? "Nur verifizierte Daten nutzen",
    evidence: `${endpoints} · ${input.proof.blockedReads}`,
    tone: "unknown",
  };
}

export function deriveOwnerLiveSystemSignals(input: {
  activeRuns: number;
  pendingApprovals: number;
  connectors: DashboardData["connectors"];
  infra: DashboardData["infra"];
  health: DashboardData["health"];
  memoryTotal: number;
  learningProposals: number;
  evidenceCount: number;
  /** OPNAV1: kanonische Human-Gate-Zahl (summary.gates.open); null = UNAVAILABLE. */
  ownerGates: number | null;
}): OwnerLiveSystemSignal[] {
  const connectorReachable = input.connectors.filter((connector) => connector.reachable).length;
  const connectorValue = input.connectors.length > 0 ? `${connectorReachable}/${input.connectors.length}` : "keine";
  const infraOnline =
    input.infra?.orchestrator?.status === "ok" ||
    input.infra?.n8n?.status === "ok" ||
    input.infra?.forgejo?.status === "ok" ||
    input.health?.ok === true;
  const brainSignals = input.learningProposals > 0 ? `${input.learningProposals} Review` : `${input.memoryTotal} Memory`;
  return [
    { label: "Code", value: `${input.activeRuns} Run aktiv` },
    // OPNAV1 Truth-Fixture: Lauf-Freigaben (Approval-Inbox) sind NICHT die
    // Human-Gate-Authority — getrennt gelabelt, kein Kosmetik-Mapping.
    { label: "Freigaben (Läufe)", value: `${input.pendingApprovals} offen` },
    { label: "Owner-Gates", value: input.ownerGates === null ? "UNAVAILABLE" : `${input.ownerGates} offen` },
    { label: "Connectoren", value: connectorValue },
    { label: "Infra", value: infraOnline ? "online" : "pruefen" },
    { label: "Brain", value: brainSignals },
    { label: "Evidence", value: `${input.evidenceCount}` },
  ];
}

export function deriveOwnerConnectorMissionDecision(connectors: DashboardData["connectors"]): OwnerConnectorMissionDecision {
  const total = connectors.length;
  const reachable = connectors.filter((connector) => connector.reachable).length;
  const copies = connectors.map((connector) => ({ connector, copy: ownerConnectorStatusCopy(connector) }));
  const blocked = copies.filter(({ connector }) => !connector.reachable);
  const reauth = blocked.find(({ connector, copy }) =>
    connector.issueCode === "reauth_required" ||
    /reauth|neu verbinden|invalid_grant|expired|revoked/i.test(`${connector.message ?? ""} ${copy.primaryCta}`),
  );
  const setup = blocked.find(({ connector }) =>
    connector.issueCode === "credentials_missing" ||
    connector.issueCode === "authorization_missing" ||
    connector.configured === false,
  );
  const degraded = blocked[0];
  const affected = blocked.map(({ connector }) => connector.name).join(", ") || "keine";

  if (reauth) {
    return {
      label: "Connector-Reauth vorbereiten",
      detail: `${reauth.connector.name}: ${reauth.copy.message}`,
      primaryAction: reauth.copy.primaryCta,
      safeAction: reauth.copy.safeCta,
      ownerPath: reauth.copy.ownerPath,
      affected,
      risk: reauth.copy.risk ?? "Connector-Live-Daten sind bis zur Reauth nicht belastbar.",
      evidence: `${reachable}/${total} Connectoren erreichbar; OAuth-Secret bleibt serverseitig; ${reauth.copy.evidence}`,
      tone: "reauth",
    };
  }

  if (setup) {
    return {
      label: "Connector-Setup klaeren",
      detail: `${setup.connector.name}: ${setup.copy.message}`,
      primaryAction: setup.copy.primaryCta,
      safeAction: setup.copy.safeCta,
      ownerPath: setup.copy.ownerPath,
      affected,
      risk: setup.copy.risk ?? "Connector-Live-Daten sind ohne Setup nicht verfuegbar.",
      evidence: `${reachable}/${total} Connectoren erreichbar; Setup-Evidence fehlt oder ist unvollstaendig.`,
      tone: "setup",
    };
  }

  if (degraded) {
    return {
      label: "Connector-Status pruefen",
      detail: `${degraded.connector.name}: ${degraded.copy.message}`,
      primaryAction: degraded.copy.primaryCta,
      safeAction: degraded.copy.safeCta,
      ownerPath: degraded.copy.ownerPath,
      affected,
      risk: degraded.copy.risk ?? "Mindestens ein Connector liefert aktuell keine belastbaren Live-Daten.",
      evidence: `${reachable}/${total} Connectoren erreichbar; ${degraded.copy.evidence}`,
      tone: "degraded",
    };
  }

  return {
    label: "Connectoren bereit",
    detail: total > 0
      ? `${reachable}/${total} Connectoren erreichbar; SIMO kann diese Live-Daten fuer Missionen nutzen.`
      : "Keine Connectoren gemeldet; SIMO arbeitet ohne Connector-Live-Daten.",
    primaryAction: total > 0 ? "Live-Daten nutzen" : "Connectoren einrichten",
    safeAction: "Ohne Aenderung fortfahren",
    ownerPath: total > 0 ? "kein Settings-Schritt erforderlich" : "Settings > Connectoren > Connectoren einrichten",
    affected,
    risk: "Keine Connector-Reauth aus dem aktuellen Status abgeleitet.",
    evidence: total > 0
      ? "Alle gemeldeten Connectoren sind erreichbar; letzter erfolgreicher Live-Read wird separat noch nicht gemeldet."
      : "Dashboard meldet keine Connector-Eintraege.",
    tone: "ready",
  };
}

export function deriveOwnerVoiceMissionDecision(input: {
  selected?: VoiceOption;
  elevenConfigured: boolean;
  voiceMode: "off" | "server";
  voiceAvailable: boolean;
  recording: boolean;
  processing: boolean;
  speaking: boolean;
  ttsTestStatus: TtsTestStatus;
}): OwnerVoiceMissionDecision {
  const status = voiceSelectionStatus({ selected: input.selected, elevenConfigured: input.elevenConfigured });
  const active = input.recording || input.processing || input.speaking || input.ttsTestStatus.state === "running";
  const cloudNeedsSetup =
    input.selected?.provider === "eleven" &&
    (input.selected.available === false || input.elevenConfigured === false);

  if (input.voiceMode === "off") {
    return {
      label: "Voice einschalten",
      detail: "Voice-first ist aktuell aus; SIMO nimmt Text-Missionen an, hoert aber nicht zu.",
      selected: status.selectedLabel,
      playback: status.playbackLabel,
      primaryAction: "Voice aktivieren",
      safeAction: "Text-Mission senden",
      evidence: `${status.reason} Teststatus: ${input.ttsTestStatus.label}.`,
      tone: "off",
    };
  }

  if (!input.voiceAvailable) {
    return {
      label: "Mikrofon freigeben",
      detail: "Voice-first ist eingeschaltet, aber der Browser meldet kein verfuegbares Mikrofon.",
      selected: status.selectedLabel,
      playback: status.playbackLabel,
      primaryAction: "Mic freigeben",
      safeAction: "Text-Mission senden",
      evidence: `${status.playback} Teststatus: ${input.ttsTestStatus.label}.`,
      tone: "setup",
    };
  }

  if (cloudNeedsSetup) {
    return {
      label: "Voice-Setup klaeren",
      detail: `${status.selectedLabel} ist gewaehlt, aber Test/Playback faellt sicher auf ${status.playbackLabel} zurueck.`,
      selected: status.selectedLabel,
      playback: status.playbackLabel,
      primaryAction: status.action,
      safeAction: "Piper lokal nutzen",
      evidence: `${status.reason} Teststatus: ${input.ttsTestStatus.label}.`,
      tone: "setup",
    };
  }

  if (active) {
    return {
      label: "Voice aktiv",
      detail: `${status.selectedLabel} ist gewaehlt; aktueller Test/Playback: ${status.playbackLabel}.`,
      selected: status.selectedLabel,
      playback: status.playbackLabel,
      primaryAction: input.ttsTestStatus.state === "running" ? "Test abwarten" : "Weiter sprechen",
      safeAction: "Stoppen",
      evidence: `${status.reason} Teststatus: ${input.ttsTestStatus.label}.`,
      tone: "active",
    };
  }

  return {
    label: "Voice bereit",
    detail: `${status.selectedLabel} ist auswaehlbar; Test spielt ${status.playbackLabel}.`,
    selected: status.selectedLabel,
    playback: status.playbackLabel,
    primaryAction: status.testLabel,
    safeAction: status.selectedLabel === status.playbackLabel ? "Text-Mission senden" : "Piper lokal nutzen",
    evidence: `${status.reason} Teststatus: ${input.ttsTestStatus.label}.`,
    tone: status.selectedLabel === status.playbackLabel ? "ready" : "fallback",
  };
}

export function deriveOwnerWatchingBrief(input: {
  systems: OwnerLiveSystemSignal[];
  activeRuns: number;
  pendingApprovals: number;
  dataErrors: number;
}): OwnerWatchingBrief {
  const connectors = input.systems.find((system) => system.label === "Connectoren")?.value ?? "keine";
  const infra = input.systems.find((system) => system.label === "Infra")?.value ?? "unbekannt";
  const evidence = input.systems.find((system) => system.label === "Evidence")?.value ?? "0";
  if (input.dataErrors > 0) {
    return {
      label: "Watch Brief: Blocker",
      detail: `${input.dataErrors} Live-Datenblocker; Connectoren ${connectors}, Infra ${infra}.`,
      tone: "degraded",
    };
  }
  if (input.pendingApprovals > 0) {
    return {
      // OPNAV1 Truth-Fixture: das sind Lauf-Freigaben (Approval-Inbox), nicht
      // die Human-Gate-Authority — Formulierung präzisiert.
      label: "Watch Brief: Freigabe",
      detail: `${input.pendingApprovals} Lauf-Freigabe offen; SIMO beobachtet ${input.systems.length} Systeme.`,
      tone: "approval",
    };
  }
  if (input.activeRuns > 0) {
    return {
      label: "Watch Brief: Arbeitet",
      detail: `${input.activeRuns} Run aktiv; Evidence ${evidence}, Connectoren ${connectors}.`,
      tone: "watching",
    };
  }
  return {
    label: "Watch Brief: Bereit",
    detail: `${input.systems.length} Systeme im Blick; Connectoren ${connectors}, Evidence ${evidence}.`,
    tone: "ready",
  };
}

export function mapCosmicVisualToOrbState(state: CosmicVisualState): Orb3DState {
  return COSMIC_TO_ORB_STATE[state];
}

export function mapOrbToCosmicVisualState(input: {
  orb: ChatOrbState;
  hasPendingApprovals: boolean;
  hasErrors: boolean;
  hasActiveWork: boolean;
  hasSuccess: boolean;
}): CosmicVisualState {
  if (input.hasErrors || input.orb === "error") return "error";
  if (input.hasPendingApprovals || input.orb === "blocked" || input.orb === "confirming") return "approval_required";
  if (input.hasActiveWork || input.orb === "acting") return "working";
  if (input.orb === "thinking") return "thinking";
  if (input.orb === "wake") return "listening";
  if (input.hasSuccess || input.orb === "speaking") return "success";
  return "idle";
}

export function parseCosmicVisualMock(search: string): CosmicVisualMock {
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const state = params.get("cosmicState");
  const amplitudeParam = params.get("cosmicAmplitude");
  const amplitudeRaw = Number(amplitudeParam ?? "0");
  const validState = state && state in COSMIC_STATE_LABEL ? (state as CosmicVisualState) : null;
  const amplitude = amplitudeParam === null ? null : Number.isFinite(amplitudeRaw) ? Math.max(0, Math.min(1, amplitudeRaw)) : 0;
  return { state: validState, amplitude };
}

export function parseCosmicPerformanceQuery(search: string): {
  mode: CosmicPerformanceMode | null;
  reducedMotion: boolean | null;
} {
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const rawMode = params.get("cosmicQuality");
  const mode = rawMode && COSMIC_PERFORMANCE_MODES.includes(rawMode as CosmicPerformanceMode) ? (rawMode as CosmicPerformanceMode) : null;
  const rawReducedMotion = params.get("cosmicReducedMotion");
  const reducedMotion =
    rawReducedMotion === null
      ? null
      : rawReducedMotion === "1" || rawReducedMotion === "true"
        ? true
        : rawReducedMotion === "0" || rawReducedMotion === "false"
          ? false
          : null;
  return { mode, reducedMotion };
}

function useCosmicVisualMock(): CosmicVisualMock {
  const [mock, setMock] = useState<CosmicVisualMock>(() =>
    typeof window === "undefined" ? { state: null, amplitude: null } : parseCosmicVisualMock(window.location.search),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = (): void => setMock(parseCosmicVisualMock(window.location.search));
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  return mock;
}

export function resolveCosmicPerformanceProfile(input: {
  queryMode?: CosmicPerformanceMode | null;
  queryReducedMotion?: boolean | null;
  prefersReducedMotion: boolean;
  width: number;
}): CosmicPerformanceProfile {
  const reducedMotion = input.queryReducedMotion ?? input.prefersReducedMotion;
  if (input.queryMode) {
    return {
      mode: input.queryMode,
      renderQuality: input.queryMode,
      reducedMotion,
      starfield: input.queryMode !== "low" && !reducedMotion,
      reason: "query",
    };
  }
  if (reducedMotion) {
    return { mode: "low", renderQuality: "low", reducedMotion, starfield: false, reason: "reduced_motion" };
  }
  if (input.width > 0 && input.width < 768) {
    return { mode: "low", renderQuality: "low", reducedMotion, starfield: false, reason: "mobile" };
  }
  if (input.width > 0 && input.width < 1180) {
    return { mode: "balanced", renderQuality: "balanced", reducedMotion, starfield: true, reason: "tablet" };
  }
  return { mode: "high", renderQuality: "high", reducedMotion, starfield: true, reason: "default" };
}

function useCosmicPerformanceProfile(): CosmicPerformanceProfile {
  const [profile, setProfile] = useState<CosmicPerformanceProfile>(() => {
    const parsed = parseCosmicPerformanceQuery(typeof window === "undefined" ? "" : window.location.search);
    return resolveCosmicPerformanceProfile({
      queryMode: parsed.mode,
      queryReducedMotion: parsed.reducedMotion,
      prefersReducedMotion: false,
      width: typeof window === "undefined" ? 0 : window.innerWidth,
    });
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const compute = (): void => {
      const parsed = parseCosmicPerformanceQuery(window.location.search);
      setProfile(resolveCosmicPerformanceProfile({
        queryMode: parsed.mode,
        queryReducedMotion: parsed.reducedMotion,
        prefersReducedMotion: motionQuery.matches,
        width: window.innerWidth,
      }));
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("popstate", compute);
    motionQuery.addEventListener("change", compute);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("popstate", compute);
      motionQuery.removeEventListener("change", compute);
    };
  }, []);

  return profile;
}

export function deriveCosmicSceneSnapshot(input: {
  orb: ChatOrbState;
  pendingApprovals: number;
  activeRuns: number;
  completedRuns: number;
  evidenceCount: number;
  dataErrors: number;
  learningProposals: number;
  notifications: number;
  toolRuns: number;
  infraOnline: boolean;
  connectorReachable?: number;
  connectorTotal?: number;
  memoryTotal: number;
  voice?: CosmicVoiceSignal;
  ownerRuntime?: CosmicOwnerRuntimeSignal;
  mockState?: CosmicVisualState | null;
  mockAmplitude?: number | null;
}): CosmicSceneSnapshot {
  const hasApprovalGate = input.pendingApprovals > 0;
  const connectorTotal = input.connectorTotal ?? 0;
  const connectorReachable = input.connectorReachable ?? connectorTotal;
  const hasConnectorDegraded = connectorTotal > 0 && connectorReachable < connectorTotal;
  const hasErrors = input.dataErrors > 0 || input.orb === "error" || hasConnectorDegraded;
  const hasSuccess = !hasApprovalGate && !hasErrors && input.activeRuns === 0 && (input.completedRuns > 0 || input.evidenceCount > 0);
  const dataVisualState = mapOrbToCosmicVisualState({
      orb: input.orb,
      hasPendingApprovals: hasApprovalGate,
      hasErrors,
      hasActiveWork: input.activeRuns > 0,
      hasSuccess,
    });
  const voiceVisualState =
    input.voice?.listening ? "listening" : input.voice?.thinking ? "thinking" : input.voice?.speaking ? "success" : null;
  const visualState = input.mockState ?? (dataVisualState === "idle" && voiceVisualState ? voiceVisualState : dataVisualState);
  const active = new Set<CosmicAgentId>();

  if (input.activeRuns > 0 || visualState === "working") active.add("code");
  if (input.infraOnline) active.add("ops");
  if (hasApprovalGate || hasErrors) active.add("security");
  if (input.notifications > 0) active.add("support");
  if (input.toolRuns > 0) active.add("research");
  if (input.memoryTotal > 0 || input.learningProposals > 0) active.add("brain");
  if (hasConnectorDegraded) active.add("support");
  if (visualState === "thinking" || visualState === "listening") active.add("ux");
  if (visualState === "listening" || visualState === "success") active.add("brain");
  if (visualState === "approval_required") active.add("security");
  if (visualState === "success") active.add("ops");
  if (input.activeRuns > 0 || input.notifications > 0) active.add("business");
  if (active.size === 0) {
    active.add("ops");
    active.add("brain");
  }

  const activeDockZones = [...new Set(COSMIC_AGENT_NODES.filter((agent) => active.has(agent.id)).map((agent) => agent.dock))];
  const orbState = mapCosmicVisualToOrbState(visualState);
  const voiceLane: CosmicLaneSignal = {
    id: "voice",
    label: "Voice",
    status: input.voice?.listening ? "hoert zu" : input.voice?.speaking ? "spricht" : input.voice?.thinking ? "verarbeitet" : "bereit",
    tone: input.voice?.listening || input.voice?.speaking ? "voice" : "ready",
    active: input.voice?.listening === true || input.voice?.speaking === true || input.voice?.thinking === true,
  };
  const agentLane: CosmicLaneSignal = {
    id: "agent",
    label: "Agent",
    status: input.activeRuns > 0 ? `${input.activeRuns} Run aktiv` : input.toolRuns > 0 ? `${input.toolRuns} Tool-Run` : "bereit",
    tone: input.activeRuns > 0 || input.toolRuns > 0 ? "working" : "ready",
    active: input.activeRuns > 0 || input.toolRuns > 0,
  };
  const ownerLane: CosmicLaneSignal = {
    id: "owner",
    label: "Owner Gate",
    status: hasApprovalGate ? `${input.pendingApprovals} Entscheidung offen` : "kein Gate offen",
    tone: hasApprovalGate ? "approval" : "ready",
    active: hasApprovalGate,
  };
  const connectorLane: CosmicLaneSignal = {
    id: "connector",
    label: "Connectoren",
    status: connectorTotal > 0 ? `${connectorReachable}/${connectorTotal} erreichbar` : "keine Live-Daten",
    tone: hasConnectorDegraded ? "degraded" : "ready",
    active: connectorTotal > 0,
  };
  const infraLane: CosmicLaneSignal = {
    id: "infra",
    label: "Infra",
    status: input.infraOnline ? "online" : "nicht bestaetigt",
    tone: input.infraOnline ? "ready" : "degraded",
    active: input.infraOnline,
  };
  const brainLane: CosmicLaneSignal = {
    id: "brain",
    label: "Brain",
    status: input.learningProposals > 0 ? `${input.learningProposals} Review` : input.memoryTotal > 0 ? `${input.memoryTotal} Memory` : "ruhig",
    tone: input.learningProposals > 0 ? "working" : "ready",
    active: input.learningProposals > 0 || input.memoryTotal > 0,
  };
  const livingTone: CosmicLivingTone =
    visualState === "error" ? "blocked" :
      visualState === "approval_required" ? "approval" :
        visualState === "working" || visualState === "thinking" ? "working" :
          visualState === "listening" || visualState === "success" ? "voice" :
            hasConnectorDegraded || !input.infraOnline ? "degraded" :
              "ready";
  const livingCopy: Record<CosmicLivingTone, { label: string; prompt: string }> = {
    ready: { label: "Bereit", prompt: "Naechste Mission starten" },
    voice: { label: visualState === "listening" ? "Hoert zu" : "Spricht", prompt: "Voice-Dialog aktiv" },
    working: { label: visualState === "thinking" ? "Denkt" : "Arbeitet", prompt: "Runstream beobachten" },
    approval: { label: "Owner Gate", prompt: "Freigabe entscheiden" },
    degraded: { label: "Teilweise degradiert", prompt: "Connectoren oder Infra pruefen" },
    blocked: { label: "Blockiert", prompt: "Live-Daten oder Fehler klaeren" },
  };
  // Owner-Zustand strikt aus echten Signalen; ein Visual-Mock (QA-Query)
  // uebersteuert nur die Szene, nicht die Owner-Ehrlichkeit — ausser er ist
  // explizit gesetzt, dann folgt der Owner-Zustand dem Mock sichtbar-als-Mock.
  const ownerState: CosmicOwnerState = input.mockState
    ? deriveCosmicOwnerStateFromMock(input.mockState)
    : deriveCosmicOwnerState({
        orb: input.orb,
        pendingApprovals: input.pendingApprovals,
        dataErrors: input.dataErrors,
        voice: input.voice,
        runtime: input.ownerRuntime,
      });
  return {
    orbState,
    visualState,
    label: COSMIC_STATE_LABEL[visualState],
    ownerState,
    ownerStateLabel: COSMIC_OWNER_STATE_LABEL[ownerState],
    ownerOrbState: COSMIC_OWNER_STATE_TO_ORB[ownerState],
    livingTone,
    livingLabel: livingCopy[livingTone].label,
    livingPrompt: livingCopy[livingTone].prompt,
    ownerCue: deriveOwnerLivingCue({
      activeRuns: input.activeRuns,
      pendingApprovals: input.pendingApprovals,
      dataErrors: input.dataErrors,
      learningProposals: input.learningProposals,
      evidenceCount: input.evidenceCount,
      infraOnline: input.infraOnline,
      connectorReachable,
      connectorTotal,
      voice: input.voice,
    }),
    amplitude: Math.max(0, Math.min(1, input.mockAmplitude ?? input.voice?.amplitude ?? (visualState === "listening" ? 0.42 : 0))),
    activeAgents: [...active],
    activeDockZones,
    activeLanes: [voiceLane, agentLane, ownerLane, connectorLane, infraLane, brainLane],
    hasApprovalGate: hasApprovalGate || visualState === "approval_required",
    hasErrors,
  };
}

export function deriveCosmicVoiceSignal(input: {
  recording?: boolean;
  processing?: boolean;
  speaking?: boolean;
  amplitude?: number | null;
}): CosmicVoiceSignal {
  const listening = input.recording === true;
  const thinking = !listening && input.processing === true;
  const speaking = !listening && !thinking && input.speaking === true;
  const fallbackAmplitude = listening ? 0.58 : speaking ? 0.32 : thinking ? 0.18 : 0;
  const amplitude = input.amplitude == null ? fallbackAmplitude : input.amplitude;
  return {
    listening,
    thinking,
    speaking,
    amplitude: Math.max(0, Math.min(1, amplitude)),
  };
}

function textHas(input: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(input));
}

function deriveAgentWorkspaceDecision(input: {
  hasPrompt: boolean;
  hasAssistant: boolean;
  pendingAssistant: boolean;
  pendingApprovals: number;
  activeRuns: number;
  evidenceCount: number;
  dataErrors: number;
  hasDiffSignal: boolean;
  hasTestSignal: boolean;
}): AgentWorkspaceDecision {
  if (input.dataErrors > 0) {
    return {
      label: "Live-Daten blockiert",
      detail: `${input.dataErrors} Live-Read muss per Owner-Session, Reauth oder sicherem Retry geklaert werden.`,
      primaryAction: "Owner Unlock pruefen",
      tone: "blocked",
    };
  }
  if (input.pendingApprovals > 0) {
    return {
      label: "Owner-Gate wartet",
      detail: `${input.pendingApprovals} Entscheidung offen; SIMO darf hier nicht automatisch weitergehen.`,
      primaryAction: "Freigabe entscheiden",
      tone: "approval",
    };
  }
  if (input.activeRuns > 0) {
    return {
      label: "Run aktiv",
      detail: "SIMO arbeitet; beobachte Tool-Ausgaben, Tests, Diff und Evidence im Arbeitsfluss.",
      primaryAction: "Runstream beobachten",
      tone: "working",
    };
  }
  if (!input.hasPrompt) {
    return {
      label: "Bereit fuer Mission",
      detail: "Sprich oder schreibe den nächsten Auftrag; SIMO startet erst nach deiner Mission.",
      primaryAction: "Mission starten",
      tone: "ready",
    };
  }
  if (input.pendingAssistant || !input.hasAssistant) {
    return {
      label: "Plan entsteht",
      detail: "Prompt ist aufgenommen; Antwort, Plan und Tool-Grenzen muessen als naechstes sichtbar werden.",
      primaryAction: "Plan abwarten",
      tone: "working",
    };
  }
  if (input.hasDiffSignal || input.hasTestSignal || input.evidenceCount > 0) {
    return {
      label: "Review bereit",
      detail: "Plan, Diff/Test-Signale oder Evidence liegen vor; naechster Schritt ist Pruefung statt Blindflug.",
      primaryAction: "Evidence pruefen",
      tone: "review",
    };
  }
  return {
    label: "Naechsten Schritt klaeren",
    detail: "SIMO hat geantwortet; falls Code-/Ops-Arbeit noetig ist, als gated Code Workspace starten.",
    primaryAction: "Mission schaerfen",
    tone: "ready",
  };
}

function deriveAgentWorkspaceProgressCue(input: {
  pendingApprovals: number;
  activeRuns: number;
  dataErrors: number;
  livePreflightBlockers?: LivePreflightBlockerSummary;
  latestRuntimeRun: DashboardData["runs"][number] | null;
  latestTimelineEvent: DashboardData["timeline"][number] | null;
  pendingAssistant: boolean;
  hasPrompt: boolean;
}): AgentWorkspaceProgressCue {
  if (input.dataErrors > 0) {
    return {
      label: input.livePreflightBlockers?.label ?? "Blocker erkannt",
      detail: input.livePreflightBlockers?.detail ?? `${input.dataErrors} Live-Read blockiert; konkrete Ursache pruefen.`,
      tone: "blocked",
    };
  }
  if (input.pendingApprovals > 0) {
    return {
      label: "Gate wartet",
      detail: `${input.pendingApprovals} Owner-Entscheidung offen; keine automatische Fortsetzung.`,
      tone: "approval",
    };
  }
  if (input.latestTimelineEvent) {
    const event = input.latestTimelineEvent;
    const action = event.actionId ?? event.type;
    const status = event.status ?? "signal";
    const evidence = event.evidenceRef ? ` Evidence: ${event.evidenceRef}.` : "";
    const request = event.requestId ? ` Request: ${event.requestId}.` : "";
    return {
      label: `Letztes Signal: ${action}`,
      detail: `${status}: ${event.summary}.${evidence}${request}`.trim(),
      tone: status === "error" || status === "failed" ? "blocked" : "working",
    };
  }
  if (input.latestRuntimeRun) {
    return {
      label: `Run ${input.latestRuntimeRun.id}`,
      detail: `${input.latestRuntimeRun.status}: Phase ${input.latestRuntimeRun.phase}.`,
      tone: input.latestRuntimeRun.status === "error" || input.latestRuntimeRun.status === "cancelled" ? "blocked" : "working",
    };
  }
  if (input.activeRuns > 0) {
    return {
      label: "Runstream aktiv",
      detail: `${input.activeRuns} Run aktiv; Tool-Ausgaben und Gates werden hier verdichtet.`,
      tone: "working",
    };
  }
  if (input.pendingAssistant) {
    return {
      label: "Antwort entsteht",
      detail: "SIMO baut gerade Plan, Tool-Grenzen und nächsten Schritt auf.",
      tone: "working",
    };
  }
  if (!input.hasPrompt) {
    return {
      label: "Bereit",
      detail: "Noch kein Auftrag im aktuellen Arbeitsfluss.",
      tone: "ready",
    };
  }
  return {
    label: "Arbeitsfluss bereit",
    detail: "Prompt und Antwort sind sichtbar; naechster Schritt ist Review, Gate oder neue Mission.",
    tone: "review",
  };
}

function deriveAgentWorkspaceOutcome(input: {
  hasPrompt: boolean;
  dataErrors: number;
  livePreflightBlockers?: LivePreflightBlockerSummary;
  pendingApprovals: number;
  activeRuns: number;
  evidenceCount: number;
  failedTool: DashboardData["toolRuns"][number] | undefined;
  latestRuntimeRun: DashboardData["runs"][number] | null;
  hasTestSignal: boolean;
  hasDiffSignal: boolean;
}): AgentWorkspaceOutcome {
  if (input.dataErrors > 0) {
    return {
      label: "NO-GO",
      detail: input.livePreflightBlockers?.detail ?? `${input.dataErrors} Live-Datenblocker verhindern eine belastbare Freigabe.`,
      evidence: input.livePreflightBlockers?.evidence ?? "Konkrete Live-Preflight-Ursache pruefen.",
      tone: "blocked",
    };
  }
  if (input.failedTool) {
    return {
      label: "NO-GO",
      detail: `${input.failedTool.toolId} meldet ${input.failedTool.status}.`,
      evidence: "Fehler beheben und Gate erneut laufen lassen.",
      tone: "blocked",
    };
  }
  if (input.latestRuntimeRun?.status === "error" || input.latestRuntimeRun?.status === "cancelled") {
    return {
      label: "NO-GO",
      detail: `Run ${input.latestRuntimeRun.id} ist ${input.latestRuntimeRun.status}.`,
      evidence: "Run-Fehler oder Abbruch vor Freigabe klaeren.",
      tone: "blocked",
    };
  }
  if (input.pendingApprovals > 0) {
    return {
      label: "CONDITIONAL GO",
      detail: `${input.pendingApprovals} Owner-Entscheidung offen.`,
      evidence: "GO erst nach Owner-Gate moeglich.",
      tone: "approval",
    };
  }
  if (input.activeRuns > 0 || input.latestRuntimeRun?.status === "running" || input.latestRuntimeRun?.status === "awaiting_approval") {
    return {
      label: "CONDITIONAL GO",
      detail: "Run ist noch nicht final abgeschlossen.",
      evidence: "Runstream, Tests und Evidence weiter beobachten.",
      tone: "working",
    };
  }
  if (input.evidenceCount > 0 && input.hasTestSignal && input.hasDiffSignal) {
    return {
      label: "GO",
      detail: "Diff-, Test- und Evidence-Signale liegen ohne offene Blocker vor.",
      evidence: `${input.evidenceCount} Evidence-Nachweise.`,
      tone: "review",
    };
  }
  if (input.evidenceCount > 0) {
    return {
      label: "CONDITIONAL GO",
      detail: "Evidence liegt vor, aber Diff-/Test-Signale sind nicht vollstaendig.",
      evidence: `${input.evidenceCount} Evidence-Nachweise; fehlende Signale pruefen.`,
      tone: "review",
    };
  }
  return {
    label: "OFFEN",
    detail: input.hasPrompt ? "Mission ist noch nicht bewertbar." : "Noch keine Mission im Arbeitsfluss.",
    evidence: "GO/CONDITIONAL GO/NO-GO entsteht erst aus Run-, Gate- und Evidence-Signalen.",
    tone: "ready",
  };
}

function trimTranscriptDetail(value: string, max = 180): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > max ? `${normalized.slice(0, max - 1)}...` : normalized;
}

function deriveAgentWorkspaceTranscript(input: {
  history: ChatController["history"];
  draftMission?: string;
  commandRuns: DashboardData["runs"];
  toolRuns: DashboardData["toolRuns"];
  timeline: DashboardData["timeline"];
  pendingApprovals: number;
}): AgentWorkspaceTranscriptItem[] {
  const items: AgentWorkspaceTranscriptItem[] = [];
  const lastUser = [...input.history].reverse().find((message) => message.role === "user");
  const lastAssistant = [...input.history].reverse().find((message) => message.role === "assistant");
  const latestRun = input.commandRuns[0] ?? null;
  const draft = input.draftMission?.trim() ?? "";

  if (lastUser) {
    items.push({
      id: `owner-${lastUser.id}`,
      source: "owner",
      title: "Owner Prompt",
      detail: trimTranscriptDetail(lastUser.content),
      tone: "ready",
    });
  }

  if (!lastUser && draft) {
    items.push({
      id: "owner-draft",
      source: "owner",
      title: "Mission Entwurf",
      detail: trimTranscriptDetail(draft),
      tone: "ready",
    });
  }

  if (lastAssistant) {
    const toolNames = lastAssistant.meta?.usedToolNames?.join(", ");
    items.push({
      id: `simo-${lastAssistant.id}`,
      source: "simo",
      title: lastAssistant.pending ? "SIMO plant" : "SIMO Antwort",
      detail: trimTranscriptDetail(toolNames ? `${lastAssistant.content} Tools: ${toolNames}.` : lastAssistant.content),
      tone: lastAssistant.pending ? "working" : "review",
    });
  }

  if (latestRun) {
    items.push({
      id: `run-${latestRun.id}`,
      source: "run",
      title: `Run ${latestRun.status}`,
      detail: trimTranscriptDetail(`${latestRun.phase}: ${latestRun.request}`),
      tone: latestRun.status === "error" || latestRun.status === "cancelled"
        ? "blocked"
        : latestRun.status === "running" || latestRun.status === "awaiting_approval"
          ? "working"
          : "review",
    });
  }

  for (const tool of input.toolRuns.slice(0, 2)) {
    items.push({
      id: `tool-${tool.id}`,
      source: "tool",
      title: `${tool.toolId} ${tool.status}`,
      detail: trimTranscriptDetail(`${tool.riskLevel} · ${tool.durationMs ?? 0}ms`),
      tone: tool.status === "error" || tool.status === "failed" ? "blocked" : "working",
    });
  }

  for (const event of input.timeline.slice(0, 2)) {
    items.push({
      id: `timeline-${event.id}`,
      source: "timeline",
      title: event.actionId ?? event.type,
      detail: trimTranscriptDetail(`${event.status}: ${event.summary}${event.evidenceRef ? ` · Evidence ${event.evidenceRef}` : ""}`),
      tone: event.status === "error" || event.status === "failed" ? "blocked" : "working",
    });
  }

  if (input.pendingApprovals > 0) {
    items.push({
      id: "gate-pending",
      source: "gate",
      title: "Owner Gate",
      detail: `${input.pendingApprovals} Entscheidung offen; keine automatische Fortsetzung.`,
      tone: "approval",
    });
  }

  if (items.length === 0) {
    items.push({
      id: "transcript-empty",
      source: "simo",
      title: "Bereit",
      detail: "Noch kein Prompt, Run, Tool oder Gate im aktuellen Arbeitsfluss.",
      tone: "ready",
    });
  }

  return items.slice(0, 6);
}

function deriveAgentWorkspacePlanBrief(input: {
  lastUserContent: string | null;
  draftMission?: string;
  lastAssistantContent: string | null;
  latestRuntimeRun: DashboardData["runs"][number] | null;
  latestTimelineEvent: DashboardData["timeline"][number] | null;
  pendingApprovals: number;
  activeRuns: number;
  dataErrors: number;
  livePreflightBlockers?: LivePreflightBlockerSummary;
  evidenceCount: number;
  hasDiffSignal: boolean;
  hasTestSignal: boolean;
  failedTool: DashboardData["toolRuns"][number] | undefined;
  decision: AgentWorkspaceDecision;
  outcome: AgentWorkspaceOutcome;
}): AgentWorkspacePlanBrief {
  const draft = input.draftMission?.trim() ?? "";
  const objective =
    input.latestRuntimeRun?.request?.trim() ||
    input.lastUserContent?.trim() ||
    draft ||
    "Noch keine Mission im aktuellen Arbeitsfluss.";
  const currentStep = input.latestTimelineEvent
    ? `${input.latestTimelineEvent.actionId ?? input.latestTimelineEvent.type}: ${input.latestTimelineEvent.status ?? "signal"}`
    : input.latestRuntimeRun
      ? `${input.latestRuntimeRun.status}: ${input.latestRuntimeRun.phase}`
      : input.lastAssistantContent
        ? "Antwort liegt vor; Plan/Gate/Evidence pruefen."
        : input.lastUserContent
          ? "Prompt aufgenommen; SIMO muss Plan und nächsten Schritt zeigen."
          : draft
            ? "Mission-Entwurf bereit; Start erzeugt erst danach Run, Gate und Evidence."
          : "Bereit fuer Prompt oder Voice-Mission.";
  const nextStep = input.decision.primaryAction;
  const guardrail = input.dataErrors > 0
    ? `${input.livePreflightBlockers?.detail ?? "Live-Datenblocker zuerst klaeren"}; kein belastbares GO.`
    : input.failedTool
      ? `${input.failedTool.toolId} meldet ${input.failedTool.status}; Fehler vor Fortsetzung pruefen.`
      : input.pendingApprovals > 0
        ? "Owner-Gate offen; keine automatische Fortsetzung."
        : input.activeRuns > 0
          ? "Run aktiv; Ergebnis erst nach Tool-/Test-/Evidence-Signalen final bewerten."
          : "Keine offene Owner-Entscheidung im aktuellen Snapshot.";
  const evidence =
    input.outcome.label === "GO"
      ? input.outcome.evidence
      : input.evidenceCount > 0
        ? `${input.evidenceCount} Evidence-Nachweise; Diff=${input.hasDiffSignal ? "ja" : "offen"}, Tests=${input.hasTestSignal ? "ja" : "offen"}.`
        : "Evidence entsteht erst durch Run-, Tool-, Test- oder Gate-Signale.";

  return {
    objective: trimTranscriptDetail(objective, 140),
    currentStep: trimTranscriptDetail(currentStep, 140),
    nextStep,
    guardrail,
    evidence,
    tone: input.outcome.tone,
  };
}

function deriveAgentWorkspaceDraftReply(input: {
  draftMission: string;
  hasUserPrompt: boolean;
  hasAssistant: boolean;
  dataErrors: number;
  livePreflightBlockers?: LivePreflightBlockerSummary;
  pendingApprovals: number;
  activeRuns: number;
}): AgentWorkspaceDraftReply | null {
  const draft = input.draftMission.trim();
  if (!draft || input.hasUserPrompt || input.hasAssistant) return null;
  const lowerDraft = draft.toLowerCase();
  const systems = [
    ["SimoT", /\bsimot\b/i],
    ["Forge", /\bforge\b|forgejo/i],
    ["Kuma", /\bkuma\b|monitoring/i],
    ["Connectoren", /connector|gmail|calendar|outlook|teams|github/i],
    ["Voice", /voice|stimme|tts|mikro|mic/i],
    ["Owner Auth", /auth|owner|401|session|login/i],
    ["Brain", /brain|learning|memory|lernen/i],
  ].filter(([, pattern]) => (pattern as RegExp).test(draft)).map(([label]) => label);
  const systemDetail = systems.length > 0 ? systems.join(", ") : "Systeme werden erst beim Start aus Kontext und Tools bestaetigt.";
  const intent = /prüfe|pruefe|check|status|grün|gruen|readback|monitor|kuma|forge/i.test(draft)
    ? "Pruefen / Readback"
    : /fix|beheb|korrig|änder|aender|implement|bau|erstelle/i.test(draft)
      ? "Aenderung vorbereiten"
      : "Mission klaeren";
  const boundaryStatus: AgentWorkspaceStageStatus = input.dataErrors > 0
    ? "blocked"
    : input.pendingApprovals > 0
      ? "approval"
      : input.activeRuns > 0
        ? "active"
        : "waiting";
  const boundaryDetail = input.dataErrors > 0
    ? input.livePreflightBlockers?.detail ?? "Live-Daten-Blocker zuerst klaeren."
    : input.pendingApprovals > 0
      ? "Owner-Gate entscheidet vor neuer Ausfuehrung."
      : input.activeRuns > 0
        ? "Laufender Run bleibt sichtbar; kein verdeckter Parallelstart."
        : "Preflight bleibt read-only bis zum Start.";
  const checklist: AgentWorkspaceDraftChecklistItem[] = [
    { label: "Kontext erkannt", detail: systemDetail, status: systems.length > 0 ? "done" : "waiting" },
    { label: "Zieltyp", detail: intent, status: "done" },
    { label: "Sichere Grenze", detail: boundaryDetail, status: boundaryStatus },
    { label: "Startziel", detail: "Start erzeugt erst danach Request, Run, Gate und Evidence.", status: "waiting" },
  ];

  if (input.dataErrors > 0) {
    return {
      headline: "Ich habe die Mission als Entwurf verstanden.",
      understanding: trimTranscriptDetail(`Du willst: ${draft}`, 160),
      nextAction: "Live-Daten entsperren, danach Mission starten oder sicher begrenzen.",
      guardrail: input.livePreflightBlockers?.detail ?? "Kein Runstart, solange Live-Preflight-Blocker offen sind.",
      evidence: input.livePreflightBlockers?.evidence ?? "Preflight aus Composer-Entwurf; kein Request, kein Run, kein Worktree.",
      checklist,
      tone: "blocked",
    };
  }

  if (input.pendingApprovals > 0) {
    return {
      headline: "Ich halte die Mission bis zum Owner-Gate bereit.",
      understanding: trimTranscriptDetail(`Du willst: ${draft}`, 160),
      nextAction: "Owner-Gate entscheiden, danach Mission als Agent-Run starten.",
      guardrail: "Keine neue Ausfuehrung ueber eine offene Owner-Entscheidung hinweg.",
      evidence: "Preflight aus Composer-Entwurf; kein Request, kein Run, kein Worktree.",
      checklist,
      tone: "approval",
    };
  }

  if (input.activeRuns > 0) {
    return {
      headline: "Ich behandle die Mission als Ergaenzung zum laufenden Run.",
      understanding: trimTranscriptDetail(`Du willst: ${draft}`, 160),
      nextAction: "Als Chat-Ergaenzung senden oder laufenden Runstream weiter beobachten.",
      guardrail: "Kein verdeckter Parallel-Start; aktive Runs bleiben sichtbar.",
      evidence: "Preflight aus Composer-Entwurf; kein neuer Request, kein neuer Worktree.",
      checklist,
      tone: "working",
    };
  }

  return {
    headline: "Ich habe die Mission verstanden.",
    understanding: trimTranscriptDetail(`Du willst: ${draft}`, 160),
    nextAction: "Mission starten erzeugt erst danach Request, Run, Gate und Evidence.",
    guardrail: "Bis zum Start bleibt alles Preflight: keine Tools, keine Writes, kein GO.",
    evidence: "Preflight aus Composer-Entwurf; kein Request, kein Run, kein Worktree.",
    checklist,
    tone: "ready",
  };
}

function deriveAgentWorkspaceFocus(input: {
  decision: AgentWorkspaceDecision;
  progressCue: AgentWorkspaceProgressCue;
  outcome: AgentWorkspaceOutcome;
  planBrief: AgentWorkspacePlanBrief;
  transcript: AgentWorkspaceTranscriptItem[];
}): AgentWorkspaceFocus {
  const latest =
    input.transcript.find((item) => item.source === "timeline") ??
    input.transcript.find((item) => item.source === "tool") ??
    input.transcript.find((item) => item.source === "run") ??
    input.transcript[0];
  if (input.outcome.tone === "blocked" || input.decision.tone === "blocked") {
    return {
      label: "Fokus: Blocker",
      primary: input.decision.detail,
      secondary: input.planBrief.guardrail,
      nextAction: input.decision.primaryAction,
      supporting: latest ? `${latest.title}: ${latest.detail}` : input.outcome.evidence,
      tone: "blocked",
    };
  }
  if (input.decision.tone === "approval" || input.outcome.tone === "approval") {
    return {
      label: "Fokus: Owner-Gate",
      primary: input.decision.detail,
      secondary: input.planBrief.guardrail,
      nextAction: input.decision.primaryAction,
      supporting: input.planBrief.evidence,
      tone: "approval",
    };
  }
  if (input.decision.tone === "working" || input.progressCue.tone === "working") {
    return {
      label: "Fokus: Run beobachten",
      primary: input.progressCue.detail,
      secondary: input.planBrief.currentStep,
      nextAction: input.planBrief.nextStep,
      supporting: latest ? `${latest.title}: ${latest.detail}` : input.planBrief.evidence,
      tone: "working",
    };
  }
  if (input.outcome.label === "GO" || input.decision.tone === "review" || input.outcome.tone === "review") {
    return {
      label: "Fokus: Review",
      primary: input.outcome.detail,
      secondary: input.planBrief.evidence,
      nextAction: input.decision.primaryAction,
      supporting: input.planBrief.currentStep,
      tone: "review",
    };
  }
  return {
    label: "Fokus: Mission starten",
    primary: input.decision.detail,
    secondary: input.planBrief.objective,
    nextAction: input.decision.primaryAction,
    supporting: input.planBrief.guardrail,
    tone: "ready",
  };
}

export function deriveAgentWorkspaceSnapshot(input: {
  history: ChatController["history"];
  draftMission?: string;
  pendingApprovals: number;
  activeRuns: number;
  evidenceCount: number;
  dataErrors: number;
  livePreflightBlockers?: LivePreflightBlockerSummary;
  commandRuns?: DashboardData["runs"];
  toolRuns?: DashboardData["toolRuns"];
  timeline?: DashboardData["timeline"];
}): AgentWorkspaceSnapshot {
  const lastUser = [...input.history].reverse().find((message) => message.role === "user");
  const lastAssistant = [...input.history].reverse().find((message) => message.role === "assistant");
  const draftMission = input.draftMission?.trim() ?? "";
  const runtimeRuns = input.commandRuns ?? [];
  const runtimeTools = input.toolRuns ?? [];
  const runtimeTimeline = input.timeline ?? [];
  const latestRuntimeRun = runtimeRuns[0] ?? null;
  const latestTimelineEvent = runtimeTimeline[0] ?? null;
  const latestRunId = latestRuntimeRun?.id ?? [...input.history].reverse().find((message) => message.runId)?.runId ?? null;
  const lastToolNames = lastAssistant?.meta?.usedToolNames?.slice(0, 3) ?? [];
  const toolCalls = lastAssistant?.meta?.toolCalls ?? 0;
  const assistantText = lastAssistant?.content ?? "";
  const latestToolNames = runtimeTools.slice(0, 3).map((tool) => tool.toolId);
  const failedTool = runtimeTools.find((tool) => /error|failed/i.test(tool.status));
  const blockedTool = runtimeTools.find((tool) => /blocked|pending/i.test(tool.status));
  const timelineText = runtimeTimeline.map((item) => `${item.type} ${item.actionId ?? ""} ${item.summary} ${item.status ?? ""}`).join("\n");
  const hasRuntimeToolEvidence = runtimeTools.length > 0;
  const hasToolEvidence = hasRuntimeToolEvidence || lastToolNames.length > 0 || toolCalls > 0;
  const hasDiffSignal =
    textHas(timelineText, [/diff/i, /patch/i, /worktree/i, /commit/i]) ||
    textHas(assistantText, [/diff/i, /patch/i, /datei/i, /file/i, /commit/i, /pull request/i, /\bpr\b/i]);
  const hasTestSignal =
    runtimeTools.some((tool) => /test|typecheck|lint|smoke|gate|check/i.test(tool.toolId)) ||
    textHas(timelineText, [/test/i, /typecheck/i, /smoke/i, /check/i, /\bpass\b/i, /bestanden/i]) ||
    textHas(assistantText, [/test/i, /typecheck/i, /gate/i, /smoke/i, /\bpass\b/i, /bestanden/i]);
  const runtimeToolSummary =
    latestToolNames.length > 0
      ? `Tool-Runs: ${latestToolNames.join(", ")}`
      : runtimeTools.length > 0
        ? `${runtimeTools.length} Tool-Runs`
        : null;
  const toolSummary =
    runtimeToolSummary ??
    (lastToolNames.length > 0
      ? `Tools: ${lastToolNames.join(", ")}`
      : lastAssistant?.meta?.toolCalls
        ? `${lastAssistant.meta.toolCalls} Tool-Calls`
        : "Tools erscheinen im Runstream");
  const runDetail = latestRuntimeRun
    ? `${latestRuntimeRun.status}: ${latestRuntimeRun.phase}`
    : input.activeRuns > 0
      ? `${input.activeRuns} Run aktiv`
      : latestRunId
        ? `Run ${latestRunId}`
        : "Plan bereit nach Auftrag";
  const runstream = [
    lastUser ? "Prompt aufgenommen" : draftMission ? "Mission Entwurf bereit" : "Prompt wartet",
    runDetail,
    toolSummary,
  ];
  const lifecycle: AgentWorkspaceStage[] = [
    {
      id: "prompt",
      label: "Prompt",
      status: lastUser ? "done" : "waiting",
      detail: lastUser ? "aufgenommen" : draftMission ? "Entwurf bereit" : "wartet",
    },
    {
      id: "plan",
      label: "Plan",
      status: lastAssistant?.pending ? "active" : lastAssistant ? "done" : "waiting",
      detail: lastAssistant?.pending ? "entsteht" : lastAssistant ? "sichtbar" : "nach Auftrag",
    },
    {
      id: "tools",
      label: "Tools",
      status: failedTool ? "blocked" : blockedTool ? "active" : hasToolEvidence ? "done" : input.activeRuns > 0 ? "active" : "waiting",
      detail: failedTool
        ? `${failedTool.toolId}: ${failedTool.status}`
        : blockedTool
          ? `${blockedTool.toolId}: ${blockedTool.status}`
          : hasRuntimeToolEvidence
            ? `${runtimeTools.length} echte Tool-Runs`
            : hasToolEvidence
              ? toolSummary
              : input.activeRuns > 0
                ? "Runstream aktiv"
                : "noch nicht gestartet",
    },
    {
      id: "diff",
      label: "Diff",
      status: hasDiffSignal ? "done" : input.activeRuns > 0 ? "active" : "waiting",
      detail: hasDiffSignal ? "Runtime-/Chat-Signal" : input.activeRuns > 0 ? "wartet auf Ausgabe" : "noch offen",
    },
    {
      id: "tests",
      label: "Tests",
      status: failedTool ? "blocked" : hasTestSignal ? "done" : input.activeRuns > 0 ? "active" : "waiting",
      detail: failedTool ? "Tool-Fehler klaeren" : hasTestSignal ? "Runtime-/Tool-Signal" : input.activeRuns > 0 ? "wartet auf Gate" : "noch offen",
    },
    {
      id: "gate",
      label: "Gate",
      status: input.dataErrors > 0 ? "blocked" : input.pendingApprovals > 0 ? "approval" : input.evidenceCount > 0 ? "done" : "waiting",
      detail:
        input.dataErrors > 0
          ? `${input.dataErrors} Blocker`
          : input.pendingApprovals > 0
            ? `${input.pendingApprovals} Owner-Entscheidung`
            : input.evidenceCount > 0
              ? "keine Entscheidung offen"
              : "bereit",
    },
    {
      id: "evidence",
      label: "Evidence",
      status: input.evidenceCount > 0 ? "done" : input.activeRuns > 0 ? "active" : "waiting",
      detail: input.evidenceCount > 0 ? `${input.evidenceCount} Nachweise` : input.activeRuns > 0 ? "entsteht" : "noch offen",
    },
  ];
  const decision = deriveAgentWorkspaceDecision({
    hasPrompt: Boolean(lastUser?.content.trim()),
    hasAssistant: Boolean(lastAssistant),
    pendingAssistant: Boolean(lastAssistant?.pending),
    pendingApprovals: input.pendingApprovals,
    activeRuns: input.activeRuns,
    evidenceCount: input.evidenceCount,
    dataErrors: input.dataErrors,
    hasDiffSignal,
    hasTestSignal,
  });
  const progressCue = deriveAgentWorkspaceProgressCue({
    pendingApprovals: input.pendingApprovals,
    activeRuns: input.activeRuns,
    dataErrors: input.dataErrors,
    livePreflightBlockers: input.livePreflightBlockers,
    latestRuntimeRun,
    latestTimelineEvent,
    pendingAssistant: Boolean(lastAssistant?.pending),
    hasPrompt: Boolean(lastUser?.content.trim()),
  });
  const outcome = deriveAgentWorkspaceOutcome({
    hasPrompt: Boolean(lastUser?.content.trim()),
    dataErrors: input.dataErrors,
    livePreflightBlockers: input.livePreflightBlockers,
    pendingApprovals: input.pendingApprovals,
    activeRuns: input.activeRuns,
    evidenceCount: input.evidenceCount,
    failedTool,
    latestRuntimeRun,
    hasTestSignal,
    hasDiffSignal,
  });
  const transcript = deriveAgentWorkspaceTranscript({
    history: input.history,
    draftMission,
    commandRuns: runtimeRuns,
    toolRuns: runtimeTools,
    timeline: runtimeTimeline,
    pendingApprovals: input.pendingApprovals,
  });
  const planBrief = deriveAgentWorkspacePlanBrief({
    lastUserContent: lastUser?.content ?? null,
    draftMission,
    lastAssistantContent: lastAssistant?.content ?? null,
    latestRuntimeRun,
    latestTimelineEvent,
    pendingApprovals: input.pendingApprovals,
    activeRuns: input.activeRuns,
    dataErrors: input.dataErrors,
    livePreflightBlockers: input.livePreflightBlockers,
    evidenceCount: input.evidenceCount,
    hasDiffSignal,
    hasTestSignal,
    failedTool,
    decision,
    outcome,
  });
  const draftReply = deriveAgentWorkspaceDraftReply({
    draftMission,
    hasUserPrompt: Boolean(lastUser?.content.trim()),
    hasAssistant: Boolean(lastAssistant),
    dataErrors: input.dataErrors,
    livePreflightBlockers: input.livePreflightBlockers,
    pendingApprovals: input.pendingApprovals,
    activeRuns: input.activeRuns,
  });
  const focus = deriveAgentWorkspaceFocus({
    decision,
    progressCue,
    outcome,
    planBrief,
    transcript,
  });
  return {
    prompt: lastUser?.content.trim() || draftMission || "Frag SIMO oder sprich eine Mission ein.",
    response: lastAssistant?.pending ? "SIMO denkt..." : lastAssistant?.content.trim() || draftReply?.headline || "Antwort, Plan und naechster Schritt erscheinen hier im Arbeitsfluss.",
    draftReply,
    runstream,
    transcript,
    planBrief,
    focus,
    lifecycle,
    decision,
    progressCue,
    outcome,
    gate: input.pendingApprovals > 0 ? `${input.pendingApprovals} Owner-Entscheidung offen` : "Keine Owner-Entscheidung offen",
    evidence: input.evidenceCount > 0 ? `${input.evidenceCount} Evidence-Nachweise` : "Evidence entsteht mit Runs und Gates",
    active: input.history.length > 0 || Boolean(draftMission) || input.activeRuns > 0 || input.pendingApprovals > 0 || input.dataErrors > 0,
  };
}

export function deriveMissionComposerFocus(input: {
  draft: string;
  hasChatController: boolean;
  voiceMode: ChatController["voiceMode"] | "off";
  voiceAvailable: boolean;
  voiceState: ChatController["voiceOwnerUx"]["pttState"] | "unavailable";
  dataErrors: number;
  pendingApprovals: number;
  activeRuns: number;
  connectorDecision: OwnerConnectorMissionDecision;
  voiceDecision: OwnerVoiceMissionDecision;
  workspaceFocus: AgentWorkspaceFocus;
}): MissionComposerFocus {
  const draft = input.draft.trim();
  const voiceReady = input.voiceMode !== "off" && input.voiceAvailable && input.voiceState === "ready";
  const voiceListening = input.voiceMode !== "off" && input.voiceState === "listening";
  const voiceEvidence = input.voiceMode === "off"
    ? "Voice aus"
    : input.voiceAvailable
      ? `Voice ${input.voiceState}`
      : "Voice nicht verfuegbar";
  const connectorEvidence = input.connectorDecision.evidence;
  const draftEvidence = draft ? `Entwurf: ${trimTranscriptDetail(draft, 120)}` : "Mission fehlt";
  const evidence = `Composer: ${draftEvidence} · ${voiceEvidence} · ${connectorEvidence}`;

  if (!input.hasChatController) {
    return {
      label: "Composer nicht verbunden",
      detail: "Der zentrale Chat-/Voice-Controller ist in dieser Ansicht nicht verbunden; SIMO zeigt nur Workspace-Signale.",
      primaryAction: "Mission im Composer oeffnen",
      safeAction: input.workspaceFocus.nextAction,
      ownerPath: null,
      evidence,
      tone: "blocked",
    };
  }

  if (input.dataErrors > 0) {
    return {
      label: draft ? "Mission parken" : "Erst Live-Daten entsperren",
      detail: draft
        ? "Die Mission ist notiert, aber Live-Daten-Blocker muessen vor einem belastbaren Agent-Start geklaert werden."
        : "SIMO braucht zuerst Owner-Session, Reauth oder sicheren Retry fuer blockierte Live-Reads.",
      primaryAction: "Live-Daten entsperren",
      safeAction: draft ? "Mission als Entwurf halten" : input.workspaceFocus.nextAction,
      ownerPath: "Owner-Gate > Live-Daten entsperren",
      evidence,
      tone: "blocked",
    };
  }

  if (input.pendingApprovals > 0) {
    return {
      label: draft ? "Mission nach Gate starten" : "Owner-Gate zuerst",
      detail: `${input.pendingApprovals} Owner-Entscheidung offen; SIMO darf keine neue Ausfuehrung ueber das Gate hinweg starten.`,
      primaryAction: "Owner-Gate entscheiden",
      safeAction: draft ? "Mission nach Freigabe starten" : input.workspaceFocus.nextAction,
      ownerPath: "Approval Inbox > Owner-Gate entscheiden",
      evidence,
      tone: "approval",
    };
  }

  if (input.activeRuns > 0) {
    return {
      label: draft ? "Mission als Ergaenzung" : "Run beobachten",
      detail: draft
        ? `${input.activeRuns} Run aktiv; die neue Mission wird als Chat-Ergaenzung gefuehrt, nicht als verdeckter Parallel-Start.`
        : `${input.activeRuns} Run aktiv; SIMO verdichtet Fortschritt, Tools und Gates im Workspace.`,
      primaryAction: draft ? "Mission ergaenzen" : "Runstream beobachten",
      safeAction: input.workspaceFocus.nextAction,
      ownerPath: null,
      evidence,
      tone: "working",
    };
  }

  if (draft) {
    const connectorRestricted = input.connectorDecision.tone !== "ready";
    return {
      label: connectorRestricted ? "Mission mit Connector-Grenze" : "Mission startklar",
      detail: connectorRestricted
        ? `SIMO kann die Mission nur mit sicheren Daten starten oder Reauth ueber ${input.connectorDecision.ownerPath} vorbereiten.`
        : "SIMO kann aus deiner Eingabe Plan, Runtime-Spur, Gates und Evidence vorbereiten.",
      primaryAction: connectorRestricted ? input.connectorDecision.safeAction : "Als Agent-Run starten",
      safeAction: input.voiceDecision.safeAction,
      ownerPath: connectorRestricted ? input.connectorDecision.ownerPath : null,
      evidence,
      tone: connectorRestricted ? "approval" : "ready",
    };
  }

  if (voiceListening) {
    return {
      label: "Sprich die Mission",
      detail: "SIMO hoert zu; formuliere Ziel, System und gewuenschtes Ergebnis.",
      primaryAction: "Mission sprechen",
      safeAction: "Text-Mission schreiben",
      ownerPath: null,
      evidence,
      tone: "working",
    };
  }

  return {
    label: voiceReady ? "Mission per Voice oder Text" : "Mission formulieren",
    detail: voiceReady
      ? "Der Composer ist bereit: sprich oder schreibe den nächsten Auftrag, SIMO schaltet danach in Plan und Gates."
      : "Schreibe die Mission; Voice bleibt optional und blockiert Text nicht.",
    primaryAction: voiceReady ? "Mission sprechen oder schreiben" : "Text-Mission schreiben",
    safeAction: input.voiceDecision.primaryAction,
    ownerPath: null,
    evidence,
    tone: "ready",
  };
}

export function OwnerCurrentSurface({
  data,
  activeRuns,
  pendingApprovals,
  scene,
}: {
  data: DashboardData;
  activeRuns: number;
  pendingApprovals: number;
  scene: CosmicSceneSnapshot;
}): JSX.Element {
  const connectors = data.connectors.filter((connector) => connector.reachable).length;
  const totalConnectors = data.connectors.length;
  const ownerState = data.ownerAuthStatus?.ownerAuth.configured ? "Owner Auth bereit" : "Owner Auth offen";
  const livePreflightBlockers = deriveLivePreflightBlockers(data.errors);
  const ownerPrompt = deriveOwnerDecisionPrompt({
    dataErrors: Object.keys(data.errors).length,
    pendingApprovals,
    activeRuns,
    connectorReachable: connectors,
    connectorTotal: totalConnectors,
    evidenceCount: data.gatedEvidence.length,
    livePreflightBlockers,
  });
  const nextAction =
    pendingApprovals > 0
      ? "Freigaben pruefen"
      : data.errors && Object.keys(data.errors).length > 0
        ? "Blockierte Live-Daten klaeren"
        : activeRuns > 0
          ? "Aktive Runs beobachten"
          : "Naechste Aufgabe starten";

  return (
    <header className="simo-owner-current" aria-label="Aktueller SIMO Owner Stand">
      <div className="min-w-0">
        <div className="simo-owner-current-kicker">SIMO heute</div>
        <h1 className="simo-owner-current-title">Deine SIMO-Zentrale</h1>
        <p className="simo-owner-current-copy">
          Erst der naechste sinnvolle Schritt, dann Details. Web ist die Zentrale, Companion bleibt dein
          Tablet-Begleiter, SimoT die Desktop-Arbeitsflaeche.
        </p>
      </div>

      <div className="simo-owner-current-grid" aria-label="Produktrollen">
        <div className="simo-owner-current-card">
          <strong>SIMO Web</strong>
          <span>Mission, Gates, Evidence und Owner-Auth an einem Ort.</span>
        </div>
        <div className="simo-owner-current-card">
          <strong>Companion</strong>
          <span>Tablet-Flow fuer Inbox, Brain, System und schnelle Owner-Pruefung.</span>
        </div>
        <div className="simo-owner-current-card">
          <strong>SimoT</strong>
          <span>Desktop-Workspace fuer Code-, Ops- und Mission-Flows.</span>
        </div>
      </div>

      <div className="simo-owner-current-rail" aria-label="Live Kennzahlen">
        <div className="simo-owner-current-stat" data-tone={ownerPrompt.tone}>
          <span>Owner Frage</span>
          <strong>{ownerPrompt.action}</strong>
          <small>{ownerPrompt.question}</small>
        </div>
        <div className="simo-owner-current-stat">
          <span>Naechster Schritt</span>
          <strong>{nextAction}</strong>
        </div>
        <div className="simo-owner-current-stat">
          <span>Status</span>
          <strong>{scene.livingLabel}</strong>
        </div>
        <div className="simo-owner-current-stat">
          <span>Approvals</span>
          <strong>{pendingApprovals}</strong>
        </div>
        <div className="simo-owner-current-stat">
          <span>Connectoren</span>
          <strong>{connectors}/{totalConnectors}</strong>
        </div>
        <div className="simo-owner-current-stat">
          <span>Runs aktiv</span>
          <strong>{activeRuns}</strong>
        </div>
        <div className="simo-owner-current-stat">
          <span>{ownerState}</span>
          <strong>{data.gatedEvidence.length} Evidence</strong>
        </div>
      </div>
    </header>
  );
}

export function OwnerOperationsCanvas({
  chat,
  data,
  activeRuns,
  pendingApprovals,
  scene,
}: {
  chat?: ChatController;
  data: DashboardData;
  activeRuns: number;
  pendingApprovals: number;
  scene: CosmicSceneSnapshot;
}): JSX.Element {
  const dataErrors = Object.entries(data.errors);
  const livePreflightBlockers = deriveLivePreflightBlockers(data.errors);
  // Slice 3 — kanonisch: abgeschlossene Mission-Runs (control_plane_runs),
  // nicht "Chat-Konversation beendet".
  const completedRuns = data.controlPlaneSummary?.semantics?.runs.byStatus.find((s) => s.status === "completed")?.count ?? 0;
  const connectorCount = data.connectors.filter((connector) => connector.reachable).length;
  const totalConnectors = data.connectors.length;
  const nextAction =
    pendingApprovals > 0
      ? "Owner-Freigaben entscheiden"
      : dataErrors.length > 0
        ? "Live-Daten entsperren"
        : activeRuns > 0
          ? "Aktive Runs beobachten"
          : "Naechste Aufgabe starten";
  const ownerPrompt = deriveOwnerDecisionPrompt({
    dataErrors: dataErrors.length,
    pendingApprovals,
    activeRuns,
    connectorReachable: connectorCount,
    connectorTotal: totalConnectors,
    evidenceCount: data.gatedEvidence.length,
    livePreflightBlockers,
  });
  const focusCards = [
    { label: "Naechster Schritt", value: nextAction, tone: dataErrors.length > 0 ? "warn" : pendingApprovals > 0 ? "approval" : "ok" },
    { label: "Runs", value: `${activeRuns}/${data.runs.length}`, tone: activeRuns > 0 ? "info" : "neutral" },
    { label: "Evidence", value: String(data.gatedEvidence.length), tone: data.gatedEvidence.length > 0 ? "ok" : "neutral" },
    { label: "Connectoren", value: `${connectorCount}/${totalConnectors}`, tone: connectorCount === totalConnectors ? "ok" : "warn" },
  ] as const;
  const workspace = deriveAgentWorkspaceSnapshot({
    history: chat?.history ?? [],
    draftMission: chat?.input ?? "",
    pendingApprovals,
    activeRuns,
    evidenceCount: data.gatedEvidence.length,
    dataErrors: dataErrors.length,
    livePreflightBlockers,
    commandRuns: data.runs,
    toolRuns: data.toolRuns,
    timeline: data.timeline,
  });
  const unlockItems = deriveLiveDataUnlockItems(data.errors);
  const unlockProof = deriveLiveDataUnlockProof({
    hasSessionToken: data.hasSessionToken,
    ownerAuthStatus: data.ownerAuthStatus,
    ownerAuthError: data.ownerAuthError,
    blockedReads: unlockItems.length,
  });
  const unlockDecision = deriveLiveDataUnlockDecision({ proof: unlockProof, items: unlockItems });
  const connectorDecision = deriveOwnerConnectorMissionDecision(data.connectors);
  const selectedVoice = chat?.voiceOptions?.find((option) => option.id === chat.ttsVoice);
  const voiceDecision = deriveOwnerVoiceMissionDecision({
    selected: selectedVoice,
    elevenConfigured: chat?.elevenConfigured ?? false,
    voiceMode: chat?.voiceMode ?? "off",
    voiceAvailable: chat?.voice?.available ?? false,
    recording: chat?.voice?.recording ?? false,
    processing: chat?.voice?.processing ?? false,
    speaking: chat?.voice?.speaking ?? false,
    ttsTestStatus: chat?.ttsTestStatus ?? { state: "ready", label: "Kein Voice-Controller", detail: "Voice-Controller ist in diesem Render nicht verbunden." },
  });
  const composerFocus = deriveMissionComposerFocus({
    draft: chat?.input ?? "",
    hasChatController: Boolean(chat),
    voiceMode: chat?.voiceMode ?? "off",
    voiceAvailable: chat?.voice?.available ?? false,
    voiceState: chat?.voiceOwnerUx?.pttState ?? "unavailable",
    dataErrors: dataErrors.length,
    pendingApprovals,
    activeRuns,
    connectorDecision,
    voiceDecision,
    workspaceFocus: workspace.focus,
  });

  return (
    <section className="simo-owner-workbench" aria-label="SIMO Owner Arbeitsflaeche">
      <div className="simo-owner-workbench-hero">
        <div className="simo-owner-workbench-copy">
          <span className="simo-owner-current-kicker">Mission jetzt</span>
          <h2>Arbeitsstand und naechste Entscheidung</h2>
          <p>
            SIMO fuehrt dich direkt zu Blockern, Freigaben, Runs und Evidence. Keine alte Kontrollwand,
            sondern ein klarer Arbeitsfluss fuer die naechste Owner-Entscheidung.
          </p>
        </div>
        <div className="simo-owner-next-card" data-tone={dataErrors.length > 0 ? "warn" : "ok"}>
          <span>Naechster Schritt</span>
          <strong>{nextAction}</strong>
          <small>{scene.livingPrompt}</small>
          <em>{scene.ownerCue}</em>
        </div>
      </div>
      <div className="simo-owner-decision-question" data-tone={ownerPrompt.tone}>
        <span>Owner Frage</span>
        <strong>{ownerPrompt.question}</strong>
        <small>{ownerPrompt.reason}</small>
      </div>
      <div className="simo-owner-decision-question" data-tone={composerFocus.tone} aria-label="Mission Composer Focus">
        <span>Mission Composer</span>
        <strong>{composerFocus.label}</strong>
        <small>{composerFocus.detail}</small>
        {composerFocus.ownerPath && <small>Owner-Pfad: {composerFocus.ownerPath}</small>}
        <small>{composerFocus.evidence}</small>
        <span className="simo-owner-blocker-actions" aria-label="Mission Composer Optionen">
          <b>{composerFocus.primaryAction}</b>
          <b>{composerFocus.safeAction}</b>
        </span>
      </div>
      <div className="simo-owner-decision-question" data-tone={connectorDecision.tone} aria-label="Connector Mission Decision">
        <span>Connector Decision</span>
        <strong>{connectorDecision.label}</strong>
        <small>{connectorDecision.detail}</small>
        <small>Owner-Pfad: {connectorDecision.ownerPath}</small>
        <small>{connectorDecision.risk}</small>
        <small>{connectorDecision.evidence}</small>
        <span className="simo-owner-blocker-actions" aria-label="Connector Optionen">
          <b>{connectorDecision.primaryAction}</b>
          <b>{connectorDecision.safeAction}</b>
        </span>
      </div>
      <div className="simo-owner-decision-question" data-tone={voiceDecision.tone} aria-label="Voice Mission Decision">
        <span>Voice Decision</span>
        <strong>{voiceDecision.label}</strong>
        <small>{voiceDecision.detail}</small>
        <small>Gewaehlt: {voiceDecision.selected} · Test spielt: {voiceDecision.playback}</small>
        <small>{voiceDecision.evidence}</small>
        <span className="simo-owner-blocker-actions" aria-label="Voice Optionen">
          <b>{voiceDecision.primaryAction}</b>
          <b>{voiceDecision.safeAction}</b>
        </span>
      </div>

      <div className="simo-agent-workspace-flow" aria-label="Agent Workspace Flow">
        <div className="simo-agent-workspace-head" data-tone={workspace.focus.tone} aria-label="Agent Workspace Focus">
          <span className="simo-owner-current-kicker">Agent Workspace</span>
          <strong>{"Prompt -> Plan -> Runstream -> Gate -> Evidence"}</strong>
          <small>{workspace.focus.label}</small>
          <small>{workspace.focus.primary}</small>
          <small>{workspace.focus.nextAction}</small>
        </div>
        <div className="simo-agent-workspace-decision" data-tone={workspace.decision.tone}>
          <span>Jetzt wichtig</span>
          <strong>{workspace.decision.label}</strong>
          <p>{workspace.decision.detail}</p>
          <small>{workspace.decision.primaryAction}</small>
        </div>
        <div className="simo-agent-workspace-progress" data-tone={workspace.progressCue.tone}>
          <span>Live Fortschritt</span>
          <strong>{workspace.progressCue.label}</strong>
          <p>{workspace.progressCue.detail}</p>
        </div>
        <div className="simo-agent-workspace-progress" data-tone={workspace.outcome.tone}>
          <span>Ergebnis</span>
          <strong>{workspace.outcome.label}</strong>
          <p>{workspace.outcome.detail}</p>
          <small>{workspace.outcome.evidence}</small>
        </div>
        {workspace.draftReply && (
          <div className="simo-agent-workspace-progress" data-tone={workspace.draftReply.tone} aria-label="SIMO Draft Understanding">
            <span>SIMO verstanden</span>
            <strong>{workspace.draftReply.headline}</strong>
            <p>{workspace.draftReply.understanding}</p>
            <small>{workspace.draftReply.nextAction}</small>
            <small>{workspace.draftReply.guardrail}</small>
            <small>{workspace.draftReply.evidence}</small>
            <span className="simo-owner-blocker-actions" aria-label="Draft Preflight Checkliste">
              {workspace.draftReply.checklist.map((item) => (
                <b key={item.label} data-status={item.status}>{item.label}: {item.detail}</b>
              ))}
            </span>
          </div>
        )}
        <div className="simo-agent-workspace-progress" data-tone={workspace.planBrief.tone} aria-label="Codex-like Plan Brief">
          <span>Planlage</span>
          <strong>{workspace.planBrief.objective}</strong>
          <p>{workspace.planBrief.currentStep}</p>
          <small>{workspace.planBrief.nextStep}</small>
          <small>{workspace.planBrief.guardrail}</small>
          <small>{workspace.planBrief.evidence}</small>
        </div>
        <div className="simo-agent-lifecycle" aria-label="Codex-like Agent Lifecycle">
          {workspace.lifecycle.map((stage) => (
            <div key={stage.id} className="simo-agent-lifecycle-stage" data-status={stage.status}>
              <span>{stage.label}</span>
              <strong>{stage.detail}</strong>
            </div>
          ))}
        </div>
        <div className="simo-agent-transcript" aria-label="Codex-like Run Transcript">
          {workspace.transcript.map((item) => (
            <div key={item.id} className="simo-agent-transcript-row" data-source={item.source} data-tone={item.tone}>
              <span>{item.source}</span>
              <strong>{item.title}</strong>
              <small>{item.detail}</small>
            </div>
          ))}
        </div>
        <div className="simo-agent-workspace-grid">
          <div className="simo-agent-workspace-cell" data-active={workspace.active ? "true" : "false"}>
            <span>Prompt</span>
            <strong>{workspace.prompt}</strong>
          </div>
          <div className="simo-agent-workspace-cell">
            <span>Antwort / Plan</span>
            <strong>{workspace.response}</strong>
          </div>
          <div className="simo-agent-workspace-cell">
            <span>Runstream</span>
            <div className="simo-agent-runstream-list">
              {workspace.runstream.map((item) => (
                <small key={item}>{item}</small>
              ))}
            </div>
          </div>
          <div className="simo-agent-workspace-cell">
            <span>Gate / Evidence</span>
            <strong>{workspace.gate}</strong>
            <small>{workspace.evidence}</small>
          </div>
        </div>
      </div>

      {dataErrors.length > 0 && (
        <div className="simo-owner-blocker-panel" role="status" aria-label="Live-Daten teilweise blockiert">
          <div>
            <span className="simo-owner-current-kicker">Owner Unlock</span>
            <h3>Live-Daten gezielt entsperren</h3>
            <p>
              SIMO zeigt Endpoint, geschuetzte Daten, erwarteten Header und naechste Aktion. Kein rohes 401-Raten,
              kein Owner-Secret im Browser.
            </p>
          </div>
          <button type="button" onClick={data.refresh}>Neu laden</button>
          <div className="simo-owner-blocker-list" aria-label="Live-Daten Auth Proof">
            <div className="simo-owner-blocker-row">
              <strong>Auth Proof</strong>
              <span>
                <small>{unlockProof.session}</small>
                <small>{unlockProof.ownerAuth}</small>
                <small>{unlockProof.ownerKey}</small>
                <small>{unlockProof.blockedReads}</small>
                <small>{unlockProof.nextProbe}</small>
              </span>
            </div>
            <div className="simo-owner-blocker-row" data-tone={unlockDecision.tone}>
              <strong>Unlock Decision</strong>
              <span>
                <b>{unlockDecision.label}</b>
                <small>{unlockDecision.detail}</small>
                <small>{unlockDecision.evidence}</small>
                <span className="simo-owner-blocker-actions" aria-label="Live-Daten Unlock Optionen">
                  <b>{unlockDecision.primaryAction}</b>
                  <b>{unlockDecision.safeAction}</b>
                </span>
              </span>
            </div>
          </div>
          <div className="simo-owner-blocker-list">
            {unlockItems.slice(0, 4).map((item) => (
              <div key={item.key} className="simo-owner-blocker-row">
                <strong>{item.protectedData}</strong>
                <span>
                  <b>{item.endpoint}</b>
                  <em>{item.authModel}</em>
                  <small>{item.sentHeader}</small>
                  <small>{item.forbiddenHeader}</small>
                  <small>{item.ownerGateFlow}</small>
                  <small>{item.backendExpectation}</small>
                  <small>{item.statusJudgement}</small>
                  <small>{item.cause}</small>
                  <small>{item.action}</small>
                  <span className="simo-owner-blocker-actions" aria-label={`${item.protectedData} Owner Optionen`}>
                    <b>{item.primaryCta}</b>
                    <b>{item.safeCta}</b>
                    <b>{item.detailsCta}</b>
                  </span>
                  <code>{item.message}</code>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="simo-owner-focus-grid" aria-label="Owner Kennzahlen">
        {focusCards.map((card) => (
          <div key={card.label} className="simo-owner-focus-card" data-tone={card.tone}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </div>
        ))}
      </div>

      <div className="simo-owner-role-board" aria-label="Produktrollen">
        <div>
          <span>SIMO</span>
          <strong>Web-Zentrale</strong>
          <p>Mission, Gates, Evidence und Owner-Auth laufen hier zusammen.</p>
        </div>
        <div>
          <span>Companion</span>
          <strong>Tablet-Begleiter</strong>
          <p>Schnelle Owner-Pruefung fuer Inbox, Brain, System und Voice.</p>
        </div>
        <div>
          <span>SimoT</span>
          <strong>Desktop-Workspace</strong>
          <p>Workspace fuer Code-, Ops- und Mission-Flows mit Evidence.</p>
        </div>
      </div>

      <div className="simo-owner-run-board" aria-label="Run und Evidence Board">
        <div>
          <span className="simo-owner-current-kicker">Runstream</span>
          <strong>{completedRuns} abgeschlossen</strong>
          <p>{activeRuns} aktiv · {pendingApprovals} Freigaben · Status {scene.livingLabel}</p>
        </div>
        <div>
          <span className="simo-owner-current-kicker">Evidence</span>
          <strong>{data.gatedEvidence.length} Nachweise</strong>
          <p>Audit, Ledger und Approval-Historie bleiben sichtbar neben der Arbeitsflaeche.</p>
        </div>
      </div>
    </section>
  );
}

function OwnerPriorityPanel({
  data,
  activeRuns,
  pendingApprovals,
}: {
  data: DashboardData;
  activeRuns: number;
  pendingApprovals: number;
}): JSX.Element {
  const inbox = data.approvalInbox?.items ?? [];
  const learning = data.learningProposals.length;
  const blockers = Object.keys(data.errors);
  const priorities = [
    blockers.length > 0
      ? { title: "Live-Daten klaeren", meta: `${blockers.length} blockiert`, tone: "warn" }
      : null,
    pendingApprovals > 0
      ? { title: "Freigaben entscheiden", meta: `${pendingApprovals} offen`, tone: "approval" }
      : null,
    inbox.length > 0
      ? { title: "Inbox pruefen", meta: `${inbox.length} Vorschlaege`, tone: "info" }
      : null,
    activeRuns > 0
      ? { title: "Runs beobachten", meta: `${activeRuns} aktiv`, tone: "info" }
      : null,
    learning > 0
      ? { title: "Brain Review", meta: `${learning} Vorschlaege`, tone: "neutral" }
      : null,
  ].filter((item): item is { title: string; meta: string; tone: string } => item !== null);

  return (
    <aside className="simo-owner-side-panel" aria-label="Prioritaeten">
      <div className="simo-owner-side-head">
        <span>Heute wichtig</span>
        <strong>{priorities.length}</strong>
      </div>
      <div className="simo-owner-priority-list">
        {(priorities.length > 0 ? priorities : [{ title: "Bereit fuer Auftrag", meta: "keine Blocker", tone: "ok" }]).map((item) => (
          <div key={`${item.title}-${item.meta}`} className="simo-owner-priority-item" data-tone={item.tone}>
            <span>{item.title}</span>
            <strong>{item.meta}</strong>
          </div>
        ))}
      </div>
    </aside>
  );
}

function OwnerEvidencePanel({ data }: { data: DashboardData }): JSX.Element {
  const latestEvidence = data.gatedEvidence.slice(0, 4);
  return (
    <aside className="simo-owner-side-panel" aria-label="Evidence Kurzblick">
      <div className="simo-owner-side-head">
        <span>Evidence</span>
        <strong>{data.gatedEvidence.length}</strong>
      </div>
      <div className="simo-owner-evidence-list">
        {latestEvidence.length > 0 ? latestEvidence.map((item) => (
          <div key={item.id} className="simo-owner-evidence-item">
            <span>{item.actionId}</span>
            <strong>{item.resultStatus}</strong>
          </div>
        )) : (
          <div className="simo-owner-evidence-empty">Noch keine Evidence im aktuellen Blick.</div>
        )}
      </div>
    </aside>
  );
}

export function FullControlSurfacePanel({ data }: { data: DashboardData }): JSX.Element {
  const rows = deriveFullControlSurfaceRows(data);
  const editabilityRows = deriveFullControlEditabilityRows(data);
  const truthRows = deriveTruthRoadmapControlRows(data);
  const ready = rows.filter((row) => row.tone === "go").length;
  const editable = editabilityRows.filter((row) => row.tone === "editable").length;
  const activeTruthRows = truthRows.filter((row) => row.tone === "active").length;
  return (
    <aside className="simo-owner-side-panel" aria-label="Full Control Surface">
      <div className="simo-owner-side-head">
        <span>Full Control</span>
        <strong>{ready}/{rows.length}</strong>
      </div>
      <div className="grid gap-2">
        {rows.map((row) => (
          <div
            key={row.id}
            className="min-w-0 rounded-lg border border-white/10 bg-black/[0.22] px-3 py-2"
            data-tone={row.tone}
          >
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="truncate text-[11px] font-semibold text-jarvis-text" title={row.label}>{row.label}</span>
              <span className="shrink-0 rounded-md border border-white/10 bg-white/[0.055] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-jarvis-muted">
                {row.status}
              </span>
            </div>
            <p className="mt-1 line-clamp-2 text-[10px] leading-relaxed text-jarvis-muted" title={row.detail}>{row.detail}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 border-t border-white/10 pt-3">
        <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
          <span className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-jarvis-muted">
            Editierbarkeit
          </span>
          <strong className="shrink-0 text-[10px] text-jarvis-text">{editable}/{editabilityRows.length}</strong>
        </div>
        <div className="grid gap-1.5">
          {editabilityRows.map((row) => (
            <div
              key={row.id}
              className="min-w-0 rounded-md border border-white/10 bg-white/[0.035] px-2.5 py-1.5"
              data-editability-tone={row.tone}
            >
              <div className="flex min-w-0 items-center justify-between gap-2">
                <span className="truncate text-[10px] font-semibold text-jarvis-text" title={row.label}>{row.label}</span>
                <span className="shrink-0 rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
                  {row.mode}
                </span>
              </div>
              <p className="mt-1 line-clamp-2 text-[9px] leading-relaxed text-jarvis-muted" title={row.detail}>{row.detail}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 border-t border-white/10 pt-3">
        <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
          <span className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-jarvis-muted">
            Truth / Roadmap
          </span>
          <strong className="shrink-0 text-[10px] text-jarvis-text">{activeTruthRows}/{truthRows.length}</strong>
        </div>
        <div className="grid gap-1.5">
          {truthRows.map((row) => (
            <div
              key={row.id}
              className="min-w-0 rounded-md border border-white/10 bg-white/[0.035] px-2.5 py-1.5"
              data-truth-tone={row.tone}
            >
              <div className="flex min-w-0 items-center justify-between gap-2">
                <span className="truncate text-[10px] font-semibold text-jarvis-text" title={row.label}>{row.label}</span>
                <span className="shrink-0 rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
                  {row.status}
                </span>
              </div>
              <div className="mt-0.5 truncate font-mono text-[8px] text-jarvis-muted/80" title={row.source}>{row.source}</div>
              <p className="mt-1 line-clamp-2 text-[9px] leading-relaxed text-jarvis-muted" title={row.detail}>{row.detail}</p>
            </div>
          ))}
        </div>
        <RoadmapWorkQueueDerivationForm onCreated={data.refresh} />
      </div>
      <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
        <span className="badge bg-jarvis-bg/60">Aenderungen policy-gated</span>
        <span className="badge bg-jarvis-bg/60">Truth per Diff</span>
        <span className="badge bg-jarvis-bg/60">Secrets verborgen</span>
        <span className="badge bg-jarvis-bg/60">Owner liest zuerst</span>
      </div>
    </aside>
  );
}

function RoadmapWorkQueueDerivationForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [title, setTitle] = useState("Roadmap-Ziel ableiten");
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canSubmit = title.trim().length > 0 && goal.trim().length > 0 && !busy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.createWorkQueueIdea({
        title: title.trim(),
        goal: [
          goal.trim(),
          "",
          "Quelle: docs/roadmaps/SIMO-SIMOT-SHARED-BRAIN-FINAL-ROADMAP-2026-07-10.md",
          "Gate: owner_review_before_execution",
          "Auto-Ausfuehrung: nein",
        ].join("\n"),
        productScope: "SIMO",
        priority: "normal",
        assignedAgent: "simo",
      });
      if (!result.ok) throw new Error("work_queue_create_failed");
      setGoal("");
      setNotice("Roadmap-Ziel liegt in der Work Queue und wartet auf Owner-Review.");
      onCreated();
    } catch (createError) {
      setError(workQueueControlErrorCopy(createError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor mt-2 rounded-md border border-white/10 bg-black/[0.18] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-jarvis-muted">Roadmap ableiten</span>
        <span className="rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
          Queue-only
        </span>
      </div>
      <input
        className="simo-keyboard-safe-field mt-2 min-h-10 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={title}
        maxLength={200}
        aria-label="Roadmap Work Queue Titel"
        placeholder="Roadmap-Ziel"
        onChange={(event) => setTitle(event.currentTarget.value)}
      />
      <textarea
        className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={goal}
        maxLength={4000}
        aria-label="Roadmap Work Queue Ziel"
        placeholder="Roadmap-Ziel, Kontext und gewuenschte Evidence"
        onChange={(event) => setGoal(event.currentTarget.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        className="mt-1 w-full rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1.5 text-[8px] uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
        onClick={() => void submit()}
      >
        Roadmap-Ziel in Work Queue legen
      </button>
      <p className="mt-1 text-[8px] leading-relaxed text-jarvis-muted">
        Erstellt nur ein Work-Queue-Item. Keine Auto-Ausfuehrung, kein Git, kein Deploy.
      </p>
      {notice && <div className="mt-1 break-words text-[9px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[9px] text-rose-200">{error}</div>}
    </div>
  );
}

function harnessControlProposalErrorCopy(error: unknown): string {
  const text = error instanceof Error ? error.message : String(error);
  if (/learning_proposal_rejected_by_secret_scan|secret/i.test(text)) {
    return "Harness-Vorschlag wurde blockiert, weil der Text wie ein Secret aussieht.";
  }
  if (/invalid_learning_proposal|policy|harness/i.test(text)) {
    return "Harness-Vorschlag braucht Aktion und Owner-Notiz.";
  }
  return ownerFacingGlobalError(text);
}

function HarnessControlProposalForm({ rows, onCreated }: { rows: HarnessControlRow[]; onCreated: () => void }): JSX.Element {
  const [action, setAction] = useState<HarnessProposalAction>("run_start");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canSubmit = note.trim().length > 0 && !busy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const draft = buildHarnessControlLearningProposal(action, note, rows);
      const result = await api.createLearningProposal(draft);
      if (!result.ok) throw new Error(result.error ?? "invalid_learning_proposal");
      setNote("");
      setNotice("Harness-Vorschlag liegt in der Learning Review Queue.");
      onCreated();
    } catch (proposalError) {
      setError(harnessControlProposalErrorCopy(proposalError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor mt-2 rounded-md border border-white/10 bg-black/[0.18] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-jarvis-muted">Harness Proposal</span>
        <span className="rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
          Review-only
        </span>
      </div>
      <select
        className="simo-keyboard-safe-field mt-2 min-h-10 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={action}
        aria-label="Harness Proposal Aktion"
        onChange={(event) => setAction(event.currentTarget.value as HarnessProposalAction)}
      >
        <option value="run_start">Run Start Schritte</option>
        <option value="run_end">Run End Pflichten</option>
        <option value="handoff">Handoff Template</option>
        <option value="brain_context">Brain Context Packet</option>
        <option value="supervisor_loop">Supervisor Loop</option>
      </select>
      <textarea
        className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={note}
        maxLength={5000}
        aria-label="Harness Proposal Notiz"
        placeholder="Gewuenschte Harness-Aenderung, Evidence-Pflicht oder Handoff-Regel beschreiben"
        onChange={(event) => setNote(event.currentTarget.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        className="mt-1 w-full rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1.5 text-[8px] uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
        onClick={() => void submit()}
      >
        Harness-Vorschlag in Review Queue legen
      </button>
      <p className="mt-1 text-[8px] leading-relaxed text-jarvis-muted">
        Speichert nur ein policy Learning Proposal. Kein Runner, keine Shell, kein Deploy, keine Connector-/Secret-Aktion.
      </p>
      {notice && <div className="mt-1 break-words text-[9px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[9px] text-rose-200">{error}</div>}
    </div>
  );
}

export function HarnessControlPanel({ data }: { data: DashboardData }): JSX.Element {
  const rows = deriveHarnessControlRows(data);
  const ready = rows.filter((row) => row.tone === "ready").length;
  const blocked = rows.filter((row) => row.tone === "blocked").length;
  return (
    <aside className="simo-owner-side-panel" aria-label="Harness Control Center">
      <div className="simo-owner-side-head">
        <span>Harness Control</span>
        <strong>{blocked > 0 ? "NO-GO" : `${ready}/${rows.length}`}</strong>
      </div>
      <div className="grid gap-1.5">
        {rows.map((row) => (
          <div
            key={row.id}
            className="min-w-0 rounded-md border border-white/10 bg-white/[0.035] px-2.5 py-1.5"
            data-harness-tone={row.tone}
          >
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="truncate text-[10px] font-semibold text-jarvis-text" title={row.label}>{row.label}</span>
              <span className="shrink-0 rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
                {row.status}
              </span>
            </div>
            <p className="mt-1 line-clamp-2 text-[9px] leading-relaxed text-jarvis-muted" title={row.detail}>{row.detail}</p>
            <div className="mt-1 truncate font-mono text-[8px] text-jarvis-muted/80" title={row.evidence}>{row.evidence}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
        <span className="badge bg-jarvis-bg/60">Brain Packet</span>
        <span className="badge bg-jarvis-bg/60">Run Ledger</span>
        <span className="badge bg-jarvis-bg/60">Handoff</span>
        <span className="badge bg-jarvis-bg/60">Loops proposal-only</span>
      </div>
      <HarnessControlProposalForm rows={rows} onCreated={data.refresh} />
    </aside>
  );
}

function brainControlProposalErrorCopy(error: unknown): string {
  const text = error instanceof Error ? error.message : String(error);
  if (/learning_proposal_rejected_by_secret_scan|secret/i.test(text)) {
    return "Brain-Control-Vorschlag wurde blockiert, weil der Text wie ein Secret aussieht.";
  }
  if (/invalid_learning_proposal|policy|brain/i.test(text)) {
    return "Brain-Control-Vorschlag braucht Aktion und Owner-Notiz.";
  }
  return ownerFacingGlobalError(text);
}

function BrainControlProposalForm({ rows, onCreated }: { rows: BrainControlRow[]; onCreated: () => void }): JSX.Element {
  const [action, setAction] = useState<BrainControlProposalAction>("review_rule");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canSubmit = note.trim().length > 0 && !busy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const draft = buildBrainControlLearningProposal(action, note, rows);
      const result = await api.createLearningProposal(draft);
      if (!result.ok) throw new Error(result.error ?? "invalid_learning_proposal");
      setNote("");
      setNotice("Brain-Control-Vorschlag liegt in der Learning Review Queue.");
      onCreated();
    } catch (proposalError) {
      setError(brainControlProposalErrorCopy(proposalError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor mt-2 rounded-md border border-white/10 bg-black/[0.18] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-jarvis-muted">Brain Policy Proposal</span>
        <span className="rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
          Review-only
        </span>
      </div>
      <select
        className="simo-keyboard-safe-field mt-2 min-h-10 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={action}
        aria-label="Brain Policy Proposal Aktion"
        onChange={(event) => setAction(event.currentTarget.value as BrainControlProposalAction)}
      >
        <option value="review_rule">Learning Review Regel</option>
        <option value="retention_policy">Retention / Privacy Policy</option>
        <option value="inject_policy">Auto-Inject Policy</option>
        <option value="scope_policy">Agent-/Project-Scope Policy</option>
        <option value="export_restore_policy">Export-/Restore-Policy</option>
      </select>
      <textarea
        className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={note}
        maxLength={5000}
        aria-label="Brain Policy Proposal Notiz"
        placeholder="Regel, Retention, Inject-Policy oder Scope-Aenderung als Review-Text beschreiben"
        onChange={(event) => setNote(event.currentTarget.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        className="mt-1 w-full rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1.5 text-[8px] uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
        onClick={() => void submit()}
      >
        Brain-Policy-Vorschlag in Review Queue legen
      </button>
      <p className="mt-1 text-[8px] leading-relaxed text-jarvis-muted">
        Speichert nur ein policy Learning Proposal. Kein Memory-Write, kein Raw-Value-Read, kein Restore-Apply, keine Connector-/Secret-Aktion.
      </p>
      {notice && <div className="mt-1 break-words text-[9px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[9px] text-rose-200">{error}</div>}
    </div>
  );
}

export function BrainControlCenterPanel({ data }: { data: DashboardData }): JSX.Element {
  const rows = deriveBrainControlRows(data);
  const ready = rows.filter((row) => row.tone === "ready").length;
  const review = rows.filter((row) => row.tone === "review").length;
  return (
    <aside className="simo-owner-side-panel" aria-label="Brain Control Center">
      <div className="simo-owner-side-head">
        <span>Brain Control</span>
        <strong>{review > 0 ? `${review} Review` : `${ready}/${rows.length}`}</strong>
      </div>
      <div className="grid gap-1.5">
        {rows.map((row) => (
          <div
            key={row.id}
            className="min-w-0 rounded-md border border-white/10 bg-white/[0.035] px-2.5 py-1.5"
            data-brain-tone={row.tone}
          >
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="truncate text-[10px] font-semibold text-jarvis-text" title={row.label}>{row.label}</span>
              <span className="shrink-0 rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
                {row.status}
              </span>
            </div>
            <p className="mt-1 line-clamp-2 text-[9px] leading-relaxed text-jarvis-muted" title={row.detail}>{row.detail}</p>
            <div className="mt-1 truncate font-mono text-[8px] text-jarvis-muted/80" title={row.evidence}>{row.evidence}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
        <span className="badge bg-jarvis-bg/60">value-free</span>
        <span className="badge bg-jarvis-bg/60">Review first</span>
        <span className="badge bg-jarvis-bg/60">Scopes sichtbar</span>
        <span className="badge bg-jarvis-bg/60">Raw Values verborgen</span>
      </div>
      <BrainControlProposalForm rows={rows} onCreated={data.refresh} />
    </aside>
  );
}

export function TruthEditorControlPanel({ data }: { data: DashboardData }): JSX.Element {
  const rows = deriveTruthEditorRows(data);
  const active = rows.filter((row) => row.tone === "active").length;
  const proposals = rows.filter((row) => row.tone === "proposal").length;
  return (
    <aside className="simo-owner-side-panel" aria-label="Project Truth und Agent Truth Editor">
      <div className="simo-owner-side-head">
        <span>Truth Editor</span>
        <strong>{proposals > 0 ? `${proposals} Proposal` : `${active}/${rows.length}`}</strong>
      </div>
      <div className="grid gap-1.5">
        {rows.map((row) => (
          <div
            key={row.id}
            className="min-w-0 rounded-md border border-white/10 bg-white/[0.035] px-2.5 py-1.5"
            data-truth-editor-tone={row.tone}
          >
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="truncate text-[10px] font-semibold text-jarvis-text" title={row.label}>{row.label}</span>
              <span className="shrink-0 rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
                {row.status}
              </span>
            </div>
            <p className="mt-1 line-clamp-2 text-[9px] leading-relaxed text-jarvis-muted" title={row.detail}>{row.detail}</p>
            <div className="mt-1 truncate font-mono text-[8px] text-jarvis-muted/80" title={row.evidence}>{row.evidence}</div>
          </div>
        ))}
      </div>
      <ProjectTruthProposalForm onCreated={data.refresh} />
      <AgentTruthProposalForm onCreated={data.refresh} />
      <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
        <span className="badge bg-jarvis-bg/60">aktive Wahrheit</span>
        <span className="badge bg-jarvis-bg/60">Diff zuerst</span>
        <span className="badge bg-jarvis-bg/60">Capability Gate</span>
        <span className="badge bg-jarvis-bg/60">Audit Pflicht</span>
      </div>
    </aside>
  );
}

function agentTruthProposalErrorCopy(error: unknown): string {
  const text = error instanceof Error ? error.message : String(error);
  if (/learning_proposal_rejected_by_secret_scan|secret/i.test(text)) {
    return "Agent-Truth-Vorschlag wurde blockiert, weil der Text wie ein Secret aussieht.";
  }
  if (/invalid_learning_proposal|agent_truth|policy/i.test(text)) {
    return "Agent-Truth-Vorschlag braucht Zielagent und Aenderungstext.";
  }
  return ownerFacingGlobalError(text);
}

function AgentTruthProposalForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [agentId, setAgentId] = useState("simo");
  const [proposedValue, setProposedValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canSubmit = agentId.trim().length > 0 && proposedValue.trim().length > 0 && !busy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    const normalizedAgent = agentId.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
    if (normalizedAgent.length === 0) {
      setError("Agent-Truth-Vorschlag braucht einen gueltigen Zielagent.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.createLearningProposal({
        kind: "policy",
        targetKey: `agent_truth.${normalizedAgent}.proposal`,
        proposedValue: proposedValue.trim(),
        source: "truth-editor-control-panel",
        reason: `Owner-visible Agent Truth proposal for ${normalizedAgent}.`,
        evidence: {
          schema: "it-simo.agent-truth-editor-proposal.v1",
          agentId: normalizedAgent,
          action: "propose_agent_truth_change",
          gate: "Learning Review",
          runtimeMutation: false,
          secretScan: "server-side",
          targetDocument: "docs/agents/AGENT-REGISTRY.md",
        },
      });
      if (!result.ok) throw new Error(result.error ?? "invalid_learning_proposal");
      setProposedValue("");
      setNotice("Agent-Truth-Vorschlag liegt in der Learning Review Queue.");
      onCreated();
    } catch (proposalError) {
      setError(agentTruthProposalErrorCopy(proposalError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor mt-2 rounded-md border border-white/10 bg-black/[0.18] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-jarvis-muted">Agent Truth Proposal</span>
        <span className="rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
          Review-only
        </span>
      </div>
      <input
        className="simo-keyboard-safe-field mt-2 min-h-10 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={agentId}
        maxLength={120}
        aria-label="Agent Truth Zielagent"
        placeholder="simo"
        onChange={(event) => setAgentId(event.currentTarget.value)}
      />
      <textarea
        className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={proposedValue}
        maxLength={5000}
        aria-label="Agent Truth Aenderungsvorschlag"
        placeholder="Agent-Beschreibung, Arbeitsweise oder Policy-Aenderung als Review-Text"
        onChange={(event) => setProposedValue(event.currentTarget.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        className="mt-1 w-full rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1.5 text-[8px] uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
        onClick={() => void submit()}
      >
        Agent-Truth-Vorschlag in Review Queue legen
      </button>
      <p className="mt-1 text-[8px] leading-relaxed text-jarvis-muted">
        Speichert nur ein policy Learning Proposal. Keine Agent-Registry-Mutation, kein Runner, kein Deploy.
      </p>
      {notice && <div className="mt-1 break-words text-[9px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[9px] text-rose-200">{error}</div>}
    </div>
  );
}

function projectTruthProposalErrorCopy(error: unknown): string {
  const text = error instanceof Error ? error.message : String(error);
  if (/learning_proposal_rejected_by_secret_scan|secret/i.test(text)) {
    return "Truth-Vorschlag wurde blockiert, weil der Text wie ein Secret aussieht.";
  }
  if (/invalid_learning_proposal|project_truth/i.test(text)) {
    return "Truth-Vorschlag braucht Zielpfad und Aenderungstext.";
  }
  return ownerFacingGlobalError(text);
}

function ProjectTruthProposalForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [targetKey, setTargetKey] = useState("docs/roadmaps/SIMO-SIMOT-SHARED-BRAIN-FINAL-ROADMAP-2026-07-10.md");
  const [proposedValue, setProposedValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canSubmit = targetKey.trim().length > 0 && proposedValue.trim().length > 0 && !busy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.createLearningProposal({
        kind: "project_truth",
        targetKey: targetKey.trim(),
        proposedValue: proposedValue.trim(),
        source: "truth-editor-control-panel",
        reason: `Owner-visible Project Truth proposal for ${targetKey.trim()}.`,
        evidence: {
          schema: "it-simo.project-truth-editor-proposal.v1",
          targetKey: targetKey.trim(),
          action: "propose_project_truth_change",
          gate: "Learning Review",
          runtimeMutation: false,
          secretScan: "server-side",
        },
      });
      if (!result.ok) throw new Error(result.error ?? "invalid_learning_proposal");
      setProposedValue("");
      setNotice("Truth-Vorschlag liegt in der Learning Review Queue.");
      onCreated();
    } catch (proposalError) {
      setError(projectTruthProposalErrorCopy(proposalError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor mt-2 rounded-md border border-white/10 bg-black/[0.18] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-jarvis-muted">Truth Proposal</span>
        <span className="rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
          Review-only
        </span>
      </div>
      <input
        className="simo-keyboard-safe-field mt-2 min-h-10 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={targetKey}
        maxLength={220}
        aria-label="Project Truth Zielpfad"
        placeholder="docs/roadmaps/SIMO-SIMOT-SHARED-BRAIN-FINAL-ROADMAP-2026-07-10.md"
        onChange={(event) => setTargetKey(event.currentTarget.value)}
      />
      <textarea
        className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={proposedValue}
        maxLength={5000}
        aria-label="Project Truth Aenderungsvorschlag"
        placeholder="Aenderung als Diff-/Review-Text beschreiben"
        onChange={(event) => setProposedValue(event.currentTarget.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        className="mt-1 w-full rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1.5 text-[8px] uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
        onClick={() => void submit()}
      >
        Truth-Vorschlag in Review Queue legen
      </button>
      <p className="mt-1 text-[8px] leading-relaxed text-jarvis-muted">
        Speichert nur ein project_truth Learning Proposal. Kein Truth-Write, kein Git, kein Deploy.
      </p>
      {notice && <div className="mt-1 break-words text-[9px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[9px] text-rose-200">{error}</div>}
    </div>
  );
}

const WORK_QUEUE_PRIORITY_OPTIONS: Array<{ id: AutonomousWorkQueuePriority; label: string }> = [
  { id: "low", label: "Niedrig" },
  { id: "normal", label: "Normal" },
  { id: "high", label: "Hoch" },
  { id: "urgent", label: "Dringend" },
];

const WORK_QUEUE_STATUS_ACTIONS: Array<{ id: AutonomousWorkQueueStatus; label: string }> = [
  { id: "queued", label: "Einreihen" },
  { id: "paused", label: "Pausieren" },
  { id: "archived", label: "Archivieren" },
];

const WORK_QUEUE_APPROVAL_OPTIONS: Array<{ id: AutonomousWorkQueueApprovalMode; label: string }> = [
  { id: "ask", label: "Fragen" },
  { id: "read_only", label: "Nur lesen" },
  { id: "session_allow", label: "Session" },
  { id: "block", label: "Blocken" },
];

interface WorkQueueEvidenceDetail {
  label: string;
  status: string | null;
  ref: string | null;
  summary: string | null;
}

function parseWorkQueueList(json: string | undefined): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
  } catch {
    return [];
  }
}

function parseWorkQueueEvidenceDetails(json: string | undefined): WorkQueueEvidenceDetail[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        if (typeof entry === "string") {
          const label = entry.trim().slice(0, 140);
          return label.length > 0 ? { label, status: null, ref: null, summary: null } : null;
        }
        if (entry && typeof entry === "object") {
          const record = entry as Record<string, unknown>;
          const label =
            typeof record.title === "string" ? record.title
              : typeof record.actionId === "string" ? record.actionId
                : typeof record.ref === "string" ? record.ref
                  : typeof record.evidenceRef === "string" ? record.evidenceRef
                    : typeof record.id === "string" ? record.id
                      : "Evidence";
          const status =
            typeof record.status === "string" ? record.status
              : typeof record.resultStatus === "string" ? record.resultStatus
                : null;
          const ref =
            typeof record.ref === "string" ? record.ref
              : typeof record.evidenceRef === "string" ? record.evidenceRef
                : typeof record.auditId === "number" ? `audit:${record.auditId}`
                  : null;
          const summary = typeof record.summary === "string" ? record.summary : null;
          return {
            label: label.trim().slice(0, 140),
            status: status?.trim().slice(0, 80) ?? null,
            ref: ref?.trim().slice(0, 120) ?? null,
            summary: summary?.trim().slice(0, 180) ?? null,
          };
        }
        return null;
      })
      .filter((entry): entry is WorkQueueEvidenceDetail => entry != null && entry.label.length > 0);
  } catch {
    return [];
  }
}

function parseWorkQueueEvidence(json: string | undefined): string[] {
  return parseWorkQueueEvidenceDetails(json).map((entry) => {
    const status = entry.status ? `: ${entry.status}` : "";
    const ref = entry.ref ? ` (${entry.ref})` : "";
    return `${entry.label}${status}${ref}`.slice(0, 180);
  });
}

function approvalModeFromItem(item: AutonomousWorkQueueItemView): AutonomousWorkQueueApprovalMode {
  const marker = parseWorkQueueList(item.approvalsNeededJson).find((entry) => entry.startsWith("approval_mode:"));
  const mode = marker?.replace("approval_mode:", "");
  return mode === "allow" || mode === "ask" || mode === "read_only" || mode === "block" || mode === "session_allow" ? mode : "ask";
}

function evidenceCountFromItem(item: AutonomousWorkQueueItemView): number {
  return parseWorkQueueEvidence(item.evidenceJson).length;
}

function toWorkQueueDateTimeInput(value: string | null | undefined): string {
  return typeof value === "string" && value.length >= 16 ? value.slice(0, 16) : "";
}

function WorkQueueCreateForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canSubmit = title.trim().length > 0 && goal.trim().length > 0 && !busy;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.createWorkQueueIdea({
        title: title.trim(),
        goal: goal.trim(),
        productScope: "SIMO",
        assignedAgent: "simo",
      });
      if (!result.ok) throw new Error("work_queue_create_failed");
      setTitle("");
      setGoal("");
      setNotice("Task angelegt und wartet auf Owner-Steuerung.");
      onCreated();
    } catch (createError) {
      setError(workQueueControlErrorCopy(createError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor mb-2 rounded-md border border-white/10 bg-black/[0.18] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-jarvis-muted">Task anlegen</span>
        <span className="rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
          Owner-gated
        </span>
      </div>
      <input
        className="simo-keyboard-safe-field mt-2 min-h-10 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={title}
        maxLength={200}
        aria-label="Work Queue Titel"
        placeholder="Titel"
        onChange={(event) => setTitle(event.currentTarget.value)}
      />
      <textarea
        className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
        value={goal}
        maxLength={4000}
        aria-label="Work Queue Ziel"
        placeholder="Ziel / Kontext"
        onChange={(event) => setGoal(event.currentTarget.value)}
      />
      <button
        type="button"
        disabled={!canSubmit}
        className="mt-2 min-h-10 w-full rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
        onClick={() => void submit()}
      >
        Task anlegen
      </button>
      {notice && <div className="mt-1 break-words text-[9px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[9px] text-rose-200">{error}</div>}
    </div>
  );
}

function workQueueControlErrorCopy(error: unknown): string {
  const text = error instanceof Error ? error.message : String(error);
  if (/work_queue_item_rejected_by_secret_scan|secret/i.test(text)) {
    return "Task wurde blockiert, weil der Text wie ein Secret aussieht.";
  }
  if (/invalid_work_queue_item|work_queue_create_failed/i.test(text)) {
    return "Work-Queue-Task braucht Titel und Ziel.";
  }
  if (/invalid_work_queue_patch|priority|status|assignedAgent/i.test(text)) {
    return "Work-Queue-Aenderung ist unvollstaendig. Bitte Aktion erneut waehlen.";
  }
  if (/work_queue_item_not_found|not_found/i.test(text)) {
    return "Work-Queue-Item ist nicht mehr aktuell. Bitte neu laden.";
  }
  return ownerFacingGlobalError(text);
}

function WorkQueueItemControl({
  item,
  onChanged,
}: {
  item: AutonomousWorkQueueItemView;
  onChanged: () => void;
}): JSX.Element {
  const [agentDraft, setAgentDraft] = useState(item.assignedAgent ?? "");
  const [dueAtDraft, setDueAtDraft] = useState(toWorkQueueDateTimeInput(item.dueAt));
  const [reviewAtDraft, setReviewAtDraft] = useState(toWorkQueueDateTimeInput(item.reviewAt));
  const [blockedReasonDraft, setBlockedReasonDraft] = useState(item.blockedReason ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const approvalMode = approvalModeFromItem(item);
  const requiredCapabilities = parseWorkQueueList(item.requiredCapabilitiesJson);
  const approvalsNeeded = parseWorkQueueList(item.approvalsNeededJson).filter((entry) => !entry.startsWith("approval_mode:"));
  const evidenceItems = parseWorkQueueEvidenceDetails(item.evidenceJson);
  const evidenceCount = evidenceItems.length;
  const dueAtChanged = dueAtDraft !== toWorkQueueDateTimeInput(item.dueAt);
  const reviewAtChanged = reviewAtDraft !== toWorkQueueDateTimeInput(item.reviewAt);
  const blockerChanged = blockedReasonDraft.trim() !== (item.blockedReason ?? "");

  useEffect(() => {
    setAgentDraft(item.assignedAgent);
    setDueAtDraft(toWorkQueueDateTimeInput(item.dueAt));
    setReviewAtDraft(toWorkQueueDateTimeInput(item.reviewAt));
    setBlockedReasonDraft(item.blockedReason ?? "");
  }, [item.assignedAgent, item.blockedReason, item.dueAt, item.reviewAt]);

  const updateItem = async (
    id: string,
    patch: {
      priority?: AutonomousWorkQueuePriority;
      status?: AutonomousWorkQueueStatus;
      assignedAgent?: string;
      approvalMode?: AutonomousWorkQueueApprovalMode;
      dueAt?: string | null;
      reviewAt?: string | null;
      blockedReason?: string | null;
    },
  ): Promise<void> => {
    setBusy(id);
    setError(null);
    setNotice(null);
    try {
      await api.updateWorkQueueItem(item.id, patch);
      onChanged();
    } catch (updateError) {
      setError(workQueueControlErrorCopy(updateError));
    } finally {
      setBusy(null);
    }
  };

  const createRoutine = async (): Promise<void> => {
    setBusy("routine");
    setError(null);
    setNotice(null);
    try {
      const result = await api.createRoutineFromWorkQueueItem(item.id);
      if (!result.ok || !result.routine) {
        throw new Error(result.error ?? "routine_create_failed");
      }
      setNotice("Routine erstellt, pausiert und wartet auf Owner-Aktivierung.");
      onChanged();
    } catch (createError) {
      setError(workQueueControlErrorCopy(createError));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="simo-keyboard-safe-editor min-w-0 rounded-md border border-white/10 bg-white/[0.035] px-2.5 py-2">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="min-w-0 truncate text-[10px] font-semibold text-jarvis-text" title={item.title}>{item.title}</span>
        <span className="shrink-0 rounded border border-white/10 bg-black/[0.18] px-1.5 py-0.5 text-[8px] uppercase tracking-[0.12em] text-jarvis-muted">
          {item.status}
        </span>
      </div>
      <p className="mt-1 line-clamp-2 text-[9px] leading-relaxed text-jarvis-muted" title={item.goal}>{item.goal}</p>
      <div className="mt-2 grid grid-cols-2 gap-1 text-[8px] text-jarvis-muted">
        <span className="min-w-0 rounded border border-white/10 bg-black/[0.14] px-1.5 py-1">
          Freigabe: <strong className="text-jarvis-text">{approvalMode}</strong>
        </span>
        <span className="min-w-0 rounded border border-white/10 bg-black/[0.14] px-1.5 py-1">
          Evidence: <strong className="text-jarvis-text">{evidenceCount}</strong>
        </span>
      </div>
      {(requiredCapabilities.length > 0 || approvalsNeeded.length > 0 || item.blockedReason) && (
        <div className="mt-1 space-y-0.5 text-[8px] leading-relaxed text-jarvis-muted">
          {requiredCapabilities.length > 0 && <div className="line-clamp-1" title={requiredCapabilities.join(", ")}>Rechte: {requiredCapabilities.join(", ")}</div>}
          {approvalsNeeded.length > 0 && <div className="line-clamp-1" title={approvalsNeeded.join(", ")}>Owner-Gate: {approvalsNeeded.join(", ")}</div>}
          {item.blockedReason && <div className="line-clamp-1 text-amber-100" title={item.blockedReason}>Blocker: {item.blockedReason}</div>}
        </div>
      )}
      {evidenceItems.length > 0 && (
        <div className="mt-1 rounded border border-white/10 bg-black/[0.14] px-1.5 py-1 text-[8px] leading-relaxed text-jarvis-muted">
          <div className="flex items-center justify-between gap-1 font-semibold uppercase tracking-[0.12em] text-jarvis-muted/90">
            <span>Evidence pruefen</span>
            <span>{evidenceCount}</span>
          </div>
          {evidenceItems.slice(0, 3).map((entry, index) => (
            <div key={`${entry.label}-${entry.ref ?? index}`} className="mt-1 min-w-0 rounded border border-white/10 bg-white/[0.025] px-1.5 py-1" title={[entry.label, entry.status, entry.ref, entry.summary].filter(Boolean).join(" · ")}>
              <div className="flex min-w-0 items-center justify-between gap-1">
                <span className="min-w-0 truncate font-semibold text-jarvis-text">{entry.label}</span>
                {entry.status && <span className="shrink-0 text-teal-100">{entry.status}</span>}
              </div>
              {(entry.ref || entry.summary) && (
                <div className="mt-0.5 line-clamp-2">
                  {entry.ref && <span className="font-mono text-jarvis-muted/90">{entry.ref}</span>}
                  {entry.ref && entry.summary ? " · " : ""}
                  {entry.summary}
                </div>
              )}
            </div>
          ))}
          {evidenceItems.length > 3 && <div className="mt-0.5 text-jarvis-muted/80">+{evidenceItems.length - 3} weitere Evidence</div>}
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {WORK_QUEUE_PRIORITY_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={busy != null || item.priority === option.id}
            className="min-h-10 rounded border border-white/10 bg-black/[0.16] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-jarvis-muted hover:border-jarvis-accent/60 disabled:opacity-50"
            onClick={() => void updateItem(`priority-${option.id}`, { priority: option.id })}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {WORK_QUEUE_STATUS_ACTIONS.map((action) => (
          <button
            key={action.id}
            type="button"
            disabled={busy != null || item.status === action.id}
            className="min-h-10 rounded border border-white/10 bg-black/[0.16] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-jarvis-muted hover:border-jarvis-accent/60 disabled:opacity-50"
            onClick={() => void updateItem(`status-${action.id}`, { status: action.id })}
          >
            {action.label}
          </button>
        ))}
        <button
          type="button"
          disabled={busy != null}
          className="min-h-10 rounded border border-teal-300/25 bg-teal-300/[0.08] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-teal-100 hover:border-teal-200/60 disabled:opacity-50"
          onClick={() => void createRoutine()}
        >
          Routine erstellen
        </button>
        <button
          type="button"
          disabled={busy != null || item.status === "archived"}
          className="min-h-10 rounded border border-rose-300/25 bg-rose-300/[0.07] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-rose-100 hover:border-rose-200/60 disabled:opacity-50"
          onClick={() => void updateItem("cancel", { status: "archived", blockedReason: "Vom Owner abgebrochen." })}
        >
          Abbrechen
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {WORK_QUEUE_APPROVAL_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={busy != null || approvalMode === option.id}
            className="min-h-10 rounded border border-white/10 bg-black/[0.16] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-jarvis-muted hover:border-jarvis-accent/60 disabled:opacity-50"
            onClick={() => void updateItem(`approval-${option.id}`, { approvalMode: option.id })}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="mt-2 flex min-w-0 flex-col gap-1.5 sm:flex-row">
        <input
          className="simo-keyboard-safe-field min-h-10 min-w-0 flex-1 rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
          value={agentDraft}
          aria-label={`Agent ${item.title}`}
          maxLength={120}
          onChange={(event) => setAgentDraft(event.currentTarget.value)}
        />
        <button
          type="button"
          disabled={busy != null || agentDraft.trim() === item.assignedAgent || agentDraft.trim().length === 0}
          className="min-h-10 shrink-0 rounded border border-white/10 bg-black/[0.16] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-jarvis-muted hover:border-jarvis-accent/60 disabled:opacity-50"
          onClick={() => void updateItem("agent", { assignedAgent: agentDraft.trim() })}
        >
          Agent setzen
        </button>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        <label className="min-w-0 text-[8px] font-semibold uppercase tracking-[0.12em] text-jarvis-muted" htmlFor={`work-queue-review-${item.id}`}>
          Review am
          <input
            id={`work-queue-review-${item.id}`}
            type="datetime-local"
            className="simo-keyboard-safe-field mt-1 min-h-10 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base normal-case tracking-normal text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
            value={reviewAtDraft}
            aria-label={`Review am ${item.title}`}
            onChange={(event) => setReviewAtDraft(event.currentTarget.value)}
          />
        </label>
        <label className="min-w-0 text-[8px] font-semibold uppercase tracking-[0.12em] text-jarvis-muted" htmlFor={`work-queue-due-${item.id}`}>
          Faellig am
          <input
            id={`work-queue-due-${item.id}`}
            type="datetime-local"
            className="simo-keyboard-safe-field mt-1 min-h-10 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base normal-case tracking-normal text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
            value={dueAtDraft}
            aria-label={`Faellig am ${item.title}`}
            onChange={(event) => setDueAtDraft(event.currentTarget.value)}
          />
        </label>
        <div className="flex flex-wrap gap-1.5 sm:col-span-2">
          <button
            type="button"
            disabled={busy != null || (!reviewAtChanged && !dueAtChanged)}
            className="min-h-10 rounded border border-sky-300/25 bg-sky-300/[0.08] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-sky-100 hover:border-sky-200/60 disabled:opacity-50"
            onClick={() => void updateItem("schedule", { reviewAt: reviewAtDraft || null, dueAt: dueAtDraft || null })}
          >
            Planzeit speichern
          </button>
          <button
            type="button"
            disabled={busy != null || (!item.reviewAt && !item.dueAt)}
            className="min-h-10 rounded border border-white/10 bg-black/[0.16] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-jarvis-muted hover:border-jarvis-accent/60 disabled:opacity-50"
            onClick={() => void updateItem("schedule-clear", { reviewAt: null, dueAt: null })}
          >
            Planzeit leeren
          </button>
        </div>
      </div>
      <div className="mt-2 rounded border border-white/10 bg-black/[0.14] px-2 py-2">
        <label className="block text-[8px] font-semibold uppercase tracking-[0.12em] text-jarvis-muted" htmlFor={`work-queue-blocker-${item.id}`}>
          Blocker / Owner-Notiz
        </label>
        <textarea
          id={`work-queue-blocker-${item.id}`}
          className="simo-keyboard-safe-field mt-1 min-h-20 w-full resize-y rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
          value={blockedReasonDraft}
          aria-label={`Blocker ${item.title}`}
          maxLength={600}
          placeholder="Warum ist dieses Item blockiert oder was muss der Owner pruefen?"
          onChange={(event) => setBlockedReasonDraft(event.currentTarget.value)}
        />
        <div className="mt-1 flex flex-wrap gap-1.5">
          <button
            type="button"
            disabled={busy != null || !blockerChanged}
            className="min-h-10 rounded border border-amber-300/25 bg-amber-300/[0.08] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-amber-100 hover:border-amber-200/60 disabled:opacity-50"
            onClick={() => void updateItem("blocker", { blockedReason: blockedReasonDraft.trim() || null })}
          >
            Blocker speichern
          </button>
          <button
            type="button"
            disabled={busy != null || !item.blockedReason}
            className="min-h-10 rounded border border-white/10 bg-black/[0.16] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-jarvis-muted hover:border-jarvis-accent/60 disabled:opacity-50"
            onClick={() => void updateItem("blocker-clear", { blockedReason: null })}
          >
            Blocker leeren
          </button>
        </div>
      </div>
      {notice && <div className="mt-1 break-words text-[9px] text-teal-100">{notice}</div>}
      {error && <div className="mt-1 break-words text-[9px] text-rose-200">{error}</div>}
    </div>
  );
}

export function WorkQueueControlPanel({ data }: { data: DashboardData }): JSX.Element {
  const items = data.workQueueItems ?? [];
  const active = items.filter((item) => item.status !== "archived" && item.status !== "done").length;
  return (
    <aside className="simo-owner-side-panel" aria-label="Autonomous Work Queue">
      <div className="simo-owner-side-head">
        <span>Work Queue</span>
        <strong>{active}</strong>
      </div>
      {data.workQueueError && (
        <div className="mb-2 break-words text-[10px] text-rose-200">{ownerFacingGlobalError(data.workQueueError.message)}</div>
      )}
      <WorkQueueCreateForm onCreated={data.refresh} />
      {items.length === 0 ? (
        <div className="rounded-md border border-white/10 bg-white/[0.035] px-2.5 py-2 text-[10px] text-jarvis-muted">
          Keine offenen Work-Queue-Items geladen.
        </div>
      ) : (
        <div className="grid gap-1.5">
          {items.slice(0, 5).map((item) => (
            <WorkQueueItemControl key={item.id} item={item} onChanged={data.refresh} />
          ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
        <span className="badge bg-jarvis-bg/60">Owner-gated</span>
        <span className="badge bg-jarvis-bg/60">Keine Auto-Ausfuehrung</span>
        <span className="badge bg-jarvis-bg/60">Audit pro Aenderung</span>
      </div>
    </aside>
  );
}

// ── SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: Current Mission Panel ──────

export interface CurrentMissionBrief {
  task: string;
  understood: string;
  checking: string;
  source: string;
  next: string;
}

function trimOwnerSentence(value: string, max = 160): string {
  const clean = value.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/**
 * Owner-lesbare Missionslage in fuenf Saetzen — nur aus echten Quellen
 * (Chat-Verlauf, Runtime-Signal, Decision-Log-Summary, Memory-Metadaten).
 * Keine technischen IDs im Primaerfluss, keine erfundenen Zustaende.
 */
export function deriveCurrentMissionBrief(input: {
  ownerState: CosmicOwnerState;
  lastUserText: string | null;
  lastAssistantText: string | null;
  pendingAssistant: boolean;
  runtime: CosmicOwnerRuntimeSignal;
  pendingApprovals: number;
  dataErrors: number;
  latestDecisionSummary: string | null;
  memoryTotal: number;
  connectorReachable: number;
  connectorTotal: number;
  workQueueRunningTitle: string | null;
}): CurrentMissionBrief {
  const task = input.workQueueRunningTitle
    ? `Work-Queue-Auftrag: ${trimOwnerSentence(input.workQueueRunningTitle)}`
    : input.lastUserText
      ? trimOwnerSentence(input.lastUserText)
      : "Kein aktiver Auftrag — sag oder schreib SIMO, was passieren soll.";
  const understood = input.pendingAssistant
    ? "SIMO formuliert gerade die Antwort auf deinen Auftrag."
    : input.lastAssistantText
      ? trimOwnerSentence(input.lastAssistantText, 200)
      : "Noch keine Rueckmeldung in dieser Unterhaltung.";
  const checking =
    input.ownerState === "error"
      ? `SIMO prueft nichts aktiv — ${input.dataErrors > 0 ? `${input.dataErrors} Live-Datenquelle(n) melden einen Fehler` : "der letzte Schritt endete mit Fehler"}.`
      : input.ownerState === "blocked"
        ? "SIMO ist blockiert und wartet, bis der Blocker geklaert ist."
        : input.ownerState === "waiting_for_approval"
          ? `SIMO wartet auf deine Entscheidung (${Math.max(input.pendingApprovals, input.runtime.awaitingApprovalRuns)} offen).`
          : input.ownerState === "transcribing"
            ? "SIMO wandelt gerade deine Sprache in Text um."
            : input.ownerState === "listening"
              ? "SIMO hoert dir gerade zu."
              : input.runtime.runningRuns > 0
                ? `SIMO arbeitet an ${input.runtime.runningRuns} laufenden Schritt(en).`
                : "Gerade laeuft keine Pruefung.";
  const source =
    input.runtime.activeToolIds.length > 0
      ? `Aktive Werkzeuge: ${input.runtime.activeToolIds.slice(0, 3).join(", ")}.`
      : input.latestDecisionSummary
        ? `Letzte Brain-Entscheidung: ${trimOwnerSentence(input.latestDecisionSummary, 120)}`
        : input.memoryTotal > 0
          ? `Wissensbasis: ${input.memoryTotal} freigegebene Memory-Eintraege, ${input.connectorReachable}/${input.connectorTotal} Connectoren erreichbar.`
          : `Quellen: ${input.connectorReachable}/${input.connectorTotal} Connectoren erreichbar, noch kein Memory aktiv.`;
  const next =
    input.ownerState === "waiting_for_approval"
      ? "Als naechstes: Deine Freigabe-Entscheidung (Erlauben, Ablehnen oder Aenderung sagen)."
      : input.ownerState === "error" || input.ownerState === "blocked"
        ? "Als naechstes: Blocker klaeren, dann kann SIMO weiterarbeiten."
        : input.runtime.runningRuns > 0 || input.runtime.workQueueRunning > 0
          ? "Als naechstes: SIMO meldet sich mit Ergebnis oder Owner-Frage."
          : input.ownerState === "done"
            ? "Als naechstes: Ergebnis pruefen oder neuen Auftrag geben."
            : "Als naechstes: SIMO startet erst nach deinem Auftrag.";
  return { task, understood, checking, source, next };
}

export function CurrentMissionPanel({
  chat,
  data,
  scene,
  ownerRuntime,
}: {
  chat: ChatController;
  data: DashboardData;
  scene: CosmicSceneSnapshot;
  ownerRuntime: CosmicOwnerRuntimeSignal;
}): JSX.Element {
  const lastUser = [...chat.history].reverse().find((message) => message.role === "user");
  const lastAssistant = [...chat.history].reverse().find((message) => message.role === "assistant" && !message.pending);
  const pendingAssistant = chat.history.some((message) => message.role === "assistant" && message.pending === true);
  const runningQueueItem = (data.workQueueItems ?? []).find((item) => item.status === "in_progress");
  const brief = deriveCurrentMissionBrief({
    ownerState: scene.ownerState,
    lastUserText: lastUser?.content ?? null,
    lastAssistantText: lastAssistant?.content ?? null,
    pendingAssistant,
    runtime: ownerRuntime,
    pendingApprovals: data.approvalInbox?.items.filter((item) => item.status === "pending").length ?? 0,
    dataErrors: Object.keys(data.errors).length,
    latestDecisionSummary: data.brainDecisionLog?.[0]?.summary ?? null,
    memoryTotal: data.memoryTotal,
    connectorReachable: data.connectors.filter((connector) => connector.reachable).length,
    connectorTotal: data.connectors.length,
    workQueueRunningTitle: runningQueueItem?.title ?? null,
  });
  const rows: Array<{ label: string; value: string }> = [
    { label: "Aktuelle Aufgabe", value: brief.task },
    { label: "Was SIMO verstanden hat", value: brief.understood },
    { label: "Was SIMO gerade prueft", value: brief.checking },
    { label: "Welche Quelle genutzt wird", value: brief.source },
    { label: "Was als naechstes passiert", value: brief.next },
  ];
  return (
    <section className="simo-current-mission" aria-label="Current Mission" data-owner-state={scene.ownerState}>
      <div className="simo-current-mission-head">
        <span>Current Mission</span>
        <strong>{scene.ownerStateLabel}</strong>
      </div>
      <div className="simo-current-mission-grid">
        {rows.map((row) => (
          <div key={row.label} className="simo-current-mission-row">
            <span>{row.label}</span>
            <p>{row.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: A/B/C-Approval-Stage ───────

function approvalRiskCopy(item: ApprovalInboxItemView): { label: string; detail: string } {
  if (item.riskLevel === "destructive") {
    return { label: "Hohes Risiko (destruktiv)", detail: "Diese Aktion kann Daten unwiderruflich veraendern. Server verlangt zusaetzlich eine Passwort-Bestaetigung." };
  }
  if (item.riskLevel === "write") {
    return { label: "Schreibende Aktion", detail: "Diese Aktion veraendert Daten in einem verbundenen System. Server verlangt zusaetzlich eine Passwort-Bestaetigung." };
  }
  if (item.riskLevel === "draft") {
    return { label: "Entwurf", detail: "SIMO legt nur einen Entwurf an; nichts wird ohne dich versendet oder ausgefuehrt." };
  }
  return { label: "Lesende Aktion", detail: "Nur Lesen — keine Aenderung an fremden Systemen." };
}

/**
 * Grosses, verstaendliches Owner-Gate mit drei Entscheidungen:
 *   A = Erlauben  -> bestehender Endpoint POST /api/approval-inbox/:id/approve
 *   B = Ablehnen  -> bestehender Endpoint POST /api/approval-inbox/:id/reject
 *   C = Aenderung -> bestehender Endpoint POST /api/approval-inbox/:id/instruct
 *       (lehnt die Aktion ab UND gibt deine Anweisung als Owner-Nachricht in
 *       die Konversation des Runs zurueck).
 *
 * Ehrlichkeits-Hinweis: Ein first-class "C = aendern und direkt weiterlaufen
 * lassen" (Approval mutieren statt reject+instruct) braucht ein eigenes
 * Backend-Decision-Modell und bleibt bewusst ein Folgeblock
 * (SIMO-APPROVAL-C-DECISION-BACKEND-1). Hier wird KEIN neuer Endpoint erfunden.
 */
export function ApprovalDecisionStage({
  data,
  onDecided,
}: {
  data: DashboardData;
  onDecided?: () => void;
}): JSX.Element | null {
  const [busyAction, setBusyAction] = useState<"approve" | "reject" | "instruct" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [instructOpen, setInstructOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [stepUpNeeded, setStepUpNeeded] = useState(false);
  const [stepUpPassword, setStepUpPassword] = useState("");
  const [readinessBusy, setReadinessBusy] = useState(false);
  const [readinessResult, setReadinessResult] = useState<{
    status: "ready" | "not_ready";
    reason?: string;
    stepUpRequired: boolean;
    stepUpSatisfied: boolean;
    canExecuteStored: boolean;
  } | null>(null);
  const instructionRef = useRef<HTMLTextAreaElement | null>(null);

  const inbox = data.approvalInbox;
  const gates = inbox?.actionGates;
  const pending = inbox?.items.filter((item) => item.status === "pending") ?? [];
  const item = pending[0];
  if (!item) return null;

  const risk = approvalRiskCopy(item);
  const gateOk = gates?.requiresOwnerSession === true && gates.statusOnly === true && gates.canExecute === false;
  const canApprove = gateOk && gates?.canApprove === true && item.safeActions.includes("approve_status");
  const canReject = gateOk && gates?.canReject === true && item.safeActions.includes("reject_status");
  const canRunStoredConnectorWrite = item.toolId === "gmail.write.draft" && item.capability === "gmail.write.draft";

  const finish = (): void => {
    setBusyAction(null);
    setStepUpNeeded(false);
    setStepUpPassword("");
    setReadinessBusy(false);
    setReadinessResult(null);
    setInstructOpen(false);
    setInstruction("");
    onDecided?.();
    data.refresh();
  };

  const checkStoredConnectorWriteReadiness = async (): Promise<void> => {
    setReadinessBusy(true);
    setActionError(null);
    try {
      const readiness = await api.connectorWriteStoredExecutionReadiness(item.id);
      setReadinessResult({
        status: readiness.canExecuteStored ? "ready" : "not_ready",
        reason: readiness.reason,
        stepUpRequired: readiness.stepUpRequired,
        stepUpSatisfied: readiness.stepUpSatisfied,
        canExecuteStored: readiness.canExecuteStored,
      });
    } catch (err) {
      setActionError(approvalInboxStageErrorCopy(err));
    } finally {
      setReadinessBusy(false);
    }
  };

  const runStoredConnectorWriteAfterReadiness = async (): Promise<void> => {
    const readiness = await api.connectorWriteStoredExecutionReadiness(item.id);
    if (!readiness.ok || readiness.canExecuteStored !== true) {
      throw new Error(readiness.reason ?? "stored_connector_execution_not_ready");
    }
    if (readiness.boundary.providerCall !== false || readiness.boundary.connectorWrite !== false || readiness.boundary.executionAttempted !== false) {
      throw new Error("stored_connector_readiness_boundary_invalid");
    }
    const execution = await api.connectorWriteExecuteStoredApproval(item.id);
    if (!execution.ok || execution.executionAllowed !== true) throw new Error(execution.reason ?? "stored_connector_execution_not_confirmed");
    window.dispatchEvent(new CustomEvent(CONNECTOR_WRITE_EXECUTED_EVENT, { detail: { approvalId: item.id, actionId: item.toolId } }));
  };

  const runApprove = async (): Promise<void> => {
    setBusyAction("approve");
    setActionError(null);
    try {
      await api.approvalInboxApprove(item.id);
      if (canRunStoredConnectorWrite) {
        await runStoredConnectorWriteAfterReadiness();
      }
      finish();
    } catch (err) {
      const text = err instanceof Error ? err.message : String(err);
      if (/step_up_required/i.test(text)) {
        setStepUpNeeded(true);
        setBusyAction(null);
        return;
      }
      setActionError(approvalInboxStageErrorCopy(err));
      setBusyAction(null);
    }
  };

  const runStepUpApprove = async (): Promise<void> => {
    if (!stepUpPassword.trim()) {
      setActionError("Bitte Owner-Passwort eingeben.");
      return;
    }
    setBusyAction("approve");
    setActionError(null);
    try {
      await api.stepUp(stepUpPassword);
      await api.approvalInboxApprove(item.id);
      if (canRunStoredConnectorWrite) {
        await runStoredConnectorWriteAfterReadiness();
      }
      finish();
    } catch (err) {
      const text = err instanceof Error ? err.message : String(err);
      setActionError(/invalid_password|403/i.test(text) ? "Passwort nicht bestaetigt. Bitte erneut versuchen." : approvalInboxStageErrorCopy(err));
      setBusyAction(null);
    }
  };

  const runReject = async (): Promise<void> => {
    setBusyAction("reject");
    setActionError(null);
    try {
      await api.approvalInboxReject(item.id);
      finish();
    } catch (err) {
      setActionError(approvalInboxStageErrorCopy(err));
      setBusyAction(null);
    }
  };

  const runInstruct = async (): Promise<void> => {
    if (!instruction.trim()) {
      setInstructOpen(true);
      instructionRef.current?.focus();
      return;
    }
    setBusyAction("instruct");
    setActionError(null);
    try {
      await api.approvalInboxInstruct(item.id, instruction.trim());
      finish();
    } catch (err) {
      setActionError(approvalInboxStageErrorCopy(err));
      setBusyAction(null);
    }
  };

  return (
    <section className="simo-approval-stage" aria-label="Owner-Freigabe A/B/C" data-risk={item.riskLevel}>
      <div className="simo-approval-stage-head">
        <span>Owner-Freigabe noetig</span>
        <strong>{pending.length === 1 ? "1 Entscheidung offen" : `${pending.length} Entscheidungen offen — aelteste zuerst`}</strong>
      </div>
      <h2 className="simo-approval-stage-title">{item.summary}</h2>
      <div className="simo-approval-stage-facts">
        <div>
          <span>Warum fragt SIMO?</span>
          <p>
            SIMO will die Aktion <strong>{item.toolId}</strong> ausfuehren
            {item.mode === "draft" ? " (als Entwurf)" : ""} und darf das nur mit deiner Freigabe.
          </p>
        </div>
        <div data-tone={item.riskLevel === "read" || item.riskLevel === "draft" ? "ok" : "warn"}>
          <span>Risiko</span>
          <p><strong>{risk.label}.</strong> {risk.detail}</p>
        </div>
      </div>
      <details className="simo-approval-stage-details">
        <summary>Technische Details anzeigen</summary>
        <div className="simo-approval-stage-details-body">
          <div>Capability: <code>{item.capability}</code></div>
          <div>Evidence: <code>{item.evidenceRef}</code></div>
          <pre>{(() => { try { return JSON.stringify(JSON.parse(item.payloadPreviewJson), null, 2); } catch { return item.payloadPreviewJson; } })()}</pre>
          <div className="simo-approval-stage-blocked">
            {item.blockedActions.map((action) => <span key={action}>{action} gesperrt</span>)}
          </div>
        </div>
      </details>
      {canRunStoredConnectorWrite && (
        <div className="simo-approval-stage-readiness" data-status={readinessResult?.status ?? "unchecked"}>
          <div>
            <span>Execution-Readiness</span>
            <p>
              {readinessResult == null
                ? "Read-only Vorflug fuer gespeicherte Gmail-Draft-Ausfuehrung."
                : readinessResult.canExecuteStored
                  ? "Bereit: Approval, Policy, Stored Payload und Step-up passen."
                  : `Nicht bereit: ${readinessResult.reason ?? "readiness_not_ready"}.`}
            </p>
            {readinessResult && (
              <small>
                Step-up: {readinessResult.stepUpRequired ? readinessResult.stepUpSatisfied ? "bestaetigt" : "erforderlich" : "nicht noetig"}
              </small>
            )}
          </div>
          <button type="button" className="btn" disabled={busyAction !== null || readinessBusy} onClick={() => void checkStoredConnectorWriteReadiness()}>
            {readinessBusy ? "Prueft..." : "Readiness pruefen"}
          </button>
        </div>
      )}
      {actionError && <div className="simo-approval-stage-error">{actionError}</div>}
      <div className="simo-approval-stage-actions">
        <button
          type="button"
          className="simo-approval-stage-btn"
          data-action="allow"
          disabled={!canApprove || busyAction !== null}
          title={canApprove ? "Aktion freigeben" : "Freigabe-Gate nicht erfuellt (Owner-Session/Status-only)"}
          onClick={() => void runApprove()}
        >
          <span>A</span>
          <strong>{busyAction === "approve" ? "Gibt frei…" : "Erlauben"}</strong>
          <small>SIMO fuehrt genau diese Aktion aus.</small>
        </button>
        <button
          type="button"
          className="simo-approval-stage-btn"
          data-action="reject"
          disabled={!canReject || busyAction !== null}
          title={canReject ? "Aktion ablehnen" : "Reject-Gate nicht erfuellt"}
          onClick={() => void runReject()}
        >
          <span>B</span>
          <strong>{busyAction === "reject" ? "Lehnt ab…" : "Ablehnen"}</strong>
          <small>SIMO fuehrt die Aktion nicht aus.</small>
        </button>
        <button
          type="button"
          className="simo-approval-stage-btn"
          data-action="change"
          disabled={!canReject || busyAction !== null}
          title={canReject ? "Aenderung als Owner-Anweisung geben" : "Instruct nutzt den Reject-Gate"}
          onClick={() => { setInstructOpen(true); setTimeout(() => instructionRef.current?.focus(), 0); }}
        >
          <span>C</span>
          <strong>Aenderung sagen</strong>
          <small>Ablehnen und SIMO sagen, was stattdessen passieren soll.</small>
        </button>
      </div>
      {stepUpNeeded && (
        <div className="simo-approval-stage-stepup">
          <span>Sicherheitsbestaetigung: Diese Freigabe fuehrt eine echte Aktion aus. Bitte Owner-Passwort bestaetigen.</span>
          <div>
            <input
              type="password"
              className="input"
              placeholder="Owner-Passwort"
              autoComplete="current-password"
              value={stepUpPassword}
              onChange={(e) => setStepUpPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void runStepUpApprove(); }}
              disabled={busyAction !== null}
            />
            <button type="button" className="btn" disabled={busyAction !== null || !stepUpPassword.trim()} onClick={() => void runStepUpApprove()}>
              {busyAction === "approve" ? "Prueft…" : "Bestaetigen"}
            </button>
            <button type="button" className="btn" disabled={busyAction !== null} onClick={() => { setStepUpNeeded(false); setStepUpPassword(""); }}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
      {instructOpen && (
        <div className="simo-approval-stage-instruct">
          <span>Deine Aenderung geht als Owner-Anweisung zurueck an SIMO; die aktuelle Aktion wird dabei abgelehnt.</span>
          <textarea
            ref={instructionRef}
            className="input"
            rows={3}
            maxLength={4000}
            placeholder={`Aenderung zu: ${item.summary}`}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            disabled={busyAction !== null}
          />
          <div>
            <button type="button" className="btn-primary" disabled={busyAction !== null || !instruction.trim()} onClick={() => void runInstruct()}>
              {busyAction === "instruct" ? "Sendet…" : "Aenderung senden"}
            </button>
            <button type="button" className="btn" disabled={busyAction !== null} onClick={() => { setInstructOpen(false); setInstruction(""); }}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
      <div className="simo-approval-stage-footnote">
        Status-only Inbox: keine breite Auto-Ausfuehrung; Gmail-Draft nutzt nach Owner-Freigabe erst den read-only Execution-Readiness-Check und danach den gespeicherten Execute-Pfad per Approval-ID. Shell/Git/Deploy und andere Connector-Writes bleiben gesperrt, jede Entscheidung wird auditiert.
      </div>
    </section>
  );
}

export function ConnectorWriteLiveEvidenceReadbackControl(): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveEvidence, setLiveEvidence] = useState<ConnectorWriteLiveEvidenceView | null>(null);

  const checkLiveEvidence = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setLiveEvidence(await api.connectorWriteLiveEvidence());
    } catch (err) {
      setError(approvalInboxStageErrorCopy(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    const onConnectorWriteExecuted = (): void => {
      void checkLiveEvidence();
    };
    window.addEventListener(CONNECTOR_WRITE_EXECUTED_EVENT, onConnectorWriteExecuted);
    return () => window.removeEventListener(CONNECTOR_WRITE_EXECUTED_EVENT, onConnectorWriteExecuted);
  }, [checkLiveEvidence]);

  return (
    <section className="simo-approval-stage-live-evidence" data-proven={liveEvidence?.executionProven === true} aria-label="Governed Gmail-Draft Live-Evidence">
      <div>
        <span>Live-Evidence nach Freigabe</span>
        <p>
          {liveEvidence == null
            ? "Nach der echten Owner-Aktion prueft SIMO hier read-only, ob Gmail-Draft, Approval, Audit und Provider-Readback zusammen belegt sind."
            : liveEvidence.executionProven
              ? "Gmail-Draft-Live-Ausfuehrung ist mit Approval, Audit und Provider-Readback belegt."
              : "Noch kein bestaetigter Gmail-Draft-Live-Beweis gefunden."}
        </p>
        {liveEvidence?.latest?.readback && (
          <small>
            Readback: {liveEvidence.latest.readback.confirmed ? "bestaetigt" : "offen"}
            {liveEvidence.latest.readback.draftId ? ` · Draft ${liveEvidence.latest.readback.draftId}` : ""}
          </small>
        )}
        {error && <div className="simo-approval-stage-error">{error}</div>}
      </div>
      <button type="button" className="btn" disabled={busy} onClick={() => void checkLiveEvidence()}>
        {busy ? "Prueft…" : "Live-Evidence pruefen"}
      </button>
    </section>
  );
}

function approvalInboxStageErrorCopy(err: unknown): string {
  const text = err instanceof Error ? err.message : String(err);
  if (/stored_connector_execution_not_ready|step_up_required/i.test(text)) {
    return "Ausfuehrung ist noch nicht bereit. Bitte Owner-Step-up bestaetigen und den aktuellen Readiness-Stand erneut pruefen.";
  }
  if (/stored_connector_readiness_boundary_invalid/i.test(text)) {
    return "Readiness-Readback ist nicht vertrauenswuerdig. SIMO fuehrt nicht aus.";
  }
  if (/approval.*not found|already decided|not awaiting|invalid_state|approval_invalid|approval_expired/i.test(text)) {
    return "Freigabe ist nicht mehr aktuell. Bitte neu laden und den aktuellen Stand pruefen.";
  }
  if (/invalid_instruction/i.test(text)) {
    return "Anweisung wurde nicht akzeptiert (leer oder zu lang). Bitte kuerzer formulieren.";
  }
  return ownerFacingGlobalError(text);
}

function OwnerOrbHero({
  data,
  activeRuns,
  pendingApprovals,
  scene,
  quality,
  audioStream,
  agentRuntime,
  showConstellation = false,
  onFocusMission,
  onOpenOperations,
  onVoiceStart,
  onVoiceEnd,
  onVoiceCancel,
  voiceEnabled,
}: {
  data: DashboardData;
  activeRuns: number;
  pendingApprovals: number;
  scene: CosmicSceneSnapshot;
  quality: OrbRenderQuality;
  audioStream?: MediaStream | null;
  agentRuntime?: CosmicAgentRuntimeEntry[];
  showConstellation?: boolean;
  onFocusMission: () => void;
  onOpenOperations?: () => void;
  onVoiceStart: () => void;
  onVoiceEnd: () => void;
  onVoiceCancel: () => void;
  voiceEnabled: boolean;
}): JSX.Element {
  const voiceHoldTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const voiceHoldActive = useRef(false);
  const orbState = data.ownerMission ? ownerMissionOrbState(data.ownerMission) : scene.ownerOrbState;
  const beginOrbGesture = (): void => {
    if (!voiceEnabled) return;
    voiceHoldActive.current = false;
    voiceHoldTimer.current = setTimeout(() => {
      voiceHoldActive.current = true;
      onVoiceStart();
    }, 350);
  };
  const endOrbGesture = (): void => {
    if (voiceHoldTimer.current) clearTimeout(voiceHoldTimer.current);
    voiceHoldTimer.current = null;
    if (voiceHoldActive.current) onVoiceEnd();
    else onFocusMission();
    voiceHoldActive.current = false;
  };
  const systems = deriveOwnerLiveSystemSignals({
    activeRuns,
    pendingApprovals,
    connectors: data.connectors,
    infra: data.infra,
    health: data.health,
    memoryTotal: data.memoryTotal,
    learningProposals: data.learningProposals.length,
    evidenceCount: data.gatedEvidence.length,
    // OPNAV1 Truth-Fixture: kanonische Human-Gate-Zahl (eine Authority).
    ownerGates: data.controlPlaneSummary ? data.controlPlaneSummary.gates.open : null,
  });
  const watchBrief = deriveOwnerWatchingBrief({
    systems,
    activeRuns,
    pendingApprovals,
    dataErrors: Object.keys(data.errors).length,
  });
  return (
    <section className="simo-trillion-hero" aria-label="SIMO Voice-first Owner-Zentrale">
      <div className="simo-trillion-copy">
        <span className="simo-trillion-eyebrow">Watching {systems.length} Systeme · Owner-first</span>
        <h1>SIMO, deine Voice-first Owner-Zentrale.</h1>
        <p>
          SIMO verdichtet Voice, Runs, Owner-Gates, Connectoren, Infra und Brain zu einem lebenden Zustand.
          Du bekommst den nächsten Schritt, nicht eine Wand aus Panels.
        </p>
        <div className="simo-trillion-actions" aria-label="Naechster Status">
          <span>{scene.livingPrompt}</span>
          <span>{scene.livingLabel}</span>
        </div>
        <div className="simo-trillion-owner-cue">{scene.ownerCue}</div>
        <div className="simo-trillion-watch-brief" data-tone={watchBrief.tone}>
          <span>{watchBrief.label}</span>
          <strong>{watchBrief.detail}</strong>
        </div>
      </div>

      <div
        className="simo-trillion-orb-stage"
        data-state={scene.visualState}
        data-owner-state={scene.ownerState}
        role="button"
        tabIndex={0}
        aria-label="SIMO Orb: klicken fuer Mission, halten fuer Voice, doppelklicken fuer Operations"
        onPointerDown={beginOrbGesture}
        onPointerUp={endOrbGesture}
        onPointerCancel={() => {
          if (voiceHoldTimer.current) clearTimeout(voiceHoldTimer.current);
          voiceHoldTimer.current = null;
          voiceHoldActive.current = false;
          onVoiceCancel();
        }}
        onDoubleClick={onOpenOperations}
        onKeyDown={(event) => {
          if (event.key === "Enter") onFocusMission();
          if (event.key === " " && voiceEnabled) { event.preventDefault(); onVoiceStart(); }
        }}
        onKeyUp={(event) => { if (event.key === " " && voiceEnabled) onVoiceEnd(); }}
      >
        <div className="simo-trillion-orb-glow" aria-hidden="true" />
        {/* Owner-Zustand steuert den Orb direkt: kein "arbeitet" ohne echten Run. */}
        <Orb3D state={orbState} size={360} audioStream={audioStream} quality={quality} showLabel={false} />
        {showConstellation && agentRuntime && (
          <div className="simo-trillion-orbit" aria-hidden={false}>
            <AgentConstellation
              activeAgents={scene.activeAgents}
              approval={scene.hasApprovalGate}
              errors={scene.hasErrors}
              runtime={agentRuntime}
              beams={deriveCosmicOrbitBeams({ runtime: agentRuntime, ownerState: scene.ownerState })}
            />
          </div>
        )}
        <div className="simo-trillion-orb-caption" data-owner-state={scene.ownerState}>
          <span>SIMO</span>
          <strong>{scene.ownerStateLabel}</strong>
        </div>
      </div>

      <div className="simo-trillion-system-strip" aria-label="Live Systeme">
        {systems.map((system) => (
          <div key={system.label} className="simo-trillion-system-chip">
            <span>{system.label}</span>
            <strong>{system.value}</strong>
          </div>
        ))}
      </div>

      <div className="simo-trillion-lane-strip" aria-label="Living state lanes">
        {scene.activeLanes.map((lane) => (
          <div key={lane.id} className="simo-trillion-lane-chip" data-tone={lane.tone} data-active={lane.active ? "true" : "false"}>
            <span>{lane.label}</span>
            <strong>{lane.status}</strong>
          </div>
        ))}
      </div>

      <AgentTeamSurface activeAgents={scene.activeAgents} runtime={agentRuntime} />
    </section>
  );
}

export function AgentTeamSurface({
  activeAgents,
  runtime,
}: {
  activeAgents: CosmicAgentId[];
  /**
   * SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: echte Runtime-Zustaende pro
   * Orbit-Agent. Wenn gesetzt, animiert NUR status === "running" — kein
   * Fake-Pulsieren aus Heuristiken.
   */
  runtime?: CosmicAgentRuntimeEntry[];
}): JSX.Element {
  const activeSet = new Set(activeAgents);
  const runtimeById = new Map((runtime ?? []).map((entry) => [entry.id, entry]));
  const runningCount = runtime ? runtime.filter((entry) => entry.status === "running").length : activeAgents.length;
  return (
    <div className="simo-trillion-agent-team" aria-label="SIMO Agent Team">
      <div className="simo-trillion-agent-team-head">
        <span>Agent Team</span>
        <strong>{runtime ? `${runningCount} laeuft` : `${activeAgents.length} aktiv`}</strong>
      </div>
      <div className="simo-trillion-agent-team-grid">
        {COSMIC_AGENT_NODES.map((agent) => {
          const entry = runtimeById.get(agent.id);
          const active = entry ? entry.status === "running" : activeSet.has(agent.id);
          return (
            <div
              key={agent.id}
              className="simo-trillion-agent-card"
              data-active={active ? "true" : "false"}
              data-agent-status={entry?.status ?? "unbound"}
              title={entry?.detail}
            >
              <span>{agent.label}</span>
              <strong>{agent.role}</strong>
              {entry && (
                <small data-status={entry.status}>{COSMIC_AGENT_STATUS_LABEL[entry.status]}</small>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function useCosmicOrbSize(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(520);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const compute = (): void => {
      const fit = Math.min(el.clientWidth, el.clientHeight || el.clientWidth);
      setSize(Math.max(280, Math.min(680, Math.floor(fit * 0.72))));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, size];
}

function AgentConstellation({
  activeAgents,
  approval,
  errors,
  runtime,
  beams,
}: {
  activeAgents: CosmicAgentId[];
  approval: boolean;
  errors: boolean;
  /**
   * SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: Wenn echte Runtime-Zustaende
   * uebergeben werden, pulsiert ein Orbit-Agent NUR bei status === "running".
   * Ohne runtime (Session-Gate-Mock vor Login) bleibt das bisherige, explizit
   * als Mock parametrisierte Verhalten.
   */
  runtime?: CosmicAgentRuntimeEntry[];
  /**
   * SIMO-ORBIT-RUNTIME-STATE-BINDING-1: Aktivitaets-Beams vom Orb zum Node.
   * Nur aus echten Runtime-Quellen (`deriveCosmicOrbitBeams`); ohne echte
   * Aktivitaet wird KEIN Beam gerendert (auch nicht im Session-Gate-Mock).
   */
  beams?: CosmicAgentId[];
}): JSX.Element {
  const activeSet = new Set(activeAgents);
  const runtimeById = new Map((runtime ?? []).map((entry) => [entry.id, entry]));
  const beamSet = new Set(beams ?? []);
  return (
    <div className="simo-cosmic-agent-field" aria-label="Agent constellation">
      {COSMIC_AGENT_NODES.map((agent) => {
        const entry = runtimeById.get(agent.id);
        const active = entry ? entry.status === "running" : activeSet.has(agent.id);
        const statusLabel = entry ? COSMIC_AGENT_STATUS_LABEL[entry.status] : agent.role;
        return (
          <div
            key={agent.id}
            className="simo-cosmic-agent-orbit"
            data-dock-zone={agent.dock}
            style={{
              "--agent-angle": `${agent.angle}deg`,
              "--agent-speed": `${agent.speed}s`,
            } as CSSProperties}
          >
            {beamSet.has(agent.id) && (
              <span className="simo-cosmic-agent-beam" aria-hidden="true" data-agent-beam={agent.id} />
            )}
            <div
              className={[
                "simo-cosmic-agent-node",
                active ? "is-active" : "",
                (entry ? entry.status === "waiting" : approval && agent.id === "security") ? "is-approval" : "",
                (entry ? entry.status === "error" || entry.status === "blocked" : errors && agent.id === "security") ? "is-error" : "",
              ].filter(Boolean).join(" ")}
              data-agent-id={agent.id}
              data-dock-zone={agent.dock}
              data-agent-status={entry?.status ?? "unbound"}
              title={entry ? `${agent.label}: ${statusLabel} — ${entry.detail}` : undefined}
            >
              <span className="simo-cosmic-agent-dot" />
              <span className="simo-cosmic-agent-copy">
                <span>{agent.label}</span>
                <small>{statusLabel}</small>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DockBeacon({
  zone,
  activeZones,
  label,
}: {
  zone: CosmicDockZone;
  activeZones: CosmicDockZone[];
  label: string;
}): JSX.Element {
  const active = activeZones.includes(zone);
  return (
    <div
      className={["simo-cosmic-dock-beacon", active ? "is-active" : ""].filter(Boolean).join(" ")}
      data-dock-zone={zone}
      aria-label={`${label} dock ${active ? "active" : "idle"}`}
    >
      <span className="simo-cosmic-dock-pip" />
      <span>{label}</span>
    </div>
  );
}

export function CosmicSessionGate({ data }: { data: DashboardData }): JSX.Element {
  const [orbRef, orbSize] = useCosmicOrbSize();
  const visualMock = useCosmicVisualMock();
  const performance = useCosmicPerformanceProfile();
  const visualState = visualMock.state ?? "idle";
  const scene = deriveCosmicSceneSnapshot({
    orb: "idle",
    pendingApprovals: visualState === "approval_required" ? 1 : 0,
    activeRuns: visualState === "working" ? 1 : 0,
    completedRuns: visualState === "success" ? 1 : 0,
    evidenceCount: visualState === "success" ? 1 : 0,
    dataErrors: visualState === "error" ? 1 : 0,
    learningProposals: 1,
    notifications: 0,
    toolRuns: visualState === "thinking" ? 1 : 0,
    infraOnline: false,
    connectorReachable: 0,
    connectorTotal: 0,
    memoryTotal: 1,
    mockState: visualState,
    mockAmplitude: visualMock.amplitude,
  });

  return (
    <section
      className={["simo-cosmic-shell", "simo-cosmic-session-shell", `quality-${performance.mode}`, performance.reducedMotion ? "is-reduced-motion" : ""].filter(Boolean).join(" ")}
      data-quality-mode={performance.mode}
      data-reduced-motion={performance.reducedMotion ? "true" : "false"}
      data-performance-reason={performance.reason}
      aria-label="SIMO Owner Session Gate"
    >
      {performance.starfield && <div className="simo-cosmic-starfield" aria-hidden="true" />}
      {deriveCosmicMockBadgeLabel(visualMock) && (
        <div className="simo-cosmic-mock-badge" role="status">{deriveCosmicMockBadgeLabel(visualMock)}</div>
      )}
      <div className="simo-cosmic-session-grid">
        <div className="simo-cosmic-core simo-cosmic-session-core" ref={orbRef}>
          <div
            className={`simo-cosmic-orb-shell state-${scene.visualState}`}
            data-state={scene.orbState}
            data-visual-state={scene.visualState}
            style={{ "--cosmic-amplitude": scene.amplitude } as CSSProperties}
          >
            {scene.hasApprovalGate && <div className="simo-cosmic-approval-ring" aria-hidden="true" />}
            <Orb3D state={scene.orbState} size={orbSize} quality={performance.renderQuality} />
            <AgentConstellation activeAgents={scene.activeAgents} approval={scene.hasApprovalGate} errors={scene.hasErrors} />
          </div>
          <div className="simo-cosmic-state-strip" aria-label="Cosmic session state">
            <span>{scene.label}</span>
            <span>{performance.mode}</span>
            <span>owner gate</span>
            <span>live reads paused</span>
          </div>
        </div>
        <aside className="simo-cosmic-session-panel" aria-label="Owner authentication">
          <div className="simo-cosmic-session-copy">
            <span className="status-dot" />
            <span>Owner Session Required</span>
          </div>
          <OwnerAuthStatusPanel status={data.ownerAuthStatus} error={data.ownerAuthError} onAuthChanged={data.refresh} />
        </aside>
      </div>
    </section>
  );
}

export function CosmicMissionScene({
  chat,
  data,
  onOpenSettingsTab,
  onOpenAgentOs,
  onOpenOperations,
}: {
  chat: ChatController;
  data: DashboardData;
  /** Oeffnet den bestehenden SettingsDrawer auf einem Tab (z. B. "connectors"). */
  onOpenSettingsTab?: (tab: SettingsTab) => void;
  /** Oeffnet die einzige produktive Agent-/Subagent-/Team-Primärfläche. */
  onOpenAgentOs?: () => void;
  onOpenOperations?: () => void;
}): JSX.Element {
  const visualMock = useCosmicVisualMock();
  const performance = useCosmicPerformanceProfile();
  const [missionActionBusy, setMissionActionBusy] = useState(false);
  const [missionActionError, setMissionActionError] = useState<string | null>(null);
  const handleMissionAction = async (action: "pause" | "resume" | "stop"): Promise<void> => {
    if (!data.ownerMission || missionActionBusy) return;
    setMissionActionBusy(true);
    setMissionActionError(null);
    try {
      await api.agentOrbMissionAction(data.ownerMission.runId, action);
      data.refresh();
    } catch (error) {
      setMissionActionError(ownerFacingGlobalError(error instanceof Error ? error.message : String(error)));
    } finally {
      setMissionActionBusy(false);
    }
  };
  const focusMissionComposer = (): void => {
    document.getElementById("simo-owner-mission-input")?.focus();
  };
  // SIMO-ORBIT-RUNTIME-STATE-BINDING-1: EINE Inbox-basierte Zaehlquelle statt
  // Dreifach-Summe (Chat-Karten + approvals + Inbox lasen dieselbe Tabelle).
  const pendingApprovals = deriveCosmicPendingApprovalCount({
    approvalInbox: data.approvalInbox,
    approvals: data.approvals,
    chatPendingApprovalIds: collectChatPendingApprovalIds(chat.history),
  });
  const dataErrors = Object.entries(data.errors);
  const connectorReachable = data.connectors.filter((connector) => connector.reachable).length;
  const connectorTotal = data.connectors.length;
  // OPNAV1 Slice 3 — kanonische Run-Semantik: "Run aktiv" zählt control_plane_runs
  // non-terminal (Mission-Runs), NICHT Chat-/Tool-Runs (agent_runs). Chat-Runs
  // bleiben für den Konversations-/Orb-Zustand weiter unten in Nutzung.
  const activeRuns = data.controlPlaneSummary?.semantics?.runs.nonTerminal ?? 0;
  const completedRuns = data.controlPlaneSummary?.semantics?.runs.byStatus.find((s) => s.status === "completed")?.count ?? 0;
  const infraOnline =
    data.infra?.orchestrator?.status === "ok" ||
    data.infra?.n8n?.status === "ok" ||
    data.infra?.forgejo?.status === "ok" ||
    data.health?.ok === true;
  // Echte Runtime-Signale fuer Owner-Zustand + Subagent-Orbit (kein Fake).
  const ownerRuntime = useMemo(
    () => deriveCosmicOwnerRuntimeSignal({ runs: data.runs, toolRuns: data.toolRuns, workQueueItems: data.workQueueItems }),
    [data.runs, data.toolRuns, data.workQueueItems],
  );
  const agentRuntime = useMemo(
    () =>
      deriveCosmicAgentRuntime({
        runs: data.runs,
        toolRuns: data.toolRuns,
        workQueueItems: data.workQueueItems,
        routines: data.routines,
        routineRuns: data.routineRuns,
        pendingApprovals,
        learningProposals: data.learningProposals.length,
        dataErrors: dataErrors.length,
        infraOnline,
        notifications: data.notifications.length,
      }),
    [
      data.runs,
      data.toolRuns,
      data.workQueueItems,
      data.routines,
      data.routineRuns,
      pendingApprovals,
      data.learningProposals.length,
      dataErrors.length,
      infraOnline,
      data.notifications.length,
    ],
  );
  const scene = useMemo(
    () =>
      deriveCosmicSceneSnapshot({
        orb: chat.orb,
        pendingApprovals,
        activeRuns,
        completedRuns,
        evidenceCount: data.gatedEvidence.length,
        dataErrors: dataErrors.length,
        learningProposals: data.learningProposals.length,
        notifications: data.notifications.length,
        toolRuns: data.toolRuns.length,
        infraOnline,
        connectorReachable,
        connectorTotal,
        memoryTotal: data.memoryTotal,
        voice: deriveCosmicVoiceSignal({
          recording: chat.voice.recording,
          processing: chat.voice.processing,
          speaking: chat.voice.speaking,
        }),
        ownerRuntime,
        mockState: visualMock.state,
        mockAmplitude: visualMock.amplitude,
      }),
    [
      activeRuns,
      chat.orb,
      chat.voice.processing,
      chat.voice.recording,
      chat.voice.speaking,
      completedRuns,
      data.gatedEvidence.length,
      data.health?.ok,
      data.infra?.forgejo.status,
      data.infra?.n8n.status,
      data.infra?.orchestrator?.status,
      infraOnline,
      connectorReachable,
      connectorTotal,
      data.learningProposals.length,
      data.memoryTotal,
      data.notifications.length,
      data.toolRuns.length,
      dataErrors.length,
      ownerRuntime,
      pendingApprovals,
      visualMock.amplitude,
      visualMock.state,
    ],
  );
  return (
    <section
      className={["simo-trillion-home", `quality-${performance.mode}`, performance.reducedMotion ? "is-reduced-motion" : ""].filter(Boolean).join(" ")}
      data-quality-mode={performance.mode}
      data-reduced-motion={performance.reducedMotion ? "true" : "false"}
      data-performance-reason={performance.reason}
      aria-label="SIMO Voice-first Owner-Zentrale"
    >
      {deriveCosmicMockBadgeLabel(visualMock) && (
        <div className="simo-cosmic-mock-badge" role="status">{deriveCosmicMockBadgeLabel(visualMock)}</div>
      )}
      <div className="simo-trillion-shell">
        <OwnerOrbHero
          data={data}
          activeRuns={activeRuns}
          pendingApprovals={pendingApprovals}
          scene={scene}
          quality={performance.renderQuality}
          audioStream={chat.voice.audioStream}
          agentRuntime={agentRuntime}
          showConstellation={performance.mode !== "low"}
          onFocusMission={focusMissionComposer}
          onOpenOperations={onOpenOperations}
          onVoiceStart={chat.handleMicDown}
          onVoiceEnd={chat.handleMicUp}
          onVoiceCancel={chat.voice.cancelRecording}
          voiceEnabled={chat.voice.available && chat.voiceMode !== "off"}
        />

        {/* Primaerflaeche: Mission-Lage + grosses Owner-Gate direkt am Orb. */}
        <CurrentMissionPanel chat={chat} data={data} scene={scene} ownerRuntime={ownerRuntime} />
        <OwnerMissionControl mission={data.ownerMission ?? null} busy={missionActionBusy} error={missionActionError ?? data.ownerMissionError?.message ?? null} onAction={(action) => void handleMissionAction(action)} />
        <ApprovalDecisionStage data={data} />
        <ConnectorWriteLiveEvidenceReadbackControl />
        <AgentControlAccessPanel data={data} onOpenSettingsTab={onOpenSettingsTab} onOpenAgentOs={onOpenAgentOs} />

        {/* SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: Evidence/Technik sind
            bewusst demotet — standardmaessig collapsed, nichts geloescht. */}
        <div className="simo-trillion-board">
          <details className="simo-owner-details">
            <summary>
              <span>Arbeitsdetails &amp; Runstream</span>
              <small>{activeRuns} Run aktiv · {pendingApprovals} Freigaben · {Object.keys(data.errors).length} Blocker</small>
            </summary>
            <OwnerOperationsCanvas chat={chat} data={data} activeRuns={activeRuns} pendingApprovals={pendingApprovals} scene={scene} />
          </details>
          <div className="simo-owner-home-side">
            <OwnerPriorityPanel data={data} activeRuns={activeRuns} pendingApprovals={pendingApprovals} />
            <details className="simo-owner-details">
              <summary>
                <span>Kontrolle, Evidence &amp; System</span>
                <small>{data.gatedEvidence.length} Evidence · Details standardmaessig eingeklappt</small>
              </summary>
              <div className="simo-owner-home-side">
                <OperatorConsolePanel data={data} />
                <OperationsActionFlowPanel data={data} onOpenAgentOs={onOpenAgentOs} />
                <FullControlSurfacePanel data={data} />
                <BrainControlCenterPanel data={data} />
                <TruthEditorControlPanel data={data} />
                <HarnessControlPanel data={data} />
                <WorkQueueControlPanel data={data} />
                <InfraPanel data={data} />
                <OwnerEvidencePanel data={data} />
                <OwnerAuthStatusPanel status={data.ownerAuthStatus} error={data.ownerAuthError} onAuthChanged={data.refresh} />
              </div>
            </details>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Prominenter Einstieg von der Living-Surface in die eindeutigen produktiven
 * Primärflächen. Agenten/Subagents/Teams führen ausschließlich ins Agent OS
 * Studio; Routinen in die bestehende Routinen-Fläche. Alte Control-Modals und
 * proposal-only AgentTeams-Writes sind von der Home-Navigation getrennt.
 */
function AgentControlAccessPanel({
  data,
  onOpenSettingsTab,
  onOpenAgentOs,
}: {
  data: DashboardData;
  onOpenSettingsTab?: (tab: SettingsTab) => void;
  onOpenAgentOs?: () => void;
}): JSX.Element {
  const agentCount = data.agentRegistry?.agents.length ?? data.agents.length;
  const routineCount = (data.routines ?? []).length;
  const enabledRoutines = (data.routines ?? []).filter((routine) => routine.enabled).length;
  return (
    <section className="simo-agent-access" aria-label="Agenten, Routinen und Steuerung verwalten">
      <button type="button" className="simo-agent-access-card" onClick={onOpenAgentOs}>
        <span>Agenten &amp; Steuerung</span>
        <strong>{agentCount} Agenten verwalten</strong>
        <small>Agenten, Subagents, Teams, Skills, Policies und Harness — im kanonischen Agent OS Studio.</small>
      </button>
      <button type="button" className="simo-agent-access-card" onClick={() => onOpenSettingsTab?.("routines")}>
        <span>Routinen</span>
        <strong>{routineCount > 0 ? `${enabledRoutines}/${routineCount} Routinen aktiv` : "Routine-Editor oeffnen"}</strong>
        <small>Anlegen, Bearbeiten, Pausieren, Loeschen und Testlauf im bestehenden Routine-Editor.</small>
      </button>
    </section>
  );
}

